import { CalendarDays, Download, Eye, EyeOff, Home, User } from 'lucide-react';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  EarningsSection,
  PzlPriceListSection,
  PositionRatesSection,
  pzlSettlementModeLabel,
} from '../features/installationBilling/BillingSections';
import { installationService } from '../services/api';
import { installationDates, mergeCalendarRead, optimisticSchedule, replaceInstallation, shiftInstallationDates } from '../utils/installationCalendarState';
import { FINAL_STATUSES, REGULAR_STATUSES, STATUS_COLORS } from '../utils/installationStatuses';
import { printDailyInstallationAmounts, printDailyInstallationSummary, printInstallation } from '../utils/printInstallation';
import './css/InstallationBilling.css';
import './css/InstallationPlanning.css';

// ──────────────────────────────────────────────────────────────
//  Constants
// ──────────────────────────────────────────────────────────────
const TABS = ['Planowanie', 'Lista montaży', 'Pracownicy i ekipy', 'Rozliczanie'];
const TAB_NAMES_URL = ['planowanie', 'lista', 'pracownicy', 'rozliczanie'];

const STATUS_LEGEND_ORDER = [
  'pre_assigned',
  'planned',
  'confirmed',
  'signed',
  'complaint',
  'quick_add',
  'calendar_note',
  'additional_work',
];

const WEEKLY_VIEW = 'Widok tygodniowy';
const CALENDAR_VIEWS = ['Widok miesięczny', WEEKLY_VIEW];

const POLISH_MONTHS = [
  'Styczeń','Luty','Marzec','Kwiecień','Maj','Czerwiec',
  'Lipiec','Sierpień','Wrzesień','Październik','Listopad','Grudzień',
];
const POLISH_DAYS_SHORT = ['Pon','Wt','Śr','Czw','Pt','Sb','Nd'];

const VACATION_TYPES = {
  UW: 'Urlop wypoczynkowy',
  NN: 'Nieusprawiedliwiona nieobecność',
  'UNŻ': 'Urlop na żądanie',
  L4: 'Chorobowe',
  SW: 'Służba wojskowa',
  ST: 'Stan szczególny (siła wyższa, powodziowe)',
  UB: 'Urlop bezpłatny',
};

const VACATION_RATE_TYPES = Object.fromEntries(
  Object.entries(VACATION_TYPES).filter(([type]) => type !== 'UB')
);

function VacationTypeBadge({ type = 'UW' }) {
  return (
    <span className={`vac-type-badge vac-type-badge--${type}`} title={VACATION_TYPES[type] || type}>
      {type}
    </span>
  );
}

// ──────────────────────────────────────────────────────────────
//  Date helpers
// ──────────────────────────────────────────────────────────────
function toISODate(d) {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function currentMonthValue() {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
}

export function sortVacationsNewestFirst(vacations = []) {
  return [...vacations].sort((a, b) =>
    b.start_date.localeCompare(a.start_date)
    || b.end_date.localeCompare(a.end_date)
    || b.id - a.id
  );
}

export function groupVacationsByStartMonth(vacations = []) {
  const groups = new Map();
  sortVacationsNewestFirst(vacations).forEach((vacation) => {
    const [year, month] = vacation.start_date.split('-');
    const key = `${year}-${month}`;
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        label: `${month}.${year}`,
        vacations: [],
      });
    }
    groups.get(key).vacations.push(vacation);
  });
  return [...groups.values()];
}

function startOfWeek(d) {
  const date = new Date(d);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day; // Monday start
  date.setDate(date.getDate() + diff);
  return date;
}

function addDays(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}

// Tekst błędu z odpowiedzi API: `detail` bywa napisem (HTTPException) albo listą (422).
function apiErrorMessage(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (Array.isArray(detail) && detail.length) {
    return detail.map((item) => item?.msg || (typeof item === 'string' ? item : JSON.stringify(item))).join('; ');
  }
  return error?.message || fallback;
}

function installationIncludesDay(installation, day) {
  if (installation?.planned_dates?.length) return installation.planned_dates.includes(day);
  if (!installation?.planned_start_date) return false;
  const endDate = installation.planned_end_date || installation.planned_start_date;
  return day >= installation.planned_start_date && day <= endDate;
}

function getInstallationsForISODate(installations, day) {
  if (!day) return [];
  return installations.filter((installation) => installationIncludesDay(installation, day));
}

function getPajaczekDayConflicts(installations) {
  const byDay = {};
  installations
    .filter((inst) => inst.pajaczek && inst.planned_start_date)
    .forEach((inst) => {
      installationDates(inst).forEach((day) => {
        byDay[day] = byDay[day] || [];
        byDay[day].push(inst);
      });
    });

  return Object.entries(byDay)
    .filter(([, dayInstallations]) => dayInstallations.length > 1)
    .map(([day, dayInstallations]) => ({
      day,
      orders: dayInstallations.map((inst) => inst.order_number || `ID ${inst.id}`),
    }));
}

// ──────────────────────────────────────────────────────────────
//  Helper functions
// ──────────────────────────────────────────────────────────────
function getTotalPerimeter(installation) {
  if (!installation?.items || installation.items.length === 0) return 0;
  return installation.items.reduce((sum, item) => sum + (item.perimeter_sum || 0), 0);
}

function formatDayLabel(dateStr) {
  if (!dateStr) return '';
  const days = ['niedziela', 'poniedziałek', 'wtorek', 'środa', 'czwartek', 'piątek', 'sobota'];
  const date = new Date(dateStr + 'T00:00:00');
  return `${dateStr} (${days[date.getDay()]})`;
}

function formatCalendarOrderNumber(orderNumber) {
  if (!orderNumber) return '';
  return orderNumber.endsWith('-PR') ? `${orderNumber.slice(0, -3)}(PR)` : orderNumber;
}

function formatMoneyCompact(value) {
  return `${Number(value || 0).toLocaleString('pl-PL', { maximumFractionDigits: 2 })} PLN`;
}

function SavedFinalAdditionsPanel({ additions = [] }) {
  if (!additions.length) return null;

  return (
    <section className="saved-final-additions" aria-label="Dopłaty końcowe">
      <div className="saved-final-additions-heading">
        <div>
          <strong>Dopłaty końcowe</strong>
          <span>Uwzględnione w końcowych kwotach pracowników</span>
        </div>
        <span className="saved-final-additions-count">
          {additions.length}
        </span>
      </div>
      <div className="saved-final-additions-list">
        {additions.map((addition) => (
          <article
            className="saved-final-addition-row"
            key={`${addition.settlement_id}:${addition.assigned_date}:${addition.team_id ?? 'direct'}:${addition.installer_id}`}
          >
            <div className="saved-final-addition-context">
              <div className="saved-final-addition-date">
                <CalendarDays size={15} aria-hidden="true" />
                <strong>{formatDayLabel(addition.assigned_date)}</strong>
                <span>{addition.team_name || 'Przypisanie indywidualne'}</span>
              </div>
              <span className="saved-final-addition-session">{addition.settlement_name}</span>
            </div>
            <div className="saved-final-addition-details">
              <div className="saved-final-addition-person">
                <span className="saved-final-addition-person-icon" aria-hidden="true">
                  <User size={16} />
                </span>
                <span>
                  <small>Pracownik</small>
                  <strong>{addition.installer_name}</strong>
                </span>
              </div>
              <div className="saved-final-addition-metric is-addition">
                <small>Dopłata</small>
                <strong>+ {formatMoneyCompact(addition.additional_amount)}</strong>
              </div>
              <span className="saved-final-addition-arrow" aria-hidden="true">→</span>
              <div className="saved-final-addition-metric is-total">
                <small>Kwota końcowa</small>
                <strong>{formatMoneyCompact(addition.final_day_amount)}</strong>
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function settlementBaseAmount(item) {
  return Number(item?.amount || 0) - Number(item?.additional_amount || 0);
}

function SettlementAmountValue({ item }) {
  const additionalAmount = Number(item?.additional_amount || 0);
  return (
    <span className="settlement-amount-breakdown">
      <strong>{Number(item?.amount || 0).toFixed(2)} PLN</strong>
      {additionalAmount > 0 && (
        <small>+{additionalAmount.toFixed(2)} PLN dopłaty</small>
      )}
    </span>
  );
}

function findPremiumCalendarCopy(installation, installations = []) {
  const orderNumber = installation?.order_number || '';
  if (!orderNumber || orderNumber.endsWith('-PR')) return null;
  return installations.find((inst) => inst.order_number === `${orderNumber}-PR`) || null;
}

function normalizeInstallationText(text) {
  return (text || '')
    .toUpperCase()
    .replaceAll('Ą', 'A')
    .replaceAll('Ć', 'C')
    .replaceAll('Ę', 'E')
    .replaceAll('Ł', 'L')
    .replaceAll('Ń', 'N')
    .replaceAll('Ó', 'O')
    .replaceAll('Ś', 'S')
    .replaceAll('¯', 'Ż')
    .replaceAll('Ż', 'Z')
    .replaceAll('Ź', 'Z');
}

function getInstallationScopeTokens(installation) {
  return (installation?.items || [])
    .flatMap((item) => normalizeInstallationText(item.scope).split(','))
    .map((part) => part.trim())
    .filter(Boolean);
}

function hasDeliveryToClient(installation) {
  return getInstallationScopeTokens(installation).some((scope) => scope.includes('DOWOZ DO KLIENTA'));
}

function isDeliveryOnlyInstallation(installation) {
  const scopes = getInstallationScopeTokens(installation);
  return scopes.length > 0 && scopes.every((scope) => scope.includes('DOWOZ DO KLIENTA'));
}

function findTirTeam(teams = []) {
  return teams.find((team) => team.is_special_truck) || null;
}

function isDadOrder(installationOrOrderNumber) {
  const orderNumber = typeof installationOrOrderNumber === 'string'
    ? installationOrOrderNumber
    : installationOrOrderNumber?.order_number;
  return (orderNumber || '').toUpperCase().startsWith('DAD/');
}

function isAdditionalWorkOrder(installation) {
  return installation?.calendar_status === 'additional_work'
    || (installation?.order_number || '').endsWith('-DP');
}

function isCalendarNote(installation) {
  return installation?.calendar_status === 'calendar_note';
}

function canAddAdditionalWork(installation, installations = []) {
  if (isCalendarNote(installation)) return false;
  if (isAdditionalWorkOrder(installation)) return false;
  return !installations.some(
    (candidate) => candidate.order_number === `${installation?.order_number || ''}-DP`
  );
}

function findDadTeam(teams = []) {
  return teams.find((team) => team.is_special_dad) || null;
}

function confirmDadTeamChange(installation, targetTeamId, teams = []) {
  if (!isDadOrder(installation)) return true;
  const targetTeam = teams.find((team) => team.id === Number(targetTeamId));
  if (!targetTeam || targetTeam.is_special_dad || targetTeam.is_special_truck) return true;
  const dadTeamName = findDadTeam(teams)?.name || 'Ekipa DAD';
  return window.confirm(
    `Czy na pewno chcesz zlecenie "${installation.order_number}" przenieść do tej ekipy? `
    + `Zlecenia DAD obsługuje ekipa "${dadTeamName}".`
  );
}

function isSpecialSystemTeam(team) {
  return !!(team?.is_special_truck || team?.is_special_dad);
}

function teamHasKatB(team) {
  return !!(team?.kat_b || team?.members?.some((member) => member.kat_b));
}

function isTeamLeader(installer) {
  return normalizeInstallationText(installer?.position).trim() === 'BRYGADZISTA';
}

// Direct installer assignments do not carry a team_id.  Resolve one display
// team for the weekly grid without changing the persisted assignment.  An
// assigned crew leader takes precedence; otherwise use the first assigned
// installer who belongs to a team.
function inferredTeamIdForDay(installation, dayISO, teams = []) {
  const dayAssignments = (installation?.assignments || [])
    .filter((assignment) => assignment.assigned_date === dayISO)
    .sort((left, right) =>
      (left.display_order || 0) - (right.display_order || 0)
      || (left.id || 0) - (right.id || 0)
    );
  if (dayAssignments.some((assignment) => assignment.team_id != null)) return null;

  const assignedInstallerIds = [...new Set(
    dayAssignments
      .map((assignment) => assignment.installer_id)
      .filter((installerId) => installerId != null)
  )];
  if (assignedInstallerIds.length === 0) return null;

  const leaderIds = assignedInstallerIds.filter((installerId) =>
    teams.some((team) => (team.members || []).some(
      (member) => member.id === installerId && isTeamLeader(member)
    ))
  );
  const candidates = [
    ...leaderIds,
    ...assignedInstallerIds.filter((installerId) => !leaderIds.includes(installerId)),
  ];

  for (const installerId of candidates) {
    const team = teams.find((candidate) =>
      (candidate.members || []).some(
        (member) => member.id === installerId
      )
    );
    if (team) return team.id;
  }
  return null;
}

// ──────────────────────────────────────────────────────────────
//  Small reusable components
// ──────────────────────────────────────────────────────────────
// One collator reused for every comparison (localeCompare with options builds a
// new collator per call, which is very slow when sorting thousands of orders).
const ORDER_NUMBER_COLLATOR = new Intl.Collator('pl', { numeric: true });
// A dropdown with more rows is unusable and expensive to render on every key.
const MAX_ORDER_SUGGESTIONS = 50;

function installationMatchesSearch(installation, normalizedQuery) {
  return ['order_number', 'contractor', 'notes', 'calendar_note_title'].some((field) =>
    (installation?.[field] || '')
      .toLocaleLowerCase('pl-PL')
      .includes(normalizedQuery)
  );
}

function installationSearchLabel(installation) {
  return isCalendarNote(installation)
    ? `Notatka: ${installation.calendar_note_title || installation.notes || 'Bez tytułu'}`
    : installation.order_number;
}

function OrderNumberSuggestions({
  query,
  installations,
  onSelect,
  onAddToCalendar,
  visible = true,
}) {
  const normalizedQuery = (query || '').trim().toLocaleLowerCase('pl-PL');
  const matches = useMemo(() => {
    if (!visible || !normalizedQuery) return [];
    return (installations || [])
      .filter((installation) => installationMatchesSearch(installation, normalizedQuery))
      .sort((left, right) =>
        ORDER_NUMBER_COLLATOR.compare(
          left.order_number || '',
          right.order_number || ''
        )
      )
      .slice(0, MAX_ORDER_SUGGESTIONS);
  }, [installations, normalizedQuery, visible]);

  if (matches.length === 0) return null;

  return (
    <div className="order-number-suggestions" role="listbox" aria-label="Pasujące zlecenia">
      {matches.map((installation) => (
        <div className="order-number-suggestion-row" key={installation.id}>
          <button
            type="button"
            className="order-number-suggestion-main"
            role="option"
            aria-selected="false"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => onSelect(installation)}
          >
            <strong>{installationSearchLabel(installation)}</strong>
            <span>
              {installation.contractor || 'Bez kontrahenta'}
              {installation.planned_start_date ? ` · ${installation.planned_start_date}` : ' · Do zaplanowania'}
            </span>
            {installation.notes
              && installation.notes.toLocaleLowerCase('pl-PL').includes(normalizedQuery) && (
                <span className="order-number-suggestion-note">
                  Notatka: {installation.notes}
                </span>
              )}
          </button>
          {onAddToCalendar && !isCalendarNote(installation) && !installation.planned_start_date && (
            <button
              type="button"
              className="order-number-suggestion-add"
              aria-label={`Zaplanuj zlecenie ${installation.order_number}`}
              title="Przypisz do kalendarza"
              onMouseDown={(event) => event.preventDefault()}
              onClick={(event) => onAddToCalendar(
                installation,
                event.currentTarget.getBoundingClientRect()
              )}
            >
              +
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function InstallationCard({ installation, onEdit, onDragStart, onContextMenu, size = 'compact', currentDate = null, currentTeamId, specialTruckName = 'TIR', highlighted = false, showAddress = false, showWh = false }) {
  let status = installation.calendar_status || 'planned';

  // Compatibility with records saved before "Przypisana ekipa" was replaced
  // by the yellow "Wstępnie zaplanowany" stage.
  if (status === 'team_assigned') {
    status = 'planned';
  }

  // Orders shown in "Nieprzypisane" are always preliminary assignments.
  if (currentDate && status !== 'calendar_note') {
    const dateStr = toISODate(currentDate);
    const hasAssignmentForDay = (installation.assignments || []).some(
      (assignment) => assignment.assigned_date === dateStr
    );
    if (!hasAssignmentForDay) {
      status = 'pre_assigned';
    }
  }
  
  const colors = STATUS_COLORS[status] || STATUS_COLORS.pre_assigned;
  const hasDelivery = hasDeliveryToClient(installation);
  const deliveryOnly = isDeliveryOnlyInstallation(installation);
  // Confirmation status takes precedence over the blue delivery-only style.
  const cardColors = deliveryOnly && status !== 'confirmed'
    ? { bg: '#dbeafe', border: '#2563eb' }
    : colors;
  
  // Zbierz zaznaczone wyposażenie
  const equipment = [];
  const equipmentMap = {
    rusztowanie: 'Rusztowanie',
    drabina: 'Drabina',
    pajaczek: 'Pajączek',
    manipulator: 'Manipulator',
    agregat: 'Agregat',
    dodatkowy_bus: 'Bus',
    tir: 'Tir'
  };
  
  Object.keys(equipmentMap).forEach(key => {
    if (installation[key]) {
      equipment.push(equipmentMap[key]);
    }
  });

  // Zbuduj pełny tooltip z informacjami i notatką
  const calendarNote = isCalendarNote(installation);
  const displayOrderNumber = calendarNote
    ? (installation.calendar_note_title || installation.notes || 'Notatka')
    : formatCalendarOrderNumber(installation.order_number);
  const whValue = (installation.wh || '').trim() || [...new Set(
    (installation.items || [])
      .map((item) => (item.wh || '').trim())
      .filter(Boolean)
  )].join(', ');
  let tooltipText = calendarNote
    ? `${displayOrderNumber} (${installation.calendar_note_country || 'PL'})\n${installation.notes || ''}`
    : `${displayOrderNumber} – ${installation.contractor || ''}`;
  if (installation.address) {
    tooltipText += `\n${installation.address}`;
  }
  if (installation.notes && !calendarNote) {
    tooltipText += `\n\nNotatka:\n${installation.notes}`;
  }
  
  return (
    <div
      className={`installation-card installation-card--${size} ${calendarNote ? 'installation-card--note' : ''} ${highlighted ? 'installation-search-highlight' : ''}`}
      data-installation-id={installation.id}
      aria-busy={!!installation._schedulePending}
      draggable={!installation._schedulePending}
      onDragStart={(e) => onDragStart(e, installation)}
      onClick={() => !installation._schedulePending && onEdit(installation, currentDate ? {
        assignedDate: toISODate(currentDate),
        teamId: currentTeamId,
      } : null)}
      onContextMenu={installation._schedulePending ? (event) => event.preventDefault() : onContextMenu}
      style={{ background: cardColors.bg, borderColor: cardColors.border, borderLeftColor: cardColors.border }}
      title={tooltipText}
    >
      <span className="card-order">
        {calendarNote && <span className="calendar-note-icon" aria-hidden="true">📝</span>}
        {displayOrderNumber}
        {installation._schedulePending && <small style={{ marginLeft: 6 }}>Zapisywanie…</small>}
        {hasDelivery && !deliveryOnly && (
          <span
            style={{
              marginLeft: '6px',
              padding: '1px 5px',
              borderRadius: '999px',
              background: '#dbeafe',
              color: '#1d4ed8',
              fontSize: '10px',
              fontWeight: 700,
            }}
            title={`${specialTruckName} również jedzie`}
          >
            {specialTruckName}
          </span>
        )}
      </span>
      {calendarNote && installation.notes && (
        <span className="card-note-description">{installation.notes}</span>
      )}
      {installation.contractor && (
        <span className="card-contractor">{installation.contractor}</span>
      )}
      {(size === 'full' || showAddress) && installation.address && (
        <span className="card-address">{installation.address}</span>
      )}
      {showWh && whValue && (
        <span className="card-wh">WH: {whValue}</span>
      )}
      {size === 'full' && installation.realization && (
        <span className="card-date">Realizacja: {installation.realization}</span>
      )}
      {equipment.length > 0 && (
        <div className="card-equipment">
          {equipment.map((item, idx) => (
            <span key={idx} className="card-equipment-badge">{item}</span>
          ))}
        </div>
      )}
      {installation.notes && !calendarNote && (
        <div className="card-note-indicator">
          <span className="note-icon">📝</span>
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
//  Unscheduled orders panel (all orders without planned_start_date)
// ──────────────────────────────────────────────────────────────
function UnscheduledOrdersPanel({
  orders,
  onEdit,
  onDragStart,
  onRefresh,
  onSchedule,
  onAddAdditionalWork,
  canManage,
  specialTruckName = 'TIR',
  highlightedInstallationId = null,
}) {
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [noteValue, setNoteValue] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [showTruckOnly, setShowTruckOnly] = useState(false);
  const [selectedStateKeys, setSelectedStateKeys] = useState(null);
  const [stateMenuOpen, setStateMenuOpen] = useState(false);
  const [orderNumberFilter, setOrderNumberFilter] = useState('');
  const [orderNumberFilterFocused, setOrderNumberFilterFocused] = useState(false);
  const [contractorFilter, setContractorFilter] = useState('');
  const [notesFilter, setNotesFilter] = useState('');
  const [orderPrefixFilter, setOrderPrefixFilter] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'order_number', direction: 'asc' });
  const [scheduleMenu, setScheduleMenu] = useState(null);

  useEffect(() => {
    if (!scheduleMenu) return undefined;
    const closeMenu = () => setScheduleMenu(null);
    window.addEventListener('click', closeMenu);
    window.addEventListener('contextmenu', closeMenu);
    return () => {
      window.removeEventListener('click', closeMenu);
      window.removeEventListener('contextmenu', closeMenu);
    };
  }, [scheduleMenu]);

  useEffect(() => {
    if (!stateMenuOpen) return undefined;
    const closeMenu = () => setStateMenuOpen(false);
    window.addEventListener('click', closeMenu);
    return () => window.removeEventListener('click', closeMenu);
  }, [stateMenuOpen]);

  if (orders.length === 0) return null;

  // Wydobądź unikalne prefiksy numerów zleceń
  const orderPrefixes = [...new Set(
    orders
      .map((inst) => {
        const orderNum = inst.order_number || '';
        const match = orderNum.match(/^([A-Z]+)\//); // Dopasuj prefix przed /
        return match ? match[1] : null;
      })
      .filter(Boolean)
  )].sort();
  const noteOptions = [...new Set(
    orders
      .map((inst) => (inst.notes || '').trim())
      .filter(Boolean)
  )].sort((left, right) => left.localeCompare(right, 'pl', { numeric: true }));

  function isFavorite(inst) {
    return !!inst.is_favorite;
  }

  async function toggleFavorite(inst) {
    try {
      await installationService.updateInstallation(inst.id, { is_favorite: !isFavorite(inst) });
      if (onRefresh) await onRefresh();
    } catch (e) {
      console.error('Błąd zapisu ulubionego zlecenia', e);
    }
  }

  function parseProductionDate(value) {
    const match = String(value || '').match(/(\d{1,2})\.(\d{1,2})\.(\d{4})/);
    if (!match) return 0;
    const [, day, month, year] = match;
    return new Date(Number(year), Number(month) - 1, Number(day)).getTime();
  }

  function changeSort(key) {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  }

  function sortIndicator(key) {
    if (sortConfig.key !== key) return '';
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  }

  function getStateKey(installation) {
    return (installation.state || '').trim() || '__EMPTY_STATE__';
  }

  const stateOptions = [...new Set(orders.map(getStateKey))]
    .sort((left, right) => {
      if (left === '__EMPTY_STATE__') return 1;
      if (right === '__EMPTY_STATE__') return -1;
      return left.localeCompare(right, 'pl', { numeric: true });
    });
  const selectedStateCount = selectedStateKeys === null
    ? stateOptions.length
    : stateOptions.filter((stateKey) => selectedStateKeys.has(stateKey)).length;

  function toggleStateFilter(stateKey) {
    setSelectedStateKeys((current) => {
      const next = current === null
        ? new Set(stateOptions)
        : new Set(stateOptions.filter((option) => current.has(option)));
      if (next.has(stateKey)) {
        next.delete(stateKey);
      } else {
        next.add(stateKey);
      }
      return next.size === stateOptions.length ? null : next;
    });
  }

  const displayedOrders = orders.filter((inst) => {
    if (selectedStateKeys !== null && !selectedStateKeys.has(getStateKey(inst))) return false;
    if (inst.id === highlightedInstallationId) return true;
    if (showFavoritesOnly && !isFavorite(inst)) return false;
    if (showTruckOnly && !hasDeliveryToClient(inst)) return false;
    const orderNeedle = orderNumberFilter.trim().toLowerCase();
    if (orderNeedle && !(inst.order_number || '').toLowerCase().includes(orderNeedle)) return false;
    const contractorNeedle = contractorFilter.trim().toLowerCase();
    if (contractorNeedle && !(inst.contractor || '').toLowerCase().includes(contractorNeedle)) return false;
    if (notesFilter && (inst.notes || '').trim() !== notesFilter) return false;
    if (orderPrefixFilter && !(inst.order_number || '').toUpperCase().startsWith(orderPrefixFilter + '/')) return false;
    return true;
  })
    .sort((a, b) => {
      const direction = sortConfig.direction === 'asc' ? 1 : -1;
      if (sortConfig.key === 'planned_production_date') {
        return (parseProductionDate(a.planned_production_date) - parseProductionDate(b.planned_production_date)) * direction;
      }
      return String(a.order_number || '').localeCompare(String(b.order_number || ''), 'pl', { numeric: true }) * direction;
    });

  function toggleSelect(instId) {
    const newSelected = new Set(selectedOrders);
    if (newSelected.has(instId)) {
      newSelected.delete(instId);
    } else {
      newSelected.add(instId);
    }
    setSelectedOrders(newSelected);
  }

  function clearSelection() {
    setSelectedOrders(new Set());
  }

  function handleDragStart(e, inst) {
    // Jeśli zaznaczono wiele i ten wiersz jest zaznaczony, przeciągnij wszystkie zaznaczone
    if (selectedOrders.size > 0 && selectedOrders.has(inst.id)) {
      const selectedInstallations = orders.filter(o => selectedOrders.has(o.id));
      onDragStart(e, selectedInstallations);
    } else {
      // Pojedyncze przeciąganie
      onDragStart(e, [inst]);
    }
  }

  function openScheduleMenu(event, installation) {
    if (!canManage) return;
    event.preventDefault();
    event.stopPropagation();
    setScheduleMenu({
      x: event.clientX,
      y: event.clientY,
      installation,
    });
  }

  async function startEditNote(inst) {
    setEditingNoteId(inst.id);
    setNoteValue(inst.notes || '');
  }

  async function saveNote(instId) {
    setSavingNote(true);
    try {
      await installationService.updateInstallation(instId, { notes: noteValue });
      setEditingNoteId(null);
      if (onRefresh) {
        await onRefresh();
      }
    } catch (e) {
      console.error('Błąd zapisu notatki', e);
    } finally {
      setSavingNote(false);
    }
  }

  function getScope(inst) {
    if (!inst.items || inst.items.length === 0) return '—';
    const scopes = [...new Set(inst.items.map(i => i.scope).filter(Boolean))];
    return scopes.join(', ') || '—';
  }

  function hasPajaczek(inst) {
    return inst.pajaczek ? 'Tak' : '—';
  }

  // function hasManipulator(inst) {
  //   return inst.manipulator ? 'Tak' : '—';
  // }

  return (
    <div className="unscheduled-panel">
      <div className="unscheduled-header">
        <div className="unscheduled-title">
          <span className="unscheduled-badge">{orders.length}</span>
          <span>Do zaplanowania:</span>
          {displayedOrders.length !== orders.length && (
            <span className="selected-badge">{displayedOrders.length} widocznych</span>
          )}
          {selectedOrders.size > 0 && (
            <span className="selected-badge">{selectedOrders.size} zaznaczonych</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {selectedOrders.size > 0 && (
            <button className="btn-clear-selection" onClick={clearSelection}>
              Odznacz wszystkie
            </button>
          )}
          <span className="unscheduled-hint">↑ Przeciągnij zlecenie na wybrany dzień w kalendarzu, aby je zaplanować</span>
        </div>
      </div>
      <div className="unscheduled-filters">
        <label className={`unscheduled-filter-chip ${showFavoritesOnly ? 'active' : ''}`}>
          <input
            type="checkbox"
            checked={showFavoritesOnly}
            onChange={(e) => setShowFavoritesOnly(e.target.checked)}
          />
          <span>★ Ulubione</span>
        </label>
        <label className={`unscheduled-filter-chip ${showTruckOnly ? 'active' : ''}`}>
          <input
            type="checkbox"
            checked={showTruckOnly}
            onChange={(e) => setShowTruckOnly(e.target.checked)}
          />
          <span>{specialTruckName}</span>
        </label>
        <div
          className="unscheduled-state-filter"
          onClick={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            className={`unscheduled-state-filter-trigger ${selectedStateKeys !== null ? 'active' : ''}`}
            onClick={() => setStateMenuOpen((open) => !open)}
            aria-expanded={stateMenuOpen}
          >
            STAN: {selectedStateKeys === null
              ? 'Wszystkie'
              : `${selectedStateCount} z ${stateOptions.length}`}
            <span aria-hidden="true">▾</span>
          </button>
          {stateMenuOpen && (
            <div className="unscheduled-state-filter-menu">
              <div className="unscheduled-state-filter-actions">
                <button type="button" onClick={() => setSelectedStateKeys(null)}>
                  Zaznacz wszystkie
                </button>
                <button type="button" onClick={() => setSelectedStateKeys(new Set())}>
                  Wyczyść
                </button>
              </div>
              <div className="unscheduled-state-filter-options">
                {stateOptions.map((stateKey) => (
                  <label key={stateKey}>
                    <input
                      type="checkbox"
                      checked={selectedStateKeys === null || selectedStateKeys.has(stateKey)}
                      onChange={() => toggleStateFilter(stateKey)}
                    />
                    <span>{stateKey === '__EMPTY_STATE__' ? 'Brak stanu' : stateKey}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        <label className="unscheduled-filter-search">
          <span>Nr zlecenia</span>
          <input
            value={orderNumberFilter}
            onChange={(e) => {
              setOrderNumberFilter(e.target.value);
              setOrderNumberFilterFocused(true);
            }}
            onFocus={() => setOrderNumberFilterFocused(true)}
            onBlur={() => setOrderNumberFilterFocused(false)}
            placeholder="np. ZW/01/04/119"
          />
          <OrderNumberSuggestions
            query={orderNumberFilter}
            installations={orders}
            visible={orderNumberFilterFocused}
            onSelect={(installation) => {
              setOrderNumberFilter(installation.order_number);
              setOrderNumberFilterFocused(false);
            }}
          />
        </label>
        <label className="unscheduled-filter-search">
          <span>Typ zlecenia</span>
          <select
            value={orderPrefixFilter}
            onChange={(e) => setOrderPrefixFilter(e.target.value)}
            style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
          >
            <option value="">Wszystkie</option>
            {orderPrefixes.map((prefix) => (
              <option key={prefix} value={prefix}>{prefix}</option>
            ))}
          </select>
        </label>
        <label className="unscheduled-filter-search">
          <span>Kontrahent</span>
          <input
            value={contractorFilter}
            onChange={(e) => setContractorFilter(e.target.value)}
            placeholder="np. MAJROWSKI"
          />
        </label>
        <label className="unscheduled-filter-search">
          <span>Notatka</span>
          <select
            value={notesFilter}
            onChange={(e) => setNotesFilter(e.target.value)}
            style={{ width: '190px', padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
          >
            <option value="">Wszystkie</option>
            {noteOptions.map((note) => (
              <option key={note} value={note}>{note}</option>
            ))}
          </select>
        </label>
        {(showFavoritesOnly || showTruckOnly || selectedStateKeys !== null || orderNumberFilter || contractorFilter || notesFilter || orderPrefixFilter) && (
          <button
            className="unscheduled-filter-clear"
            onClick={() => {
              setShowFavoritesOnly(false);
              setShowTruckOnly(false);
              setSelectedStateKeys(null);
              setOrderNumberFilter('');
              setContractorFilter('');
              setNotesFilter('');
              setOrderPrefixFilter('');
            }}
          >
            Wyczyść filtry
          </button>
        )}
      </div>
      <div className="unscheduled-table-wrapper">
        <table className="unscheduled-table">
          <thead>
            <tr>
              <th style={{ width: '40px' }}></th>
              <th style={{ width: '38px' }}></th>
              <th style={{ width: '30px' }}></th>
              <th style={{ width: '200px' }}>Notatka</th>
              <th style={{ width: '120px', cursor: 'pointer', userSelect: 'none' }} onClick={() => changeSort('planned_production_date')}>
                Data produkcji{sortIndicator('planned_production_date')}
              </th>
              <th style={{ width: '180px' }}>Kontrahent</th>
              <th style={{ width: '100px' }}>WH</th>
              <th style={{ width: '130px', cursor: 'pointer', userSelect: 'none' }} onClick={() => changeSort('order_number')}>
                Nr zlecenia{sortIndicator('order_number')}
              </th>
              <th style={{ width: '220px' }}>Adres</th>
              <th style={{ width: '150px' }}>Zakres montażu</th>
              <th style={{ width: '110px' }}>Suma obwodów (m²)</th>
              <th style={{ width: '100px' }}>Pajączek (wynajem)</th>
            </tr>
          </thead>
          <tbody>
            {displayedOrders.map((inst) => {
              const whValue = inst.wh || (inst.items && inst.items.length > 0 
                ? [...new Set(inst.items.map(i => i.wh).filter(Boolean))].join(', ') 
                : '—');
              const deliveryOnly = isDeliveryOnlyInstallation(inst);
              
              return (
                <tr 
                  key={inst.id}
                  className={`unscheduled-row ${selectedOrders.has(inst.id) ? 'selected-row' : ''} ${highlightedInstallationId === inst.id ? 'installation-search-highlight' : ''}`}
                  data-installation-id={inst.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, inst)}
                  onContextMenu={(event) => openScheduleMenu(event, inst)}
                  onClick={(e) => {
                    if (editingNoteId === inst.id) return;
                    if (e.target.type === 'checkbox') return;
                    onEdit(inst);
                  }}
                  style={{
                    cursor: editingNoteId === inst.id ? 'default' : 'grab',
                    background: deliveryOnly ? '#dbeafe' : undefined,
                    borderLeft: deliveryOnly ? '4px solid #2563eb' : undefined,
                  }}
                >
                  <td onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedOrders.has(inst.id)}
                      onChange={() => toggleSelect(inst.id)}
                      className="order-checkbox"
                    />
                  </td>
                  <td onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center' }}>
                    <button
                      type="button"
                      onClick={() => toggleFavorite(inst)}
                      title={isFavorite(inst) ? 'Usuń z ulubionych' : 'Dodaj do ulubionych'}
                      style={{
                        border: 0,
                        background: 'transparent',
                        cursor: 'pointer',
                        color: isFavorite(inst) ? '#ca8a04' : '#94a3b8',
                        fontSize: '18px',
                        lineHeight: 1,
                      }}
                    >
                      ★
                    </button>
                  </td>
                  <td className="drag-handle" title="Przeciągnij aby zaplanować">
                    <span className="drag-icon">⋮⋮</span>
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {editingNoteId === inst.id ? (
                      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        <input
                          className="note-input"
                          value={noteValue}
                          onChange={(e) => setNoteValue(e.target.value)}
                          autoFocus
                          disabled={savingNote}
                        />
                        <button 
                          className="btn-save-note" 
                          onClick={() => saveNote(inst.id)}
                          disabled={savingNote}
                          title="Zapisz"
                        >
                          ✓
                        </button>
                        <button 
                          className="btn-cancel-note" 
                          onClick={() => setEditingNoteId(null)}
                          disabled={savingNote}
                          title="Anuluj"
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <div 
                        className="note-cell"
                        onClick={() => startEditNote(inst)}
                        title="Kliknij aby edytować notatkę"
                      >
                        {inst.notes || <span className="note-empty">Brak notatki</span>}
                      </div>
                    )}
                  </td>
                  <td>{inst.planned_production_date || '—'}</td>
                  <td>{inst.contractor || '—'}</td>
                  <td>{whValue}</td>
                  <td><strong>{inst.order_number}</strong></td>
                  <td>{inst.address || '—'}</td>
                  <td>{getScope(inst)}</td>
                  <td>{getTotalPerimeter(inst).toFixed(2)}</td>
                  <td>{hasPajaczek(inst)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {scheduleMenu && (
        <div
          className="installation-context-menu"
          onClick={(event) => event.stopPropagation()}
          onContextMenu={(event) => event.preventDefault()}
          style={{
            left: Math.min(scheduleMenu.x, window.innerWidth - 270),
            top: Math.min(scheduleMenu.y, window.innerHeight - 170),
          }}
        >
          <div className="installation-context-menu-title">
            {scheduleMenu.installation.order_number}
          </div>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => {
              onSchedule?.(scheduleMenu.installation);
              setScheduleMenu(null);
            }}
          >
            Przypisz dzień i ekipę
          </button>
          {canAddAdditionalWork(scheduleMenu.installation, orders) && (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                onAddAdditionalWork?.(scheduleMenu.installation);
                setScheduleMenu(null);
              }}
            >
              Dodaj dodatkową pracę
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
//  Calendar views
// ──────────────────────────────────────────────────────────────
function MonthView({ currentDate, installations, onEdit, onAddAdditionalWork, onDragStart, onDrop, canManage, hideSundays = false, specialTruckName = 'TIR', highlightedInstallationId = null }) {
  const [contextMenu, setContextMenu] = useState(null);
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  const firstDayOfWeek = (firstDay.getDay() + 6) % 7; // Monday=0
  const daysInMonth = lastDay.getDate();
  const totalCells = Math.ceil((firstDayOfWeek + daysInMonth) / 7) * 7;

  useEffect(() => {
    if (!contextMenu) return undefined;
    const closeMenu = () => setContextMenu(null);
    window.addEventListener('click', closeMenu);
    window.addEventListener('contextmenu', closeMenu);
    return () => {
      window.removeEventListener('click', closeMenu);
      window.removeEventListener('contextmenu', closeMenu);
    };
  }, [contextMenu]);

  function openContextMenu(event, installation) {
    if (!canManage || !canAddAdditionalWork(installation, installations)) return;
    event.preventDefault();
    event.stopPropagation();
    setContextMenu({
      x: event.clientX,
      y: event.clientY,
      installation,
    });
  }

  function getInstallationsForDay(day) {
    return installations.filter((inst) => {
      if (!inst.planned_start_date) return false;
      return installationIncludesDay(inst, toISODate(day));
    });
  }

  const cols = hideSundays ? 6 : 7;
  const days = [];
  
  if (hideSundays) {
    // When Sundays are hidden, use 6-column grid
    const adjustedPad = firstDayOfWeek === 6 ? 0 : firstDayOfWeek;
    
    // Fill leading days from previous month (excluding Sundays)
    if (adjustedPad > 0) {
      const prevMonthLastDay = new Date(year, month, 0).getDate();
      for (let i = adjustedPad - 1; i >= 0; i--) {
        const day = new Date(year, month - 1, prevMonthLastDay - i);
        if (day.getDay() !== 0) days.push(day);
      }
    }
    
    // Current month days (excluding Sundays)
    for (let d = 1; d <= daysInMonth; d++) {
      const day = new Date(year, month, d);
      if (day.getDay() !== 0) days.push(day);
    }
    
    // Fill trailing days from next month (excluding Sundays)
    let nextDay = 1;
    while (days.length % 6 !== 0) {
      const day = new Date(year, month + 1, nextDay++);
      if (day.getDay() !== 0) days.push(day);
    }
  } else {
    // Standard 7-column grid
    for (let i = 0; i < totalCells; i++) {
      const dayNum = i - firstDayOfWeek + 1;
      if (dayNum > 0 && dayNum <= daysInMonth) {
        // Current month
        days.push(new Date(year, month, dayNum));
      } else if (dayNum <= 0) {
        // Previous month
        const prevMonthLastDay = new Date(year, month, 0).getDate();
        days.push(new Date(year, month - 1, prevMonthLastDay + dayNum));
      } else {
        // Next month
        days.push(new Date(year, month + 1, dayNum - daysInMonth));
      }
    }
  }

  const headers = hideSundays ? POLISH_DAYS_SHORT.slice(0, 6) : POLISH_DAYS_SHORT;
  const gridStyle = { gridTemplateColumns: `repeat(${cols}, 1fr)` };

  return (
    <div className="calendar-month">
      <div className="calendar-week-header" style={gridStyle}>
        {headers.map((d) => <div key={d} className="calendar-day-name">{d}</div>)}
      </div>
      <div className="calendar-grid" style={gridStyle}>
        {days.map((day, idx) => {
          const inMonth = day.getMonth() === month;
          const isToday = isSameDay(day, new Date());
          const dayInstallations = getInstallationsForDay(day);
          return (
            <div
              key={toISODate(day)}
              className={`calendar-cell ${!inMonth ? 'other-month' : ''} ${isToday ? 'today' : ''}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => onDrop(e, day)}
            >
              <div className="cell-day-number">{day.getDate()}</div>
              <div className="cell-installations">
                {dayInstallations.map((inst) => (
                  <InstallationCard
                    key={inst.id}
                    installation={inst}
                    onEdit={onEdit}
                    onContextMenu={(event) => openContextMenu(event, inst)}
                    onDragStart={(event, installation) => onDragStart(event, installation, {
                      day: toISODate(day),
                      teamId: null,
                      allTeams: true,
                    })}
                    currentDate={day}
                    specialTruckName={specialTruckName}
                    highlighted={highlightedInstallationId === inst.id}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {contextMenu && (
        <div
          className="installation-context-menu"
          onClick={(event) => event.stopPropagation()}
          onContextMenu={(event) => event.preventDefault()}
          style={{
            left: Math.min(contextMenu.x, window.innerWidth - 270),
            top: Math.min(contextMenu.y, window.innerHeight - 120),
          }}
        >
          <div className="installation-context-menu-title">
            {contextMenu.installation.order_number}
          </div>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => {
              onAddAdditionalWork?.(contextMenu.installation);
              setContextMenu(null);
            }}
          >
            Dodaj dodatkową pracę
          </button>
        </div>
      )}
    </div>
  );
}

// function WeekView({ currentDate, installations, onEdit, onDragStart, onDrop, hideSundays = false }) {
//   const [dragOverCell, setDragOverCell] = useState(null);
//   const monday = startOfWeek(currentDate);
//   const allDays = Array.from({ length: 7 }, (_, i) => addDays(monday, i));

//   function getInstallationsForDay(day) {
//     return installations.filter((inst) => {
//       if (!inst.planned_start_date) return false;
//       const start = new Date(inst.planned_start_date);
//       const end = inst.planned_end_date ? new Date(inst.planned_end_date) : start;
//       return isInRange(day, start, end);
//     });
//   }

//   // Filter out Sundays if hideSundays is enabled
//   const days = hideSundays
//     ? allDays.filter(day => day.getDay() !== 0)
//     : allDays;

//   return (
//     <div className="calendar-week" style={{ gridTemplateColumns: `repeat(${days.length}, 1fr)` }}>
//       {days.map((day) => {
//         const isToday = isSameDay(day, new Date());
//         const dayInsts = getInstallationsForDay(day);
//         const cellKey = toISODate(day);
//         return (
//           <div
//             key={cellKey}
//             className={`week-day-col ${isToday ? 'today' : ''} ${dragOverCell === cellKey ? 'drag-over' : ''}`}
//             onDragOver={(e) => e.preventDefault()}
//             onDragEnter={() => setDragOverCell(cellKey)}
//             onDragLeave={() => setDragOverCell(null)}
//             onDrop={(e) => { onDrop(e, day); setDragOverCell(null); }}
//           >
//             <div className="week-day-header">
//               <span className="week-day-name">
//                 {POLISH_DAYS_SHORT[days.indexOf(day)]}
//               </span>
//               <span className={`week-day-num ${isToday ? 'today-num' : ''}`}>
//                 {day.getDate()}
//               </span>
//             </div>
//             <div className="week-day-body">
//               {dayInsts.map((inst) => (
//                 <InstallationCard
//                   key={inst.id}
//                   installation={inst}
//                   onEdit={onEdit}
//                   onDragStart={onDragStart}
//                   currentDate={day}
//                 />
//               ))}
//             </div>
//           </div>
//         );
//       })}
//     </div>
//   );
// }

// function DayView({ currentDate, installations, onEdit, onDragStart, onDrop }) {
//   const [dragOver, setDragOver] = useState(false);
//   const dayInsts = installations.filter((inst) => {
//     if (!inst.planned_start_date) return false;
//     const start = new Date(inst.planned_start_date);
//     const end = inst.planned_end_date ? new Date(inst.planned_end_date) : start;
//     return isInRange(currentDate, start, end);
//   });

//   return (
//     <div
//       className={`calendar-day ${dragOver ? 'drag-over' : ''}`}
//       onDragOver={(e) => e.preventDefault()}
//       onDragEnter={() => setDragOver(true)}
//       onDragLeave={() => setDragOver(false)}
//       onDrop={(e) => { onDrop(e, currentDate); setDragOver(false); }}
//     >
//       <div className="day-header">
//         <span className="day-date-label">
//           {currentDate.getDate()} {POLISH_MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
//         </span>
//       </div>
//       <div className="day-body">
//         {dayInsts.length === 0 ? (
//           <div className="day-empty">Brak montaży w tym dniu</div>
//         ) : (
//           dayInsts.map((inst) => (
//             <InstallationCard
//               key={inst.id}
//               installation={inst}
//               onEdit={onEdit}
//               onDragStart={onDragStart}
//               currentDate={currentDate}
//             />
//           ))
//         )}
//       </div>
//     </div>
//   );
// }

function TeamRowsView({ currentDate, installations, teams, onEdit, onAddAdditionalWork, onDragStart, onDrop, onAssignmentsChanged, canManage, hideSundays = false, showAddresses = false, showWh = false, highlightedInstallationId = null }) {
  const [dragOverCell, setDragOverCell] = useState(null);
  const [assignmentMenu, setAssignmentMenu] = useState(null);
  const [assignmentMenuMode, setAssignmentMenuMode] = useState(null);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [assignmentMenuBusy, setAssignmentMenuBusy] = useState(false);
  const [assignmentMenuError, setAssignmentMenuError] = useState('');
  const [reorderingGroup, setReorderingGroup] = useState(null);
  const [reorderError, setReorderError] = useState('');
  const [teamDayComments, setTeamDayComments] = useState([]);
  const [calendarVacations, setCalendarVacations] = useState([]);
  const [editingTeamComment, setEditingTeamComment] = useState(null);
  const [teamCommentBusy, setTeamCommentBusy] = useState(false);
  const [teamCommentError, setTeamCommentError] = useState('');
  const [hiddenTeamIds, setHiddenTeamIds] = useState([]);
  const [hiddenTeamsBusy, setHiddenTeamsBusy] = useState(false);
  const [hiddenTeamsError, setHiddenTeamsError] = useState('');
  const monday = startOfWeek(currentDate);
  const allDays = Array.from({ length: 7 }, (_, i) => addDays(monday, i));
  const weekStartISO = toISODate(monday);
  const weekEndISO = toISODate(addDays(monday, 6));
  const tirTeam = findTirTeam(teams);
  const specialTruckName = tirTeam?.name || 'TIR';
  const hiddenTeamIdSet = useMemo(() => new Set(hiddenTeamIds), [hiddenTeamIds]);
  const visibleTeams = teams.filter((team) => !hiddenTeamIdSet.has(team.id));
  const hiddenTeams = teams.filter((team) => hiddenTeamIdSet.has(team.id));

  useEffect(() => {
    if (!assignmentMenu) return undefined;
    const closeMenu = () => {
      setAssignmentMenu(null);
      setAssignmentMenuMode(null);
    };
    window.addEventListener('click', closeMenu);
    window.addEventListener('contextmenu', closeMenu);
    return () => {
      window.removeEventListener('click', closeMenu);
      window.removeEventListener('contextmenu', closeMenu);
    };
  }, [assignmentMenu]);

  const fetchTeamDayComments = useCallback(async () => {
    try {
      const comments = await installationService.getTeamDayComments(weekStartISO, weekEndISO);
      setTeamDayComments(comments);
    } catch (error) {
      console.error('Nie udało się pobrać komentarzy ekip', error);
    }
  }, [weekStartISO, weekEndISO]);

  useEffect(() => {
    fetchTeamDayComments();
  }, [fetchTeamDayComments]);

  useEffect(() => {
    let cancelled = false;
    setHiddenTeamsBusy(true);
    setHiddenTeamsError('');
    installationService.getWeeklyHiddenTeams(weekStartISO)
      .then((data) => {
        if (!cancelled) setHiddenTeamIds(data.team_ids || []);
      })
      .catch((error) => {
        if (!cancelled) {
          console.error('Nie udało się pobrać ukrytych ekip', error);
          setHiddenTeamIds([]);
          setHiddenTeamsError('Nie udało się pobrać ustawień ukrytych ekip.');
        }
      })
      .finally(() => {
        if (!cancelled) setHiddenTeamsBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [weekStartISO]);

  useEffect(() => {
    let cancelled = false;
    installationService.getCalendarVacations(weekStartISO, weekEndISO)
      .then((data) => {
        if (!cancelled) setCalendarVacations(data);
      })
      .catch((error) => {
        if (!cancelled) {
          console.error('Nie udało się pobrać urlopów dla kalendarza', error);
          setCalendarVacations([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [weekStartISO, weekEndISO]);

  function getTeamDayComment(teamId, day) {
    const dayISO = toISODate(day);
    return teamDayComments.find(comment => comment.team_id === teamId && comment.comment_date === dayISO);
  }

  async function updateHiddenTeams(nextTeamIds) {
    if (hiddenTeamsBusy) return;
    const previousTeamIds = hiddenTeamIds;
    setHiddenTeamIds(nextTeamIds);
    setHiddenTeamsBusy(true);
    setHiddenTeamsError('');
    try {
      const data = await installationService.saveWeeklyHiddenTeams(weekStartISO, nextTeamIds);
      setHiddenTeamIds(data.team_ids || []);
    } catch (error) {
      console.error('Nie udało się zapisać ukrytych ekip', error);
      setHiddenTeamIds(previousTeamIds);
      setHiddenTeamsError(
        error.response?.data?.detail || 'Nie udało się zapisać ustawień ukrytych ekip.'
      );
    } finally {
      setHiddenTeamsBusy(false);
    }
  }

  function hideTeam(teamId) {
    updateHiddenTeams([...new Set([...hiddenTeamIds, teamId])]);
  }

  function showTeam(teamId) {
    updateHiddenTeams(hiddenTeamIds.filter((id) => id !== teamId));
  }

  function getTeamVacationsForDay(team, day) {
    const dayISO = toISODate(day);
    const memberIds = new Set((team.members || []).map((member) => member.id));
    return calendarVacations.filter((vacation) => (
      memberIds.has(vacation.installer_id)
      && vacation.start_date <= dayISO
      && vacation.end_date >= dayISO
    ));
  }

  function openTeamCommentEditor(event, team, day) {
    if (!canManage || event.target.closest('.installation-card, .team-installation-order-controls, .team-vacation-comment')) return;
    const existingComment = getTeamDayComment(team.id, day);
    setTeamCommentError('');
    setEditingTeamComment({
      teamId: team.id,
      teamName: team.name,
      date: toISODate(day),
      content: existingComment?.content || '',
      exists: !!existingComment,
      duplicateGroupId: existingComment?.duplicate_group_id || null,
      duplicateCount: existingComment?.duplicate_count || (existingComment?.duplicate_group_id ? 2 : 1),
      conversionPromptOpen: false,
      deletionPromptOpen: false,
      visibleInSettlement: false,
      selectedDates: [toISODate(day)],
      selectedTeamIds: [team.id],
      calendarExpanded: false,
      calendarMonth: day.getMonth(),
      calendarYear: day.getFullYear(),
    });
  }

  function changeTeamCommentMonth(offset) {
    setEditingTeamComment((current) => {
      const next = new Date(current.calendarYear, current.calendarMonth + offset, 1);
      return {
        ...current,
        calendarMonth: next.getMonth(),
        calendarYear: next.getFullYear(),
      };
    });
  }

  function toggleTeamCommentDate(dateStr) {
    setEditingTeamComment((current) => {
      if (dateStr === current.date) return current;
      const selectedDates = current.selectedDates.includes(dateStr)
        ? current.selectedDates.filter((day) => day !== dateStr)
        : [...current.selectedDates, dateStr].sort();
      return { ...current, selectedDates };
    });
  }

  function toggleTeamCommentTeam(teamId) {
    setEditingTeamComment((current) => {
      if (teamId === current.teamId) return current;
      const selectedTeamIds = current.selectedTeamIds.includes(teamId)
        ? current.selectedTeamIds.filter((id) => id !== teamId)
        : [...current.selectedTeamIds, teamId];
      return { ...current, selectedTeamIds };
    });
  }

  function renderTeamCommentCalendar() {
    const firstDay = new Date(
      editingTeamComment.calendarYear,
      editingTeamComment.calendarMonth,
      1
    );
    const lastDay = new Date(
      editingTeamComment.calendarYear,
      editingTeamComment.calendarMonth + 1,
      0
    );
    const days = [];
    let current = startOfWeek(firstDay);
    while (current <= lastDay || days.length % 7 !== 0) {
      days.push(new Date(current));
      current = addDays(current, 1);
      if (days.length >= 42) break;
    }

    return (
      <div className="split-calendar team-comment-date-picker">
        <div className="split-calendar-header">
          <button type="button" className="btn-nav" onClick={() => changeTeamCommentMonth(-1)}>‹</button>
          <span className="split-calendar-month">
            {POLISH_MONTHS[editingTeamComment.calendarMonth]} {editingTeamComment.calendarYear}
          </span>
          <button type="button" className="btn-nav" onClick={() => changeTeamCommentMonth(1)}>›</button>
        </div>
        <div className="vac-cal-grid">
          {POLISH_DAYS_SHORT.map((dayName) => <div key={dayName} className="vac-cal-head">{dayName}</div>)}
          {days.map((day) => {
            const dateStr = toISODate(day);
            const isCurrentMonth = day.getMonth() === editingTeamComment.calendarMonth;
            const isSelected = editingTeamComment.selectedDates.includes(dateStr);
            const isSource = dateStr === editingTeamComment.date;
            return (
              <div
                key={dateStr}
                className={`vac-cal-day ${isCurrentMonth ? '' : 'other-month'} ${isSelected ? 'selected-date' : ''} ${isSource ? 'source-date' : ''}`}
                data-date={dateStr}
                role="button"
                tabIndex={isCurrentMonth ? 0 : -1}
                aria-pressed={isSelected}
                aria-disabled={isSource}
                title={isSource ? 'Pierwotna data komentarza' : undefined}
                onClick={() => isCurrentMonth && toggleTeamCommentDate(dateStr)}
                onKeyDown={(event) => {
                  if (isCurrentMonth && (event.key === 'Enter' || event.key === ' ')) {
                    event.preventDefault();
                    toggleTeamCommentDate(dateStr);
                  }
                }}
                style={{ cursor: isSource ? 'not-allowed' : isCurrentMonth ? 'pointer' : 'default' }}
              >
                {day.getDate()}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  async function saveTeamComment(conversionScope = null) {
    const content = editingTeamComment?.content.trim();
    if (!editingTeamComment || !content) return;
    if (
      editingTeamComment.visibleInSettlement
      && editingTeamComment.duplicateGroupId
      && conversionScope === null
    ) {
      setEditingTeamComment(current => ({
        ...current,
        conversionPromptOpen: true,
        deletionPromptOpen: false,
      }));
      return;
    }
    setTeamCommentBusy(true);
    setTeamCommentError('');
    try {
      if (editingTeamComment.visibleInSettlement) {
        await installationService.promoteTeamDayCommentToCalendarNote(
          editingTeamComment.teamId,
          editingTeamComment.date,
          content,
          conversionScope || 'single'
        );
      } else {
        const selectedTeamIds = editingTeamComment.selectedTeamIds;
        await installationService.saveTeamDayComments(
          selectedTeamIds.length === 1 ? selectedTeamIds[0] : selectedTeamIds,
          editingTeamComment.selectedDates,
          content,
          editingTeamComment.teamId,
          editingTeamComment.date
        );
      }
      await fetchTeamDayComments();
      if (editingTeamComment.visibleInSettlement) {
        await onAssignmentsChanged?.();
      }
      setEditingTeamComment(null);
    } catch (error) {
      console.error('Nie udało się zapisać komentarza ekipy', error);
      setTeamCommentError(error.response?.data?.detail || 'Nie udało się zapisać komentarza.');
    } finally {
      setTeamCommentBusy(false);
    }
  }

  async function deleteTeamComment(deleteScope = null) {
    if (!editingTeamComment?.exists) return;
    if (editingTeamComment.duplicateGroupId && deleteScope === null) {
      setEditingTeamComment(current => ({
        ...current,
        conversionPromptOpen: false,
        deletionPromptOpen: true,
      }));
      return;
    }
    setTeamCommentBusy(true);
    setTeamCommentError('');
    try {
      await installationService.deleteTeamDayComment(
        editingTeamComment.teamId,
        editingTeamComment.date,
        deleteScope || 'single'
      );
      await fetchTeamDayComments();
      setEditingTeamComment(null);
    } catch (error) {
      console.error('Nie udało się usunąć komentarza ekipy', error);
      setTeamCommentError(error.response?.data?.detail || 'Nie udało się usunąć komentarza.');
    } finally {
      setTeamCommentBusy(false);
    }
  }

  function openAssignmentMenu(event, installation, day, currentTeamId) {
    if (!canManage) return;
    event.preventDefault();
    event.stopPropagation();
    if (installation._schedulePending) return;
    setAssignmentMenu({
      x: event.clientX,
      y: event.clientY,
      installation,
      day: toISODate(day),
      currentTeamId,
    });
    setAssignmentMenuMode(null);
    setStatusMenuOpen(false);
    setAssignmentMenuError('');
  }

  async function changeTeamAssignment(targetTeamId, mode) {
    if (!assignmentMenu || assignmentMenuBusy) return;
    const { installation, day, currentTeamId } = assignmentMenu;
    if (!confirmDadTeamChange(installation, targetTeamId, teams)) return;
    setAssignmentMenuBusy(true);
    setAssignmentMenuError('');
    try {
      if (mode === 'move' && currentTeamId !== null && currentTeamId !== undefined) {
        const oldAssignment = (installation.assignments || []).find(
          (assignment) => assignment.team_id === currentTeamId && assignment.assigned_date === day
        );
        if (!oldAssignment) {
          throw new Error('Nie znaleziono przypisania ekipy dla wybranego dnia. Odśwież kalendarz i spróbuj ponownie.');
        }
        await installationService.moveAssignmentToTeam(oldAssignment.id, targetTeamId);
      } else {
        await installationService.createAssignment(installation.id, {
          team_id: targetTeamId,
          assigned_date: day,
        });
      }

      setAssignmentMenu(null);
      setAssignmentMenuMode(null);
      await onAssignmentsChanged?.();
    } catch (error) {
      console.error(`Nie udało się ${mode === 'move' ? 'przenieść' : 'dodać'} ekipy`, error);
      setAssignmentMenuError(
        error.response?.data?.detail || error.message || 'Nie udało się zmienić przypisania ekipy.'
      );
    } finally {
      setAssignmentMenuBusy(false);
    }
  }

  async function quickChangeStatus(targetStatus) {
    if (!assignmentMenu || assignmentMenuBusy) return;
    setAssignmentMenuBusy(true);
    setAssignmentMenuError('');
    try {
      await installationService.updateInstallationCalendar(
        assignmentMenu.installation.id,
        { calendar_status: targetStatus }
      );
      setAssignmentMenu(null);
      setAssignmentMenuMode(null);
      await onAssignmentsChanged?.();
    } catch (error) {
      console.error('Nie udało się szybko zmienić statusu', error);
      setAssignmentMenuError(
        error.response?.data?.detail || 'Nie udało się zmienić statusu zlecenia.'
      );
    } finally {
      setAssignmentMenuBusy(false);
    }
  }

  // Get installations for a specific team and day
  function getInstallationsForTeamAndDay(teamId, day) {
    const dayISO = toISODate(day);
    return installations.filter((inst) => {
      if (!inst.planned_start_date) return false;
      
      // Check if installation is on this day
      if (!installationIncludesDay(inst, toISODate(day))) return false;
      
      // Explicit crew assignments win.  If only people were assigned, show
      // the card in one inferred crew (prefer the assigned crew leader).
      const hasExplicitTeamAssignment = (inst.assignments || []).some(
        (assignment) => assignment.team_id === teamId
          && assignment.assigned_date === dayISO
      );
      if (hasExplicitTeamAssignment) return true;
      return inferredTeamIdForDay(inst, dayISO, teams) === teamId;
    }).sort((left, right) => {
      const leftAssignment = (left.assignments || []).find(
        assignment => assignment.assigned_date === dayISO && (
          assignment.team_id === teamId
          || (assignment.team_id == null && assignment.installer_id != null)
        )
      );
      const rightAssignment = (right.assignments || []).find(
        assignment => assignment.assigned_date === dayISO && (
          assignment.team_id === teamId
          || (assignment.team_id == null && assignment.installer_id != null)
        )
      );
      return (leftAssignment?.display_order || 0) - (rightAssignment?.display_order || 0)
        || (leftAssignment?.id || 0) - (rightAssignment?.id || 0);
    });
  }

  async function moveInstallationInTeamDay(teamId, day, dayInstallations, index, direction) {
    if (dayInstallations.some(item => item._schedulePending)) return;
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= dayInstallations.length) return;

    const dayISO = toISODate(day);
    const assignmentIds = dayInstallations.map(installation =>
      (installation.assignments || []).find(
        assignment => assignment.team_id === teamId && toISODate(new Date(assignment.assigned_date)) === dayISO
      )?.id
    );
    if (assignmentIds.some(assignmentId => !assignmentId)) {
      setReorderError('Nie udało się ustalić przypisań dla wszystkich zleceń. Odśwież kalendarz.');
      return;
    }

    [assignmentIds[index], assignmentIds[targetIndex]] = [assignmentIds[targetIndex], assignmentIds[index]];
    const groupKey = `${teamId}-${dayISO}`;
    setReorderingGroup(groupKey);
    setReorderError('');
    try {
      await installationService.reorderAssignments(assignmentIds);
      await onAssignmentsChanged?.();
    } catch (error) {
      console.error('Nie udało się zmienić kolejności zleceń', error);
      setReorderError(error.response?.data?.detail || 'Nie udało się zapisać kolejności zleceń.');
    } finally {
      setReorderingGroup(null);
    }
  }

  // Get unassigned installations for a day
  function getUnassignedForDay(day) {
    const dayISO = toISODate(day);
    return installations.filter((inst) => {
      if (!inst.planned_start_date) return false;
      
      // Check if installation is on this day
      if (!installationIncludesDay(inst, toISODate(day))) return false;
      
      const hasTeamAssignmentForDay = (inst.assignments || []).some(
        (assignment) => assignment.assigned_date === dayISO &&
          assignment.team_id != null
      );
      const inferredTeamId = inferredTeamIdForDay(inst, dayISO, teams);
      return !hasTeamAssignmentForDay && inferredTeamId == null;
    });
  }

  // Filter out Sundays if hideSundays is enabled
  const days = hideSundays
    ? allDays.filter(day => day.getDay() !== 0)
    : allDays;

  const teamGridStyle = { gridTemplateColumns: `150px repeat(${days.length}, 1fr)` };
  const rawAssignmentMenuStatus = assignmentMenu?.installation.calendar_status;
  const assignmentMenuStatus = rawAssignmentMenuStatus === 'team_assigned'
    ? 'planned'
    : rawAssignmentMenuStatus;
  const quickStatusOptions = assignmentMenuStatus === 'pre_assigned'
    ? ['planned', 'confirmed']
    : assignmentMenuStatus === 'planned'
      ? ['pre_assigned', 'confirmed']
      : [];

  return (
    <div className="team-rows-view">
      {reorderError && <div className="team-order-error" role="alert">{reorderError}</div>}
      {hiddenTeamsError && <div className="team-order-error" role="alert">{hiddenTeamsError}</div>}
      {hiddenTeams.length > 0 && (
        <div className="hidden-teams-bar">
          <span className="hidden-teams-label">Ukryte ekipy ({hiddenTeams.length}):</span>
          {hiddenTeams.map((team) => (
            <button
              key={team.id}
              type="button"
              className="hidden-team-button"
              onClick={() => showTeam(team.id)}
              disabled={hiddenTeamsBusy}
              title={`Pokaż ekipę ${team.name}`}
            >
              <Eye size={14} aria-hidden="true" />
              {team.name}
            </button>
          ))}
          {hiddenTeams.length > 1 && (
            <button
              type="button"
              className="show-all-teams-button"
              onClick={() => updateHiddenTeams([])}
              disabled={hiddenTeamsBusy}
            >
              Pokaż wszystkie
            </button>
          )}
        </div>
      )}
      <div className="team-rows-header" style={teamGridStyle}>
        <div className="team-name-cell">Ekipa</div>
        {days.map((day) => {
          const isToday = isSameDay(day, new Date());
          return (
            <div key={toISODate(day)} className={`team-day-header ${isToday ? 'today' : ''}`}>
              <div className="team-day-name">{POLISH_DAYS_SHORT[days.indexOf(day)]}</div>
              <div className="team-day-num">{day.getDate()}</div>
            </div>
          );
        })}
      </div>
      <div className="team-rows-body">
        {visibleTeams.map((team) => (
          <div key={team.id} className="team-row" style={teamGridStyle}>
            <div className="team-name-cell">
              <span className="team-name-label">{team.name}</span>
              <button
                type="button"
                className="hide-team-button"
                onClick={() => hideTeam(team.id)}
                disabled={hiddenTeamsBusy}
                title={`Ukryj ekipę ${team.name} w tym tygodniu`}
                aria-label={`Ukryj ekipę ${team.name} w tym tygodniu`}
              >
                <EyeOff size={15} aria-hidden="true" />
              </button>
            </div>
            {days.map((day) => {
              const teamDayInsts = getInstallationsForTeamAndDay(team.id, day);
              const teamDayVacations = getTeamVacationsForDay(team, day);
              const cellKey = `${team.id}-${toISODate(day)}`;
              const teamDayComment = getTeamDayComment(team.id, day);
              const dayISO = toISODate(day);
              const hasExplicitTeamAssignment = (installation) =>
                (installation.assignments || []).some(
                  (assignment) => assignment.assigned_date === dayISO
                    && assignment.team_id === team.id
                );
              const canReorderTeamDay = teamDayInsts.length > 1
                && teamDayInsts.every(hasExplicitTeamAssignment);
              return (
                <div
                  key={cellKey}
                  className={`team-day-cell ${dragOverCell === cellKey ? 'drag-over' : ''}`}
                  onDragOver={(e) => e.preventDefault()}
                  onDragEnter={() => setDragOverCell(cellKey)}
                  onDragLeave={() => setDragOverCell(null)}
                  onDrop={(e) => { onDrop(e, day, team.id); setDragOverCell(null); }}
                  onDoubleClick={(event) => openTeamCommentEditor(event, team, day)}
                  title={canManage ? 'Kliknij dwukrotnie, aby dodać lub edytować komentarz' : undefined}
                >
                  {teamDayComment && (
                    <div
                      className="team-day-comment"
                      title={teamDayComment.content}
                      role={canManage ? 'button' : undefined}
                      tabIndex={canManage ? 0 : undefined}
                      onClick={(event) => {
                        if (!canManage) return;
                        event.stopPropagation();
                        openTeamCommentEditor(event, team, day);
                      }}
                      onKeyDown={(event) => {
                        if (canManage && (event.key === 'Enter' || event.key === ' ')) {
                          event.preventDefault();
                          event.stopPropagation();
                          openTeamCommentEditor(event, team, day);
                        }
                      }}
                    >
                      <span>💬</span>{teamDayComment.content}
                    </div>
                  )}
                  {teamDayVacations.map((vacation) => (
                    <div
                      key={vacation.id}
                      className={`team-vacation-comment ${vacation.approved ? 'approved' : 'planned'}`}
                      title={`${vacation.approved ? 'Urlop potwierdzony' : 'Urlop zaplanowany'}: ${vacation.installer_name} ${vacation.installer_surname} (${vacation.vacation_type})`}
                    >
                      Urlop {vacation.installer_name} {vacation.installer_surname} ({vacation.vacation_type})
                    </div>
                  ))}
                  {teamDayInsts.map((inst, index) => {
                    // Inferred placement is visual only: direct-person
                    // assignments still have no persisted source team.
                    const sourceTeamId = hasExplicitTeamAssignment(inst) ? team.id : null;
                    return (
                      <div className="team-installation-order-item" key={inst.id} onContextMenu={(event) => openAssignmentMenu(event, inst, day, sourceTeamId)}>
                        {canManage && canReorderTeamDay && (
                          <div className="team-installation-order-controls" onClick={(event) => event.stopPropagation()}>
                            <button
                              type="button"
                              onClick={() => moveInstallationInTeamDay(team.id, day, teamDayInsts, index, -1)}
                              disabled={index === 0 || reorderingGroup === cellKey}
                              title="Przesuń zlecenie wyżej"
                              aria-label={`Przesuń zlecenie ${inst.order_number} wyżej`}
                            >↑</button>
                            <button
                              type="button"
                              onClick={() => moveInstallationInTeamDay(team.id, day, teamDayInsts, index, 1)}
                              disabled={index === teamDayInsts.length - 1 || reorderingGroup === cellKey}
                              title="Przesuń zlecenie niżej"
                              aria-label={`Przesuń zlecenie ${inst.order_number} niżej`}
                            >↓</button>
                          </div>
                        )}
                        <InstallationCard
                          installation={inst}
                          onEdit={onEdit}
                          onDragStart={(event, installation) => onDragStart(event, installation, {
                            day: dayISO,
                            teamId: sourceTeamId,
                          })}
                          currentDate={day}
                          currentTeamId={sourceTeamId}
                          specialTruckName={specialTruckName}
                          showAddress={showAddresses}
                          showWh={showWh}
                          highlighted={highlightedInstallationId === inst.id}
                        />
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        ))}
        <div className="team-row unassigned-row" style={teamGridStyle}>
          <div className="team-name-cell">
            <span className="team-name-label">Nieprzypisane</span>
          </div>
          {days.map((day) => {
            const unassignedInsts = getUnassignedForDay(day);
            const cellKey = `unassigned-${toISODate(day)}`;
            return (
              <div
                key={cellKey}
                className={`team-day-cell ${dragOverCell === cellKey ? 'drag-over' : ''}`}
                onDragOver={(e) => e.preventDefault()}
                onDragEnter={() => setDragOverCell(cellKey)}
                onDragLeave={() => setDragOverCell(null)}
                onDrop={(e) => { onDrop(e, day, null); setDragOverCell(null); }}
              >
                {unassignedInsts.map((inst) => (
                  <div key={inst.id} onContextMenu={(event) => openAssignmentMenu(event, inst, day, null)}>
                    <InstallationCard
                      installation={inst}
                      onEdit={onEdit}
                      onDragStart={(event, installation) => onDragStart(event, installation, {
                        day: toISODate(day),
                        teamId: null,
                      })}
                      currentDate={day}
                      currentTeamId={null}
                      specialTruckName={specialTruckName}
                      showAddress={showAddresses}
                      showWh={showWh}
                      highlighted={highlightedInstallationId === inst.id}
                    />
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
      {editingTeamComment && (
        <div className="modal-overlay" style={{ zIndex: 12000 }} onClick={() => !teamCommentBusy && setEditingTeamComment(null)}>
          <div className="modal-box team-comment-modal" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h3>Komentarz — {editingTeamComment.teamName}</h3>
              <button className="modal-close" onClick={() => setEditingTeamComment(null)} disabled={teamCommentBusy}>✕</button>
            </div>
            <div className="modal-body">
              <label className="team-comment-label">
                Data: <strong>{editingTeamComment.date}</strong>
                <textarea
                  value={editingTeamComment.content}
                  onChange={(event) => setEditingTeamComment(current => ({ ...current, content: event.target.value }))}
                  maxLength={2000}
                  rows={6}
                  autoFocus
                  placeholder="Wpisz komentarz dla ekipy na ten dzień…"
                />
              </label>
              <div className="team-comment-copy-controls">
                <button
                  type="button"
                  className="btn-secondary team-comment-calendar-toggle"
                  aria-expanded={editingTeamComment.calendarExpanded}
                  onClick={() => setEditingTeamComment(current => ({
                    ...current,
                    calendarExpanded: !current.calendarExpanded,
                  }))}
                  disabled={teamCommentBusy || editingTeamComment.visibleInSettlement}
                >
                  {editingTeamComment.calendarExpanded ? 'Zwiń kalendarz' : 'Rozwiń kalendarz i wybierz dni'}
                </button>
                <span>
                  Wybrano {editingTeamComment.selectedDates.length}
                  {editingTeamComment.selectedDates.length === 1 ? ' dzień' : ' dni'}
                </span>
              </div>
              {editingTeamComment.calendarExpanded && !editingTeamComment.visibleInSettlement && renderTeamCommentCalendar()}
              <fieldset className="team-comment-team-picker" disabled={teamCommentBusy || editingTeamComment.visibleInSettlement}>
                <legend>Duplikuj komentarz dla ekip</legend>
                <div>
                  {teams.map((team) => {
                    const isSource = team.id === editingTeamComment.teamId;
                    return (
                      <label key={team.id}>
                        <input
                          type="checkbox"
                          checked={editingTeamComment.selectedTeamIds.includes(team.id)}
                          onChange={() => toggleTeamCommentTeam(team.id)}
                          disabled={isSource || teamCommentBusy || editingTeamComment.visibleInSettlement}
                        />
                        <span>{team.name}{isSource ? ' (pierwotna)' : ''}</span>
                      </label>
                    );
                  })}
                </div>
              </fieldset>
              <label className="team-comment-settlement-option">
                <input
                  type="checkbox"
                  checked={editingTeamComment.visibleInSettlement}
                  onChange={(event) => setEditingTeamComment(current => ({
                    ...current,
                    visibleInSettlement: event.target.checked,
                    conversionPromptOpen: false,
                    deletionPromptOpen: false,
                    ...(event.target.checked ? {
                      selectedDates: [current.date],
                      selectedTeamIds: [current.teamId],
                      calendarExpanded: false,
                    } : {}),
                  }))}
                  disabled={teamCommentBusy}
                />
                <span>
                  <strong>Zamień w notatkę widoczną w rozliczeniu</strong>
                  <small>Po zapisaniu komentarz stanie się notatką kalendarzową przypisaną do tej ekipy i daty.</small>
                </span>
              </label>
              {editingTeamComment.conversionPromptOpen && (
                <div className="team-comment-conversion-prompt" role="dialog" aria-label="Zakres zamiany komentarza">
                  <div className="team-comment-conversion-prompt__copy">
                    <span className="team-comment-conversion-prompt__icon" aria-hidden="true">!</span>
                    <div>
                      <strong>Komentarz występuje {editingTeamComment.duplicateCount} razy</strong>
                      <p>Wybierz, czy zamienić w notatkę tylko wybrany wpis, czy wszystkie jego duplikaty.</p>
                    </div>
                  </div>
                  <div className="team-comment-conversion-prompt__actions">
                    <button
                      type="button"
                      className="btn-secondary team-comment-conversion-prompt__cancel"
                      onClick={() => setEditingTeamComment(current => ({ ...current, conversionPromptOpen: false }))}
                      disabled={teamCommentBusy}
                    >
                      Anuluj
                    </button>
                    <button
                      type="button"
                      className="btn-secondary team-comment-conversion-prompt__single"
                      onClick={() => saveTeamComment('single')}
                      disabled={teamCommentBusy}
                    >
                      Tylko ten wpis
                    </button>
                    <button
                      type="button"
                      className="btn-primary team-comment-conversion-prompt__all"
                      onClick={() => saveTeamComment('group')}
                      disabled={teamCommentBusy}
                    >
                      Zamień wszystkie ({editingTeamComment.duplicateCount})
                    </button>
                  </div>
                </div>
              )}
              {editingTeamComment.deletionPromptOpen && (
                <div className="team-comment-conversion-prompt team-comment-deletion-prompt" role="dialog" aria-label="Zakres usuwania komentarza">
                  <div className="team-comment-conversion-prompt__copy">
                    <span className="team-comment-conversion-prompt__icon" aria-hidden="true">!</span>
                    <div>
                      <strong>Komentarz występuje {editingTeamComment.duplicateCount} razy</strong>
                      <p>Wybierz, czy usunąć tylko wybrany wpis, czy wszystkie jego duplikaty.</p>
                    </div>
                  </div>
                  <div className="team-comment-conversion-prompt__actions">
                    <button
                      type="button"
                      className="btn-secondary team-comment-conversion-prompt__cancel"
                      onClick={() => setEditingTeamComment(current => ({ ...current, deletionPromptOpen: false }))}
                      disabled={teamCommentBusy}
                    >
                      Anuluj
                    </button>
                    <button
                      type="button"
                      className="btn-secondary team-comment-conversion-prompt__single"
                      onClick={() => deleteTeamComment('single')}
                      disabled={teamCommentBusy}
                    >
                      Tylko ten wpis
                    </button>
                    <button
                      type="button"
                      className="btn-danger team-comment-deletion-prompt__all"
                      onClick={() => deleteTeamComment('group')}
                      disabled={teamCommentBusy}
                    >
                      Usuń wszystkie ({editingTeamComment.duplicateCount})
                    </button>
                  </div>
                </div>
              )}
              {teamCommentError && <div className="modal-error">{teamCommentError}</div>}
            </div>
            <div className="modal-footer">
              {editingTeamComment.exists && (
                <button type="button" className="btn-danger" onClick={() => deleteTeamComment()} disabled={teamCommentBusy}>Usuń komentarz</button>
              )}
              <button type="button" className="btn-secondary" onClick={() => setEditingTeamComment(null)} disabled={teamCommentBusy}>Anuluj</button>
              <button type="button" className="btn-primary" onClick={() => saveTeamComment()} disabled={teamCommentBusy || !editingTeamComment.content.trim()}>
                {teamCommentBusy
                  ? 'Zapisywanie…'
                  : editingTeamComment.visibleInSettlement
                    ? 'Zamień w notatkę'
                    : 'Zapisz komentarz'}
              </button>
            </div>
          </div>
        </div>
      )}
      {assignmentMenu && (
        <div
          className="assignment-context-menu"
          onClick={(event) => event.stopPropagation()}
          onContextMenu={(event) => event.preventDefault()}
          style={{
            left: Math.min(assignmentMenu.x, window.innerWidth - 175),
            top: Math.min(assignmentMenu.y, window.innerHeight - 280),
            maxHeight: '270px',
            overflowY: assignmentMenuMode ? 'auto' : 'visible',
          }}
        >
          <div className="assignment-context-menu-title">
            {isCalendarNote(assignmentMenu.installation)
              ? assignmentMenu.installation.notes || 'Notatka'
              : assignmentMenu.installation.order_number} · {assignmentMenu.day}
          </div>
          {assignmentMenuError && (
            <div style={{ margin: '4px 0 8px', padding: '8px', borderRadius: '6px', background: '#fee2e2', color: '#b91c1c', fontSize: '12px' }}>
              {assignmentMenuError}
            </div>
          )}
          {!assignmentMenuMode ? (
            <div className="assignment-context-menu-actions">
              {assignmentMenu.currentTeamId !== null && (
                <button className="btn-secondary assignment-context-menu-button" onClick={() => setAssignmentMenuMode('move')}>
                  Przenieś do innej ekipy
                </button>
              )}
              <button className="btn-secondary assignment-context-menu-button" onClick={() => setAssignmentMenuMode('add')}>
                {assignmentMenu.currentTeamId === null ? 'Przypisz do ekipy' : 'Dodaj kolejną ekipę'}
              </button>
              {canAddAdditionalWork(assignmentMenu.installation, installations) && (
                <button
                  className="btn-secondary assignment-context-menu-button"
                  onClick={() => {
                    onAddAdditionalWork?.(assignmentMenu.installation);
                    setAssignmentMenu(null);
                  }}
                >
                  Dodaj dodatkową pracę
                </button>
              )}
              {quickStatusOptions.length > 0 && (
                <div
                  className="assignment-status-menu-trigger"
                  onMouseEnter={() => setStatusMenuOpen(true)}
                >
                  <button
                    className="btn-secondary assignment-context-menu-button assignment-status-menu-button"
                    onClick={() => setStatusMenuOpen((open) => !open)}
                    aria-expanded={statusMenuOpen}
                    type="button"
                  >
                    <span>Zmiana statusu</span>
                    <span aria-hidden="true">›</span>
                  </button>
                  {statusMenuOpen && (
                    <div
                      className="assignment-status-submenu"
                      style={{
                        ...(assignmentMenu.x + 342 <= window.innerWidth
                          ? { left: 'calc(100% + 6px)' }
                          : { right: 'calc(100% + 6px)' }),
                      }}
                    >
                      {quickStatusOptions.map((targetStatus) => (
                        <button
                          key={targetStatus}
                          className="btn-secondary assignment-context-menu-button"
                          disabled={assignmentMenuBusy}
                          onClick={() => quickChangeStatus(targetStatus)}
                        >
                          {STATUS_COLORS[targetStatus].label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <>
              <button className="btn-secondary" style={{ width: '100%', marginBottom: '6px' }} onClick={() => setAssignmentMenuMode(null)}>
                ← Wróć
              </button>
              {teams
                .filter((team) => team.id !== assignmentMenu.currentTeamId)
                .filter((team) => (
                  isDeliveryOnlyInstallation(assignmentMenu.installation)
                    ? !team.is_special_dad
                    : !team.is_special_truck
                ))
                .map((team) => (
                  <button
                    key={team.id}
                    className="btn-secondary"
                    disabled={assignmentMenuBusy}
                    style={{ width: '100%', marginBottom: '4px' }}
                    onClick={() => changeTeamAssignment(team.id, assignmentMenuMode)}
                  >
                    {team.name}
                  </button>
                ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
//  Modals
// ──────────────────────────────────────────────────────────────
export function AdditionalWorkModal({ installation, teams, onClose, onSaved }) {
  const today = toISODate(new Date());
  const [workStartDate, setWorkStartDate] = useState(today);
  const [workEndDate, setWorkEndDate] = useState(today);
  const [teamId, setTeamId] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(event) {
    event.preventDefault();
    if (!workStartDate || !workEndDate) {
      setError('Wybierz zakres dni dodatkowej pracy.');
      return;
    }
    if (workStartDate < today || workEndDate < today) {
      setError('Dni dodatkowej pracy nie mogą być wcześniejsze niż dzisiaj.');
      return;
    }
    if (workEndDate < workStartDate) {
      setError('Ostatni dzień nie może być wcześniejszy niż pierwszy dzień.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      // Jedna transakcja po stronie serwera: zlecenie -DP, daty i ekipa na każdy dzień.
      // Konflikt (np. ekipa na urlopie) nie zostawia pustego zlecenia -DP.
      await installationService.createRelatedInstallation(installation.id, {
        kind: 'additional_work',
        planned_start_date: workStartDate,
        planned_end_date: workEndDate,
        team_id: teamId ? Number(teamId) : null,
        notes: notes || null,
      });

      await onSaved?.();
      onClose();
    } catch (requestError) {
      setError(apiErrorMessage(requestError, 'Błąd dodawania dodatkowej pracy'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="modal-overlay"
      style={{ zIndex: 12000 }}
      onClick={() => !saving && onClose()}
    >
      <form
        className="modal-box schedule-order-modal"
        onClick={(event) => event.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="modal-header">
          <div>
            <h3>Dodaj dodatkową pracę</h3>
            <div style={{ color: '#64748b', fontSize: '13px', marginTop: '3px' }}>
              {installation.order_number}
            </div>
          </div>
          <button type="button" className="modal-close" onClick={onClose} disabled={saving}>✕</button>
        </div>
        <div className="modal-body schedule-order-fields">
          <label>
            Pierwszy dzień dodatkowej pracy
            <input
              type="date"
              value={workStartDate}
              min={today}
              onChange={(event) => {
                const nextStartDate = event.target.value;
                setWorkStartDate(nextStartDate);
                if (!workEndDate || workEndDate < nextStartDate) {
                  setWorkEndDate(nextStartDate);
                }
              }}
              required
              autoFocus
            />
          </label>
          <label>
            Ostatni dzień dodatkowej pracy
            <input
              type="date"
              value={workEndDate}
              min={workStartDate || today}
              onChange={(event) => setWorkEndDate(event.target.value)}
              required
            />
          </label>
          <label>
            Ekipa (opcjonalnie)
            <select value={teamId} onChange={(event) => setTeamId(event.target.value)}>
              <option value="">Przypisz później</option>
              {teams.filter((team) => !team.is_special_truck).map((team) => (
                <option key={team.id} value={team.id}>{team.name}</option>
              ))}
            </select>
          </label>
          <label>
            Notatka
            <textarea
              rows={3}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Widoczna tylko w dodatkowym zleceniu"
            />
          </label>
          {error && <div className="modal-error" role="alert">{error}</div>}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>Anuluj</button>
          <button type="submit" className="btn-primary" disabled={saving || !workStartDate || !workEndDate}>
            {saving ? 'Dodawanie…' : 'Dodaj'}
          </button>
        </div>
      </form>
    </div>
  );
}

export function InstallationModal({ installation, installations = [], onClose, onSave, installers, teams, vacations = [], canViewSettlement = false, canEditSettlement = canViewSettlement }) {
  const isNew = !installation || !installation.id;
  const [form, setForm] = useState({
    order_number: '',
    contractor: '',
    state: '',
    address: '',
    notes: '',
    calendar_status: 'planned',
    planned_start_date: '',
    planned_end_date: '',
    rusztowanie: false,
    drabina: false,
    pajaczek: false,
    delivery_team_auto_disabled: false,
    manipulator: false,
    agregat: false,
    dodatkowy_bus: false,
    tir: false,
    overhead_percentage: 5.0,
    distribution_percentage: 25.0,
    ...( installation || {} ),
    delivery_team_excluded_dates: installation?.delivery_team_excluded_dates
      ?? (installation?.delivery_team_auto_disabled ? installationDates(installation) : []),
  });
  const [currentInstallation, setCurrentInstallation] = useState(installation);
  const [settlement, setSettlement] = useState(null);
  const [loadingSettlement, setLoadingSettlement] = useState(false);
  const [assignTeamId, setAssignTeamId] = useState('');
  const [assignInstallerId, setAssignInstallerId] = useState('');
  const [selectedDay, setSelectedDay] = useState(''); // Wybrany dzień do przypisania
  const [vacationWarnings, setVacationWarnings] = useState([]);
  const [generalWarnings, setGeneralWarnings] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showAssignmentQuestion, setShowAssignmentQuestion] = useState(false);
  const [assignmentAction, setAssignmentAction] = useState('copy');
  const [addMemberDay, setAddMemberDay] = useState(null);
  const [addMemberInstallerId, setAddMemberInstallerId] = useState('');
  const [addMemberTeamId, setAddMemberTeamId] = useState('');
  const [showPdfDayPicker, setShowPdfDayPicker] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState(false);
  const [generalInfoExpanded, setGeneralInfoExpanded] = useState(true);
  const [assignmentsExpanded, setAssignmentsExpanded] = useState(false);
  const [settlementExpanded, setSettlementExpanded] = useState(false);
  const [showDaysCalendar, setShowDaysCalendar] = useState(false);
  const [daysCalendarMode, setDaysCalendarMode] = useState('days');
  const [rangeStart, setRangeStart] = useState(null);
  const [daysCalendarView, setDaysCalendarView] = useState(() => {
    const initialDay = installation?.planned_start_date;
    return initialDay
      ? { year: Number(initialDay.slice(0, 4)), month: Number(initialDay.slice(5, 7)) - 1 }
      : { year: new Date().getFullYear(), month: new Date().getMonth() };
  });

  const currentOrderNumber = form.order_number || installation?.order_number || '';
  const isAdditionalWork = form.calendar_status === 'additional_work' || currentOrderNumber.endsWith('-DP');
  const originalOrderNumber = isAdditionalWork
    ? currentOrderNumber.replace(/-DP$/, '')
    : currentOrderNumber;
  const relatedAdditionalWork = isAdditionalWork
    ? currentInstallation
    : installations.find((inst) => inst.order_number === `${originalOrderNumber}-DP`);
  const assignments = currentInstallation?.assignments || [];
  const assignmentSettlementKey = assignments
    .map(a => `${a.id}:${a.team_id ?? ''}:${a.installer_id ?? ''}:${a.assigned_date ?? ''}`)
    .sort()
    .join('|');
  const isPremiumCopy = currentOrderNumber.endsWith('-PR');
  const usesPzlFlatPricing = settlement?.is_pzl
    && settlement.pzl_settlement_mode !== 'usl';
  const isDadInstallation = isDadOrder(currentOrderNumber);
  const dadTeamName = findDadTeam(teams)?.name || 'Ekipa DAD';
  const isDeliveryOnly = isDeliveryOnlyInstallation(currentInstallation || installation || form);
  const hasDelivery = hasDeliveryToClient(currentInstallation || installation || form);
  const deliveryTeamName = findTirTeam(teams)?.name || 'KAWALEC';
  const premiumUslItem = isPremiumCopy
    ? (currentInstallation?.items || []).find((item) => (item.product_type || '').trim().toUpperCase() === 'USL')
    : null;
  const installationScope = [...new Set(
    (currentInstallation?.items || installation?.items || [])
      .map((item) => (item.scope || '').trim())
      .filter(Boolean)
  )].join(', ');
  const totalPerimeter = getTotalPerimeter(currentInstallation);
  const hasHighPerimeter = totalPerimeter > 80;
  const pajaczekConflictWarnings = form.pajaczek
    ? installationDates(form).flatMap((day) => {
        const conflicts = installations.filter((inst) =>
          inst.id !== currentInstallation?.id &&
          inst.pajaczek &&
          installationIncludesDay(inst, day)
        );
        if (conflicts.length === 0) return [];
        const orders = conflicts.map((inst) => inst.order_number || `ID ${inst.id}`).join(', ');
        return [`Pajączek jest już przypisany w dniu ${day} do zlecenia: ${orders}.`];
      })
    : [];
  
  // Generuj listę dni między planned_start_date a planned_end_date
  const installationDays = installationDates(currentInstallation);
  
  // Grupuj przypisania po dniach
  const assignmentsByDay = {};
  installationDays.forEach(day => {
    assignmentsByDay[day] = assignments.filter(a => a.assigned_date === day);
  });
  
  // Sprawdź czy w danym dniu jest ktoś z Kat.B
  function hasKatBForDay(day) {
    const dayAssignments = assignmentsByDay[day] || [];
    const teamIds = dayAssignments.filter(a => a.team_id).map(a => a.team_id);
    if (teamIds.length === 0) return true; // Brak przypisań = brak ostrzeżenia
    
    return teamIds.some(teamId => {
      const team = teams.find(t => t.id === teamId);
      return teamHasKatB(team);
    });
  }
  
  const needsKatBWarning = !isNew && installationDays.some(day => {
    const dayAssignments = assignmentsByDay[day] || [];
    return dayAssignments.length > 0 && !hasKatBForDay(day);
  });

  function getApprovedVacation(installerId, dateStr) {
    return vacations.find(v =>
      v.installer_id === installerId &&
      v.approved === true &&
      v.start_date <= dateStr &&
      v.end_date >= dateStr
    );
  }

  // Zaplanowany urlop nie blokuje planowania; blokuje dopiero zatwierdzony.
  function isOnVacation(installerId, dateStr) {
    return !!getApprovedVacation(installerId, dateStr);
  }

  // Zwróć listę dostępnych montażystów dla danego dnia
  function getAvailableInstallersForDay(day) {
    const dayAssignments = assignmentsByDay[day] || [];
    const assignedIds = new Set(
      dayAssignments.flatMap(a => {
        if (a.installer_id) return [a.installer_id];
        if (a.team_id) {
          const team = teams.find(t => t.id === a.team_id);
          return team ? team.members.map(m => m.id) : [];
        }
        return [];
      })
    );
    return installers.filter(i => !assignedIds.has(i.id));
  }
  
  // Ustaw domyślnie pierwszy dzień jako wybrany
  if (!selectedDay && installationDays.length > 0) {
    setSelectedDay(installationDays[0]);
  }
  
  // Synchronizuj calendar_status w formularzu gdy otwieramy nowe zlecenie
  useEffect(() => {
    if (currentInstallation?.calendar_status) {
      setForm(prev => ({ ...prev, calendar_status: currentInstallation.calendar_status }));
    }
  }, [currentInstallation?.id, currentInstallation?.calendar_status]);

  // Load the live settlement and any additions preserved in saved sessions.
  useEffect(() => {
    async function loadSettlement() {
      if (!canViewSettlement || !currentInstallation?.id) {
        setSettlement(null);
        return;
      }
      setLoadingSettlement(true);
      try {
        const data = await installationService.getInstallationSettlement(
          currentInstallation.id,
          form.overhead_percentage,
          form.distribution_percentage
        );
        setSettlement(data);
      } catch (e) {
        console.error('Error loading settlement:', e);
        setSettlement(null);
      } finally {
        setLoadingSettlement(false);
      }
    }
    loadSettlement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewSettlement, currentInstallation?.id, assignmentSettlementKey, form.overhead_percentage, form.distribution_percentage]);

  async function handleSave() {
    if (!form.order_number) { setError('Numer zlecenia jest wymagany'); return; }
    
    // Sprawdź czy daty się zmieniły i są przypisania
    if (!isNew && assignments.length > 0 && !showAssignmentQuestion) {
      const oldStart = currentInstallation?.planned_start_date || null;
      const oldEnd = currentInstallation?.planned_end_date || null;
      const newStart = form.planned_start_date || null;
      const newEnd = form.planned_end_date || null;
      
      if (oldStart !== newStart || oldEnd !== newEnd || JSON.stringify(installationDates(form)) !== JSON.stringify(installationDates(currentInstallation))) {
        // Daty się zmieniły - pokaż pytanie
        setShowAssignmentQuestion(true);
        return;
      }
    }
    
    setSaving(true);
    try {
      await onSave(form, assignmentAction, assignments);
      onClose();
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : (detail ? JSON.stringify(detail) : 'Błąd zapisu'));
    } finally {
      setSaving(false);
    }
  }

  function selectInstallationDays(selectedDates) {
    const days = [...new Set(selectedDates)].sort();
    if (days.length && (Date.parse(days[days.length - 1]) - Date.parse(days[0])) > 365 * 86400000) {
      setError('Wybierz dni montażu w zakresie do 365 dni.');
      return;
    }
    setError('');
    setForm(prev => ({
      ...prev,
      planned_start_date: days[0] || '',
      planned_end_date: days[days.length - 1] || '',
      planned_dates: days.length ? days : null,
    }));
  }

  function handleDaysCalendarClick(day) {
    if (daysCalendarMode === 'range') {
      if (!rangeStart) {
        setRangeStart(day);
        selectInstallationDays([day]);
      } else {
        const [start, end] = [rangeStart, day].sort();
        const days = installationDates({ planned_start_date: start, planned_end_date: end });
        if (days.length && days[days.length - 1] === end) {
          selectInstallationDays(days);
          setRangeStart(null);
        } else {
          setError('Wybierz dni montażu w zakresie do 365 dni.');
        }
      }
      return;
    }
    const selected = installationDates(form);
    selectInstallationDays(selected.includes(day)
      ? selected.filter(value => value !== day)
      : [...selected, day]);
  }

  function changeDaysCalendarMonth(offset) {
    setDaysCalendarView(current => {
      const next = new Date(current.year, current.month + offset, 1);
      return { year: next.getFullYear(), month: next.getMonth() };
    });
  }

  function renderDaysCalendar() {
    const firstDay = new Date(daysCalendarView.year, daysCalendarView.month, 1);
    const lastDay = new Date(daysCalendarView.year, daysCalendarView.month + 1, 0);
    const days = [];
    let cursor = startOfWeek(firstDay);
    while (cursor <= lastDay || days.length % 7 !== 0) {
      days.push(new Date(cursor));
      cursor = addDays(cursor, 1);
      if (days.length >= 42) break;
    }
    const selected = new Set(installationDates(form));

    return (
      <div className="split-calendar installation-days-calendar">
        <div className="split-calendar-header">
          <button type="button" className="btn-nav" onClick={() => changeDaysCalendarMonth(-1)} aria-label="Poprzedni miesiąc">‹</button>
          <span className="split-calendar-month">{POLISH_MONTHS[daysCalendarView.month]} {daysCalendarView.year}</span>
          <button type="button" className="btn-nav" onClick={() => changeDaysCalendarMonth(1)} aria-label="Następny miesiąc">›</button>
        </div>
        <div className="vac-cal-grid">
          {POLISH_DAYS_SHORT.map(name => <div key={name} className="vac-cal-head">{name}</div>)}
          {days.map(day => {
            const isoDay = toISODate(day);
            const isSelected = selected.has(isoDay);
            return (
              <button
                key={isoDay}
                type="button"
                data-date={isoDay}
                className={`vac-cal-day ${day.getMonth() !== daysCalendarView.month ? 'out-month' : ''} ${isSelected ? 'selected-date' : ''}`}
                aria-label={day.toLocaleDateString('pl-PL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                aria-pressed={isSelected}
                onClick={() => handleDaysCalendarClick(isoDay)}
              >
                {day.getDate()}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  async function refreshCurrentInstallation() {
    const updatedInstallation = await installationService.getInstallation(currentInstallation.id);
    setCurrentInstallation(updatedInstallation);
    setForm(prev => ({
      ...prev,
      ...updatedInstallation,
      manual_settlement_amounts: updatedInstallation.manual_settlement_amounts || {},
    }));
    return updatedInstallation;
  }

  async function savePremiumUslValue(rawValue) {
    if (!canEditSettlement || !currentInstallation?.id) return;
    const value = rawValue !== '' ? parseFloat(String(rawValue).replace(',', '.')) : 0;
    if (Number.isNaN(value) || value < 0) return;
    setSaving(true);
    try {
      const payload = {
        name: 'Prace premium',
        scope: 'MONTAŻ PREMIUM PLUS',
        quantity: 1,
        product_type: 'USL',
        net_price_pln: value,
      };
      if (premiumUslItem?.id) {
        await installationService.updateInstallationItem(premiumUslItem.id, payload);
      } else {
        await installationService.createInstallationItem(currentInstallation.id, payload);
      }
      await refreshCurrentInstallation();
    } catch (e) {
      setError(e.response?.data?.detail || 'Błąd zapisu USL');
    } finally {
      setSaving(false);
    }
  }

  async function saveSettlementSettings(nextValues) {
    if (!canEditSettlement || !currentInstallation?.id) return;
    setSaving(true);
    try {
      await installationService.updateInstallation(currentInstallation.id, nextValues);
      await refreshCurrentInstallation();
    } catch (e) {
      setError(e.response?.data?.detail || 'Błąd zapisu rozliczenia');
    } finally {
      setSaving(false);
    }
  }

  function updateManualSettlementAmount(installerId, value) {
    const manualAmounts = {
      ...(form.manual_settlement_amounts || {}),
      [String(installerId)]: value,
    };
    setForm(prev => ({ ...prev, manual_settlement_amounts: manualAmounts }));
  }

  function saveManualSettlementAmounts() {
    const cleaned = {};
    Object.entries(form.manual_settlement_amounts || {}).forEach(([installerId, value]) => {
      const amount = value !== '' ? parseFloat(String(value).replace(',', '.')) : 0;
      cleaned[installerId] = Number.isNaN(amount) ? 0 : amount;
    });
    saveSettlementSettings({
      settlement_mode: 'manual',
      manual_settlement_amounts: cleaned,
    });
  }

  async function handleAddAssignment() {
    if (!assignTeamId && !assignInstallerId) return;
    if (!selectedDay) {
      setError('Wybierz dzień do przypisania');
      return;
    }
    if (
      assignTeamId
      && !confirmDadTeamChange(currentInstallation, Number(assignTeamId), teams)
    ) return;
    setSaving(true);
    setVacationWarnings([]);
    setGeneralWarnings([]);
    setError('');
    try {
      const result = await installationService.createAssignment(currentInstallation.id, {
        team_id: assignTeamId ? Number(assignTeamId) : null,
        installer_id: assignInstallerId ? Number(assignInstallerId) : null,
        assigned_date: selectedDay,
      });
      
      // Odśwież dane instalacji po przypisaniu
      const updatedInstallation = await installationService.getInstallation(currentInstallation.id);
      setCurrentInstallation(updatedInstallation);
      
      // Wyczyść wybory
      setAssignTeamId('');
      setAssignInstallerId('');
      // Zostaw wybrany dzień
      
      // Wyświetl ostrzeżenia jeśli są
      if (result.vacation_warnings && result.vacation_warnings.length > 0) {
        setVacationWarnings(result.vacation_warnings);
      }
      if (result.warnings && result.warnings.length > 0) {
        setGeneralWarnings(result.warnings);
      }
      
      // NIE zamykaj modala - pozwól użytkownikowi zobaczyć zaktualizowane dane i ostrzeżenia
    } catch (e) {
      setError(e.response?.data?.detail || 'Błąd przypisania');
    } finally {
      setSaving(false);
    }
  }

  async function handleRemoveAssignment(assignmentId) {
    setSaving(true);
    setError('');
    try {
      await installationService.deleteAssignment(assignmentId);
      // Odśwież dane instalacji po usunięciu przypisania
      const updatedInstallation = await installationService.getInstallation(currentInstallation.id);
      setCurrentInstallation(updatedInstallation);
    } catch (e) {
      setError(e.response?.data?.detail || 'Błąd usunięcia przypisania');
    } finally {
      setSaving(false);
    }
  }

  async function handleConfirmAddMember() {
    if (!addMemberDay || !addMemberInstallerId) return;
    setSaving(true);
    setError('');
    try {
      await installationService.createAssignment(currentInstallation.id, {
        team_id: addMemberTeamId ? Number(addMemberTeamId) : null,
        installer_id: Number(addMemberInstallerId),
        assigned_date: addMemberDay,
      });
      const updatedInstallation = await installationService.getInstallation(currentInstallation.id);
      setCurrentInstallation(updatedInstallation);
      setAddMemberDay(null);
      setAddMemberInstallerId('');
      setAddMemberTeamId('');
    } catch (e) {
      setError(e.response?.data?.detail || 'Błąd dodania osoby');
    } finally {
      setSaving(false);
    }
  }

  async function handleMoveIndividualToTeam(assignmentId, teamId) {
    if (!assignmentId || !teamId) return;
    setSaving(true);
    setError('');
    try {
      await installationService.moveAssignmentToTeam(assignmentId, Number(teamId));
      const updatedInstallation = await installationService.getInstallation(currentInstallation.id);
      setCurrentInstallation(updatedInstallation);
    } catch (e) {
      setError(e.response?.data?.detail || 'Błąd przenoszenia osoby do ekipy');
    } finally {
      setSaving(false);
    }
  }

  async function handleRemoveTeamMember(teamAssignment, memberToRemoveId) {
    setSaving(true);
    setError('');
    try {
      const team = teams.find(t => t.id === teamAssignment.team_id);
      const remainingMembers = team ? team.members.filter(m =>
        m.id !== memberToRemoveId && !isOnVacation(m.id, teamAssignment.assigned_date)
      ) : [];

      // 1. Usuń przypisanie całej ekipy
      await installationService.deleteAssignment(teamAssignment.id);

      // 2. Stwórz przypisania z zachowanym team_id dla pozostałych członków
      for (const member of remainingMembers) {
        await installationService.createAssignment(currentInstallation.id, {
          team_id: teamAssignment.team_id,
          installer_id: member.id,
          assigned_date: teamAssignment.assigned_date,
        });
      }

      // 3. Odśwież instalację
      const updatedInstallation = await installationService.getInstallation(currentInstallation.id);
      setCurrentInstallation(updatedInstallation);
    } catch (e) {
      setError(e.response?.data?.detail || 'Błąd usunięcia członka ekipy');
    } finally {
      setSaving(false);
    }
  }

  async function handleRemoveTeamGroup(teamId, day) {
    setSaving(true);
    setError('');
    try {
      const toDelete = (assignmentsByDay[day] || []).filter(a => a.team_id === teamId);
      for (const a of toDelete) {
        await installationService.deleteAssignment(a.id);
      }
      const updatedInstallation = await installationService.getInstallation(currentInstallation.id);
      setCurrentInstallation(updatedInstallation);
    } catch (e) {
      setError(e.response?.data?.detail || 'Błąd usunięcia ekipy');
    } finally {
      setSaving(false);
    }
  }

  async function handleUnschedule() {
    setSaving(true);
    try {
      const premiumCalendarCopy = findPremiumCalendarCopy(currentInstallation || installation, installations);

      // Usuń wszystkie przypisania ekip
      if (currentInstallation?.assignments && currentInstallation.assignments.length > 0) {
        for (const assignment of currentInstallation.assignments) {
          await installationService.deleteAssignment(assignment.id);
        }
      }

      if (premiumCalendarCopy) {
        await installationService.deleteInstallation(premiumCalendarCopy.id);
      }
      
      // Przenieś do oczekujących (wyczyść daty)
      await installationService.updateInstallationCalendar(installation.id, {
        planned_start_date: null,
        planned_end_date: null,
      });
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'Błąd przenoszenia');
    } finally {
      setSaving(false);
    }
  }

  function handlePdfClick() {
    // Jeśli więcej niż 1 dzień, pokaż wybór dnia
    if (installationDays.length > 1) {
      setShowPdfDayPicker(true);
    } else {
      // Pojedynczy dzień lub brak dat - drukuj wszystko
      printInstallation(currentInstallation, teams, installers, null);
    }
  }

  function handlePdfDaySelected(day) {
    setShowPdfDayPicker(false);
    printInstallation(currentInstallation, teams, installers, day);
  }

  async function handleCopyOrderNumber() {
    const orderNumber = form.order_number || installation?.order_number || '';
    if (!orderNumber) return;

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(orderNumber);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = orderNumber;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopyFeedback(true);
      window.setTimeout(() => setCopyFeedback(false), 1500);
    } catch (e) {
      console.error('Copy order number failed', e);
    }
  }

  async function handleDeleteAdditionalWork() {
    if (!relatedAdditionalWork?.id) return;
    if (!window.confirm(`Usunąć dodatkową pracę ${relatedAdditionalWork.order_number}?`)) return;

    setSaving(true);
    setError('');
    try {
      await installationService.deleteInstallation(relatedAdditionalWork.id);
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'Błąd usuwania dodatkowej pracy');
    } finally {
      setSaving(false);
    }
  }

  function renderSettlementParameters() {
    if (isNew || isPremiumCopy) return null;

    return (
      <section className="settlement-parameters">
        <h4>Parametry rozliczenia</h4>
        <div className="settlement-parameters-grid">
          <label>
            <span>Narzut %</span>
            <input
              type="number"
              min="0"
              max="100"
              step="1"
              value={form.overhead_percentage ?? 5}
              onChange={(e) => setForm({ ...form, overhead_percentage: parseFloat(e.target.value) || 0 })}
              disabled={!canEditSettlement}
            />
          </label>
          <label>
            <span>Procent do podziału %</span>
            <input
              type="number"
              min="0"
              max="100"
              step="1"
              value={form.distribution_percentage ?? 25}
              onChange={(e) => setForm({ ...form, distribution_percentage: parseFloat(e.target.value) || 0 })}
              disabled={!canEditSettlement}
            />
          </label>
        </div>
        <p className="settlement-parameters-formula">
          Formuła: Y = SUMA_USL × narzut%, X = SUMA_USL - Y, Suma do podziału = X × procent%
        </p>
      </section>
    );
  }

  return (
    <>
      <aside
        className="modal-box installation-details-drawer"
        aria-label={isNew ? 'Nowy montaż' : `Szczegóły montażu ${installation.order_number}`}
      >
        <div className="modal-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, flex: 1 }}>
            <h3 style={{ margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {isNew ? 'Nowy montaż' : `Montaż: ${installation.order_number}`}
            </h3>
            {!isNew && (form.order_number || installation?.order_number) && (
              <button
                type="button"
                onClick={handleCopyOrderNumber}
                title="Skopiuj numer zlecenia"
                style={{
                  border: '1px solid #cbd5e1',
                  background: copyFeedback ? '#dcfce7' : '#f8fafc',
                  color: copyFeedback ? '#166534' : '#475569',
                  borderRadius: '999px',
                  padding: '2px 8px',
                  fontSize: '11px',
                  cursor: 'pointer',
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                {copyFeedback ? 'Skopiowano' : 'Kopiuj nr zlecenia'}
              </button>
            )}
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        {error && <div className="modal-error">{error}</div>}
        
        {hasHighPerimeter && (
          <div className="modal-warning-banner">
            <span className="warning-icon">⚠️</span>
            <div className="warning-content">
              <strong>Wysoka suma obwodów</strong>
              <p>Suma obwodów wynosi {totalPerimeter.toFixed(1)} m (przekracza 80 m). Zalecane jest rozplanowanie montażu na więcej dni.</p>
            </div>
          </div>
        )}
        
        {needsKatBWarning && (
          <div className="modal-warning-banner">
            <span className="warning-icon">⚠️</span>
            <div className="warning-content">
              <strong>Brak kierowcy z Kat. B</strong>
              <p>W przypisanych ekipach nie ma nikogo z kategorią B. Upewnij się, że ekipa ma wyznaczonego kierowcę.</p>
            </div>
          </div>
        )}

        <div className="modal-body">
          {showAssignmentQuestion ? (
            <div>
              <p style={{ marginBottom: '16px', fontSize: '14px', fontWeight: 'bold' }}>
                Zlecenie ma już przypisane ekipy, a zmieniłeś zakres dat. Co zrobić z przypisaniami?
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'start', gap: '8px', padding: '10px', border: '1.5px solid #cbd5e1', borderRadius: '8px', cursor: 'pointer', background: assignmentAction === 'keep' ? '#eff6ff' : '#fff' }}>
                  <input 
                    type="radio" 
                    name="assignmentAction" 
                    value="keep"
                    checked={assignmentAction === 'keep'}
                    onChange={(e) => setAssignmentAction(e.target.value)}
                    style={{ marginTop: '2px' }}
                  />
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '13px', color: '#1e293b' }}>Zostaw istniejące przypisania</div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>Ekipy pozostaną przypisane do swoich dni. Status zlecenia pozostanie bez zmian. Nowe dni będą bez przypisań.</div>
                  </div>
                </label>
                
                <label style={{ display: 'flex', alignItems: 'start', gap: '8px', padding: '10px', border: '1.5px solid #cbd5e1', borderRadius: '8px', cursor: 'pointer', background: assignmentAction === 'copy' ? '#eff6ff' : '#fff' }}>
                  <input 
                    type="radio" 
                    name="assignmentAction" 
                    value="copy"
                    checked={assignmentAction === 'copy'}
                    onChange={(e) => setAssignmentAction(e.target.value)}
                    style={{ marginTop: '2px' }}
                  />
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '13px', color: '#1e293b' }}>Skopiuj na wszystkie dni</div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>Przypisania zostaną skopiowane na wszystkie wybrane dni.</div>
                  </div>
                </label>
              </div>
            </div>
          ) : (
          <>
          <section className="installation-general-section">
            <button
              type="button"
              className="installation-general-toggle"
              aria-expanded={generalInfoExpanded}
              onClick={() => setGeneralInfoExpanded((expanded) => !expanded)}
            >
              <span>Informacje ogólne</span>
              {!generalInfoExpanded && (
                <span className="installation-general-summary">
                  {form.order_number}{form.contractor ? ` · ${form.contractor}` : ''}
                </span>
              )}
              <span className="installation-general-chevron" aria-hidden="true">
                {generalInfoExpanded ? '−' : '+'}
              </span>
            </button>

            {generalInfoExpanded && (
            <div className="installation-general-content">
          <div className="form-grid installation-general-grid">
            <label className="general-field general-field--order">Numer zlecenia *
              <input value={form.order_number} readOnly={!isNew}
                onChange={(e) => setForm({ ...form, order_number: e.target.value })} />
            </label>
            <label className="general-field general-field--contractor">Kontrahent
              <input value={form.contractor || ''} onChange={(e) => setForm({ ...form, contractor: e.target.value })} />
            </label>
            <label className="general-field general-field--address">Adres
              <input value={form.address || ''} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </label>
            <label className="general-field general-field--status">Status
              <select value={form.calendar_status}
                onChange={(e) => setForm({ ...form, calendar_status: e.target.value })}>
                <optgroup label="Status">
                  {REGULAR_STATUSES.map((k) => (
                    <option key={k} value={k}>{STATUS_COLORS[k].label}</option>
                  ))}
                </optgroup>
                <optgroup label="Status końcowy">
                  {FINAL_STATUSES.map((k) => (
                    <option key={k} value={k} disabled={form.calendar_status !== 'confirmed' && !FINAL_STATUSES.includes(form.calendar_status)}>
                      {STATUS_COLORS[k].label}
                    </option>
                  ))}
                </optgroup>
              </select>
            </label>
            <label className="general-field general-field--state">STAN
              <input value={form.state || ''} onChange={(e) => setForm({ ...form, state: e.target.value })} />
            </label>
            <label className="general-field general-field--scope">ZAKRES MONTAŻU
              <input value={installationScope} readOnly />
            </label>
            <label className="general-field general-field--perimeter-sum">Suma obwodów
              <input
                aria-label="Suma obwodów"
                value={`${totalPerimeter.toFixed(2)} m`}
                readOnly
              />
            </label>
            <label className="general-field general-field--date">Data od
              <input type="date" value={form.planned_start_date || ''}
                onChange={(e) => setForm({ ...form, planned_start_date: e.target.value, planned_dates: null })} />
            </label>
            <label className="general-field general-field--date">Data do
              <input type="date" value={form.planned_end_date || ''}
                onChange={(e) => setForm({ ...form, planned_end_date: e.target.value, planned_dates: null })} />
            </label>
            {(!isNew || (form.planned_start_date && form.planned_end_date)) && (
              <fieldset className="installation-days-picker installation-days-picker--expanded">
                <legend>Dni montażu</legend>
                <div className="installation-days-picker-heading">
                  <p>{isNew
                    ? 'Odznacz dni przerwy w podanym przedziale.'
                    : 'Wybierz dni w kalendarzu lub odznacz przerwy poniżej.'}</p>
                  <span className="installation-days-count" aria-live="polite">
                    Wybrano: {installationDates(form).length}
                  </span>
                </div>
                {!isNew && (
                  <>
                    <div className="installation-days-calendar-actions">
                      <button
                        type="button"
                        className="installation-days-calendar-toggle"
                        aria-expanded={showDaysCalendar}
                        onClick={() => {
                          if (!showDaysCalendar && form.planned_start_date) {
                            setDaysCalendarView({
                              year: Number(form.planned_start_date.slice(0, 4)),
                              month: Number(form.planned_start_date.slice(5, 7)) - 1,
                            });
                          }
                          setShowDaysCalendar(!showDaysCalendar);
                        }}
                      >
                        <CalendarDays size={16} aria-hidden="true" />
                        {showDaysCalendar ? 'Ukryj kalendarz' : 'Pokaż kalendarz'}
                      </button>
                      <button type="button" className="installation-days-clear" onClick={() => {
                        setRangeStart(null);
                        selectInstallationDays([]);
                      }}>Wyczyść wybór</button>
                    </div>
                    {showDaysCalendar && (
                      <div className="installation-days-calendar-panel">
                        <div className="installation-days-calendar-modes" role="group" aria-label="Sposób wybierania dni">
                          <button type="button" aria-pressed={daysCalendarMode === 'days'}
                            onClick={() => { setDaysCalendarMode('days'); setRangeStart(null); }}>Pojedyncze dni</button>
                          <button type="button" aria-pressed={daysCalendarMode === 'range'}
                            onClick={() => { setDaysCalendarMode('range'); setRangeStart(null); }}>Zakres dni</button>
                        </div>
                        <p className="installation-days-calendar-hint">
                          {daysCalendarMode === 'range'
                            ? (rangeStart ? 'Wybierz ostatni dzień zakresu.' : 'Wybierz pierwszy, a potem ostatni dzień zakresu.')
                            : 'Klikaj dni, aby je zaznaczać lub odznaczać.'}
                        </p>
                        {renderDaysCalendar()}
                      </div>
                    )}
                  </>
                )}
                {form.planned_start_date && form.planned_end_date && (
                  <div className="installation-days-list">
                    <span className="installation-days-list-label">Dni w wybranym przedziale</span>
                    <div className="installation-days-options">
                      {installationDates({ ...form, planned_dates: null }).map(day => (
                        <label key={day}>
                          <input type="checkbox" checked={installationDates(form).includes(day)}
                            onChange={event => {
                              const days = installationDates(form);
                              const selected = event.target.checked ? [...days, day].sort() : days.filter(value => value !== day);
                              if (selected.length) setForm({ ...form, planned_dates: selected });
                            }} />
                          {new Date(`${day}T12:00:00`).toLocaleDateString('pl-PL', { weekday: 'short', day: '2-digit', month: '2-digit' })}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </fieldset>
            )}
          </div>

          <label className="installation-general-notes">Notatki
            <textarea rows={3} value={form.notes || ''}
              onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </label>
            </div>
            )}
          </section>

          {/* Equipment / logistics flags */}
          <div className="equipment-flags">
            <span className="equipment-flags-label">Wyposażenie / logistyka:</span>
            {hasDelivery && !isDeliveryOnly && installationDates(form).map(day => (
              <label key={day} className="equipment-flag-item" title="Odznaczenie wyłącza dowóz wyłącznie w tym dniu">
                <input
                  type="checkbox"
                  checked={!(form.delivery_team_excluded_dates || []).includes(day)}
                  onChange={(e) => setForm({
                    ...form,
                    delivery_team_excluded_dates: e.target.checked
                      ? (form.delivery_team_excluded_dates || []).filter(value => value !== day)
                      : [...new Set([...(form.delivery_team_excluded_dates || []), day])],
                  })}
                />
                {deliveryTeamName} — dowóz {day}
              </label>
            ))}
            {[
              { key: 'rusztowanie', label: 'Rusztowanie' },
              { key: 'drabina',     label: 'Drabina' },
              { key: 'pajaczek',   label: 'Pajączek' },
              { key: 'manipulator', label: 'Manipulator' },
              { key: 'agregat',    label: 'Agregat' },
              { key: 'dodatkowy_bus', label: 'Dodatkowy bus' },
              { key: 'tir',        label: 'Tir' },
            ].map(({ key, label }) => (
              <label key={key} className="equipment-flag-item">
                <input
                  type="checkbox"
                  checked={!!form[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
                />
                {label}
              </label>
            ))}
          </div>

          {!isNew && relatedAdditionalWork && (
            <div style={{ marginTop: '14px', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#fafafa' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                <div>
                  <div style={{ fontWeight: 700, color: '#334155' }}>Dodatkowa praca</div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                    Powiązany wpis: {relatedAdditionalWork.order_number}. USL dla tego wpisu wynosi 0.
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                  <button
                    type="button"
                    className="btn-danger btn-sm"
                    onClick={handleDeleteAdditionalWork}
                    disabled={saving}
                  >
                    Usuń dodatkową pracę
                  </button>
                </div>
              </div>
            </div>
          )}

          {isPremiumCopy && (
            <div style={{ marginTop: '16px', padding: '16px', background: '#f8fafc', borderRadius: '8px' }}>
              <h4 style={{ margin: '0 0 12px', fontSize: '14px', color: '#475569' }}>Suma USL dla PR</h4>
              <label>USL do podziału
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  {...(canEditSettlement
                    ? { defaultValue: premiumUslItem?.net_price_pln ?? 0 }
                    : { value: settlement?.total_usl ?? 0 })}
                  onBlur={(e) => savePremiumUslValue(e.target.value)}
                  disabled={saving || !canEditSettlement}
                />
              </label>
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#64748b' }}>
                Dla zleceń PR do podziału trafia wyłącznie ta kwota USL.
              </div>
            </div>
          )}

          {/* Assignments section - only for existing installations */}
          {!isNew && installationDays.length > 0 && isDeliveryOnly && (
            <div className="assignments-section assignments-section--compact">
              <button
                type="button"
                className="section-collapse-toggle"
                aria-expanded={assignmentsExpanded}
                onClick={() => setAssignmentsExpanded((expanded) => !expanded)}
              >
                <span>Przypisania per dzień</span>
                <span aria-hidden="true">{assignmentsExpanded ? '−' : '+'}</span>
              </button>
              {assignmentsExpanded && (
                <div style={{ padding: '12px', background: '#dbeafe', border: '1px solid #2563eb', borderRadius: '8px', color: '#1e40af', fontSize: '13px' }}>
                  To zlecenie obejmuje wyłącznie dowóz do klienta. Przypisana jest specjalna ekipa transportowa, bez możliwości przypisania ekipy montażowej.
                </div>
              )}
            </div>
          )}
          {isDeliveryOnly && renderSettlementParameters()}

          {!isNew && installationDays.length > 0 && !isDeliveryOnly && (
            <div className="assignments-section assignments-section--compact">
              <button
                type="button"
                className="section-collapse-toggle"
                aria-expanded={assignmentsExpanded}
                onClick={() => setAssignmentsExpanded((expanded) => !expanded)}
              >
                <span>Przypisania per dzień</span>
                <span aria-hidden="true">{assignmentsExpanded ? '−' : '+'}</span>
              </button>
              {assignmentsExpanded && (
              <>
              {isDadInstallation && (
                <div style={{ marginBottom: '12px', padding: '12px', background: '#dbeafe', border: '1px solid #2563eb', borderRadius: '8px', color: '#1e40af', fontSize: '13px' }}>
                  Zlecenia DAD domyślnie obsługuje ekipa „{dadTeamName}”. Możesz przypisać je do innej ekipy po potwierdzeniu.
                </div>
              )}
              
              {installationDays.map((day) => {
                const dayAssignments = assignmentsByDay[day] || [];
                const dayTeamOptions = [...new Set(
                  dayAssignments.filter(a => a.team_id).map(a => a.team_id)
                )]
                  .map(teamId => teams.find(team => team.id === teamId))
                  .filter(team => team && !team.is_special_truck && !team.is_special_dad);
                const dateObj = new Date(day);
                const dayLabel = `${dateObj.getDate()} ${POLISH_MONTHS[dateObj.getMonth()]} ${dateObj.getFullYear()}`;
                const dayName = POLISH_DAYS_SHORT[dateObj.getDay() === 0 ? 6 : dateObj.getDay() - 1];
                const hasKatB = hasKatBForDay(day);
                
                return (
                  <div key={day} className="day-assignment-block">
                    <div className="day-assignment-header">
                      <span className="day-assignment-label">
                        {dayName}, {dayLabel}
                        {!hasKatB && dayAssignments.length > 0 && (
                          <span className="kat-b-warning-badge" title="Brak Kat.B w tym dniu">⚠️</span>
                        )}
                      </span>
                      {dayAssignments.length === 0 && (
                        <span className="day-assignment-empty">Brak przypisań</span>
                      )}
                    </div>
                    
                    {dayAssignments.length > 0 && (() => {
                        // Grupuj przypisania po team_id
                        const teamGroups = {};
                        const individualAssignments = [];
                        dayAssignments.forEach(a => {
                          if (a.team_id) {
                            if (!teamGroups[a.team_id]) teamGroups[a.team_id] = { teamName: a.team_name, assignments: [] };
                            teamGroups[a.team_id].assignments.push(a);
                          } else {
                            individualAssignments.push(a);
                          }
                        });

                        return (
                          <ul className="assignment-list">
                            {/* Ekipy (pełne lub częściowe) */}
                            {Object.entries(teamGroups).map(([teamIdStr, group]) => {
                              const teamId = Number(teamIdStr);
                              const isFullTeam = group.assignments.some(a => !a.installer_id);
                              const team = teams.find(t => t.id === teamId);

                              // Dla pełnej ekipy: pokaż wszystkich z teams prop (bez urlopowiczów)
                              // Dla częściowej: pokaż konkretnych przypisanych
                              const displayMembers = isFullTeam
                                ? (team ? team.members.filter(m => !isOnVacation(m.id, day)) : []).map(m => ({
                                    id: m.id,
                                    name: `${m.first_name} ${m.last_name}`,
                                    kat_b: m.kat_b,
                                    // pełna ekipa ma jeden assignment
                                    assignmentId: group.assignments[0].id,
                                    isFullTeamMember: true,
                                  })).concat(
                                    group.assignments
                                      .filter(a => a.installer_id && !team?.members.some(m => m.id === a.installer_id))
                                      .map(a => ({
                                        id: a.installer_id,
                                        name: a.installer_name || '',
                                        kat_b: installers.find(i => i.id === a.installer_id)?.kat_b || false,
                                        assignmentId: a.id,
                                        isFullTeamMember: false,
                                      }))
                                  )
                                : group.assignments.map(a => ({
                                    id: a.installer_id,
                                    name: a.installer_name || '',
                                    kat_b: team?.members.find(m => m.id === a.installer_id)?.kat_b || false,
                                    assignmentId: a.id,
                                    isFullTeamMember: false,
                                  }));

                              return (
                                <li key={`team-${teamId}`} className="team-assignment-expanded">
                                  <div className="team-assignment-row">
                                    <strong className="team-assignment-name">
                                      {group.teamName}
                                      <span className="team-assignment-count">
                                        {displayMembers.length}{' '}
                                        {displayMembers.length === 1
                                          ? 'osoba'
                                          : (displayMembers.length >= 2 && displayMembers.length <= 4 ? 'osoby' : 'osób')}
                                      </span>
                                    </strong>
                                    <button
                                      className="btn-icon-remove"
                                      title="Usuń całą ekipę z tego dnia"
                                      onClick={() => handleRemoveTeamGroup(teamId, day)}
                                      disabled={saving}
                                    >✕</button>
                                  </div>
                                  {displayMembers.length > 0 && (
                                    <ul className="team-members-list">
                                      {displayMembers.map(member => (
                                        <li key={member.id} className="team-member-item">
                                          <span className="member-name" title={member.name}>{member.name}</span>
                                          {member.kat_b && <span className="member-katb-badge">Kat.B</span>}
                                          {!isDadInstallation && (
                                            <button
                                              className="btn-icon-remove"
                                              title={`Usuń ${member.name} z tego dnia`}
                                              onClick={() =>
                                                member.isFullTeamMember
                                                  ? handleRemoveTeamMember(group.assignments[0], member.id)
                                                  : handleRemoveAssignment(member.assignmentId)
                                              }
                                              disabled={saving}
                                            >✕</button>
                                          )}
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </li>
                              );
                            })}

                            {/* Indywidualni pracownicy (bez team_id) */}
                            {individualAssignments.map(a => (
                              <li key={a.id} className="individual-assignment-row">
                                <span>{a.installer_name || ''}</span>
                                <span className="individual-assignment-actions">
                                  {dayTeamOptions.length > 0 && (
                                    <select
                                      value=""
                                      aria-label={`Przenieś ${a.installer_name || 'osobę'} do ekipy`}
                                      onChange={event => handleMoveIndividualToTeam(a.id, event.target.value)}
                                      disabled={saving}
                                    >
                                      <option value="">Przenieś do ekipy…</option>
                                      {dayTeamOptions.map(team => (
                                        <option key={team.id} value={team.id}>{team.name}</option>
                                      ))}
                                    </select>
                                  )}
                                  <button className="btn-icon-remove" onClick={() => handleRemoveAssignment(a.id)}>✕</button>
                                </span>
                              </li>
                            ))}
                          </ul>
                        );
                      })()}

                    {/* Inline dodawanie osoby do dnia */}
                    {!isDadInstallation && (addMemberDay === day ? (
                      <div className="day-add-member-inline">
                        <select
                          value={addMemberInstallerId}
                          onChange={e => setAddMemberInstallerId(e.target.value)}
                          className="assign-select"
                          autoFocus
                        >
                          <option value="">-- Wybierz osobę --</option>
                          {getAvailableInstallersForDay(day).map(i => {
                            const vacation = getApprovedVacation(i.id, day);
                            return (
                              <option
                                key={i.id}
                                value={i.id}
                                disabled={!!vacation}
                                style={{ textDecoration: vacation ? 'line-through' : 'none' }}
                              >
                                {i.first_name} {i.last_name}
                                {vacation ? ` — urlop (${vacation.vacation_type})` : ''}
                              </option>
                            );
                          })}
                        </select>
                        {dayTeamOptions.length > 0 && (
                          <select
                            value={addMemberTeamId}
                            onChange={e => setAddMemberTeamId(e.target.value)}
                            className="assign-select"
                            aria-label="Ekipa dla dodawanej osoby"
                          >
                            <option value="">-- Wybierz ekipę --</option>
                            {dayTeamOptions.map(team => (
                              <option key={team.id} value={team.id}>{team.name}</option>
                            ))}
                          </select>
                        )}
                        <button
                          className="btn-primary btn-sm"
                          onClick={handleConfirmAddMember}
                          disabled={!addMemberInstallerId || (dayTeamOptions.length > 0 && !addMemberTeamId) || saving}
                        >Dodaj</button>
                        <button
                          className="btn-secondary btn-sm"
                          onClick={() => { setAddMemberDay(null); setAddMemberInstallerId(''); setAddMemberTeamId(''); }}
                        >Anuluj</button>
                      </div>
                    ) : (
                      <button
                        className="btn-add-member"
                        onClick={() => {
                          setAddMemberDay(day);
                          setAddMemberInstallerId('');
                          setAddMemberTeamId(dayTeamOptions.length === 1 ? String(dayTeamOptions[0].id) : '');
                        }}
                      >+ {dayTeamOptions.length > 0 ? 'Dodaj osobę do ekipy' : 'Dodaj osobę'}</button>
                    ))}
                  </div>
                );
              })}

              {(vacationWarnings.length > 0 || pajaczekConflictWarnings.length > 0 || generalWarnings.filter(w => !w.includes('Kat.B') && !w.includes('kategori') && !w.includes('obwod')).length > 0) && (
                <div className="vacation-conflict-banner">
                  <div className="vacation-conflict-title">⚠️ Ostrzeżenia</div>
                  
                  {vacationWarnings.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Konflikty urlopowe:</div>
                      <ul className="vacation-conflict-list">
                        {vacationWarnings.map((w, i) => (
                          <li key={i}>{w} – <strong>nieobecny w tym terminie</strong></li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {pajaczekConflictWarnings.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Konflikt wyposażenia:</div>
                      <ul className="vacation-conflict-list">
                        {pajaczekConflictWarnings.map((w, i) => (
                          <li key={i}>{w}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {generalWarnings.filter(w => !w.includes('Kat.B') && !w.includes('kategori') && !w.includes('obwod')).length > 0 && (
                    <div>
                      <ul className="vacation-conflict-list">
                        {generalWarnings
                          .filter(w => !w.includes('Kat.B') && !w.includes('kategori') && !w.includes('obwod'))
                          .map((w, i) => (
                            <li key={i}>{w}</li>
                          ))
                        }
                      </ul>
                    </div>
                  )}
                  
                  <button className="vacation-conflict-close" onClick={() => { setVacationWarnings([]); setGeneralWarnings([]); }}>
                    Rozumiem ✕
                  </button>
                </div>
              )}

              <div className="assign-row">
                <select 
                  value={selectedDay} 
                  onChange={(e) => {
                    setSelectedDay(e.target.value);
                    setAssignInstallerId('');
                  }}
                  className="assign-select"
                >
                  <option value="">-- Wybierz dzień --</option>
                  {installationDays.map((day) => {
                    const dateObj = new Date(day);
                    const dayLabel = `${dateObj.getDate()} ${POLISH_MONTHS[dateObj.getMonth()]}`;
                    const dayName = POLISH_DAYS_SHORT[dateObj.getDay() === 0 ? 6 : dateObj.getDay() - 1];
                    return (
                      <option key={day} value={day}>{dayName}, {dayLabel}</option>
                    );
                  })}
                </select>
                <select 
                  value={assignTeamId} 
                  onChange={(e) => { setAssignTeamId(e.target.value); setAssignInstallerId(''); }}
                  className="assign-select"
                >
                  <option value="">-- Wybierz ekipę --</option>
                  {teams
                    .filter((team) => !team.is_special_truck)
                    .map((team) => <option key={team.id} value={team.id}>{team.name}</option>)}
                </select>
                {!isDadInstallation && (
                  <>
                    <span style={{ margin: '0 8px', color: '#64748b' }}>lub</span>
                    <select
                      value={assignInstallerId}
                      onChange={(e) => { setAssignInstallerId(e.target.value); setAssignTeamId(''); }}
                      className="assign-select"
                    >
                      <option value="">-- Wybierz pracownika --</option>
                      {installers.map((i) => {
                        const vacation = selectedDay
                          ? getApprovedVacation(i.id, selectedDay)
                          : null;
                        return (
                          <option
                            key={i.id}
                            value={i.id}
                            disabled={!!vacation}
                            style={{ textDecoration: vacation ? 'line-through' : 'none' }}
                          >
                            {i.first_name} {i.last_name}
                            {vacation ? ` — urlop (${vacation.vacation_type})` : ''}
                          </option>
                        );
                      })}
                    </select>
                  </>
                )}
                <button 
                  className="btn-primary" 
                  onClick={handleAddAssignment} 
                  disabled={saving || (!assignTeamId && !assignInstallerId) || !selectedDay}
                >
                  {saving ? 'Przypisywanie...' : 'Przypisz'}
                </button>
              </div>
              </>
              )}

              {renderSettlementParameters()}

              {/* Settlement breakdown section */}
              {canViewSettlement && settlement && (
                <div className={`settlement-section ${settlementExpanded ? '' : 'settlement-section--collapsed'}`}>
                  <button
                    type="button"
                    className="section-collapse-toggle section-collapse-toggle--settlement"
                    aria-expanded={settlementExpanded}
                    onClick={() => setSettlementExpanded((expanded) => !expanded)}
                  >
                    <span>
                      💰 Rozliczenie zlecenia
                      {settlement.saved_additional_amounts?.length > 0
                        ? ` · ${settlement.saved_additional_amounts.length} ${settlement.saved_additional_amounts.length === 1 ? 'dopłata' : 'dopłat'}`
                        : ''}
                    </span>
                    <span aria-hidden="true">{settlementExpanded ? '−' : '+'}</span>
                  </button>
                  {settlementExpanded && (
                  <>
                  <div className="settlement-summary">
                    <div className="settlement-row">
                      <span>Suma USL:</span>
                      <strong>{settlement.total_usl.toFixed(2)} PLN</strong>
                    </div>
                    {!isPremiumCopy && !usesPzlFlatPricing && (
                      <>
                        <div className="settlement-row">
                          <span>Narzut ({settlement.overhead_percentage}%):</span>
                          <span>− {settlement.overhead_amount.toFixed(2)} PLN</span>
                        </div>
                        <div className="settlement-row">
                          <span>Podstawa:</span>
                          <span>{settlement.base_amount.toFixed(2)} PLN</span>
                        </div>
                      </>
                    )}
                    <div className="settlement-row">
                      <span>
                        {usesPzlFlatPricing
                          ? 'Pula PZL do wypłaty:'
                          : (isPremiumCopy ? 'Do podziału:' : `Do podziału (${settlement.distribution_percentage}%):`)}
                      </span>
                      <strong className="settlement-highlight">{settlement.total_to_distribute.toFixed(2)} PLN</strong>
                    </div>
                    {settlement.is_pzl && (
                      <div className="settlement-row">
                        <span>Tryb PZL:</span>
                        <strong>
                          {pzlSettlementModeLabel(settlement.pzl_settlement_mode)}
                          {usesPzlFlatPricing ? ` · ${formatMoneyCompact(settlement.pzl_settlement_amount)}` : ''}
                        </strong>
                      </div>
                    )}
                  </div>

                  <SavedFinalAdditionsPanel
                    additions={settlement.saved_additional_amounts}
                  />

                  {!settlement.is_pzl && (
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center', margin: '12px 0' }}>
                    <span style={{ fontSize: '13px', fontWeight: 600 }}>Sposób rozliczenia:</span>
                    <label style={{ display: 'flex', gap: '6px', alignItems: 'center', fontSize: '13px' }}>
                      <input
                        type="radio"
                        name="settlementMode"
                        checked={(form.settlement_mode || 'weights') === 'weights'}
                        onChange={() => saveSettlementSettings({ settlement_mode: 'weights' })}
                        disabled={saving || !canEditSettlement}
                      />
                      według wag
                    </label>
                    <label style={{ display: 'flex', gap: '6px', alignItems: 'center', fontSize: '13px' }}>
                      <input
                        type="radio"
                        name="settlementMode"
                        checked={form.settlement_mode === 'manual'}
                        onChange={() => saveSettlementSettings({ settlement_mode: 'manual' })}
                        disabled={saving || !canEditSettlement}
                      />
                      manualne kwoty
                    </label>
                    {form.settlement_mode === 'manual' && (
                      <button className="btn-primary btn-sm" onClick={saveManualSettlementAmounts} disabled={saving || !canEditSettlement}>
                        Zapisz kwoty
                      </button>
                    )}
                  </div>
                  )}
                  
                  <div className="settlement-workers">
                    <h5>Podział między pracowników:</h5>
                    {settlement.days && settlement.days.length > 1 && !usesPzlFlatPricing ? (
                      settlement.days.map((day, dayIdx) => (
                        <div key={day.date || dayIdx} style={{ marginBottom: '16px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                            <strong style={{ fontSize: '13px', color: '#334155' }}>
                              📅 {formatDayLabel(day.date)}
                              {day.team_name ? ` · ${day.team_name}` : ''}
                            </strong>
                            <span style={{ fontSize: '12px', color: '#64748b' }}>
                              Do podziału w tym dniu: <strong>{day.total_to_distribute.toFixed(2)} PLN</strong>
                            </span>
                          </div>
                          <table className="settlement-table">
                            <thead>
                              <tr>
                                <th>Pracownik</th>
                                <th>Stanowisko</th>
                                <th>Waga</th>
                                <th>Udział</th>
                                <th>Kwota</th>
                              </tr>
                            </thead>
                            <tbody>
                              {day.installers.map((item, idx) => (
                                <tr key={idx}>
                                  <td>{item.installer_name}</td>
                                  <td>{item.position}</td>
                                  <td className="numeric-cell">{form.settlement_mode === 'manual' || usesPzlFlatPricing ? '—' : item.position_weight}</td>
                                  <td className="numeric-cell">{item.share_percentage.toFixed(2)}%</td>
                                  <td className="numeric-cell settlement-amount">
                                    {form.settlement_mode === 'manual' && !settlement.is_pzl ? (
                                      <>
                                        <input
                                          className="item-input numeric-input"
                                          inputMode="decimal"
                                          value={(form.manual_settlement_amounts || {})[String(item.installer_id)] ?? settlementBaseAmount(item)}
                                          onChange={(e) => updateManualSettlementAmount(item.installer_id, e.target.value)}
                                          disabled={!canEditSettlement}
                                        />
                                        {Number(item.additional_amount || 0) > 0 && (
                                          <small className="settlement-manual-final-amount">
                                            Razem: {Number(item.amount || 0).toFixed(2)} PLN
                                          </small>
                                        )}
                                      </>
                                    ) : <SettlementAmountValue item={item} />}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ))
                    ) : (
                      <table className="settlement-table">
                        <thead>
                          <tr>
                            <th>Pracownik</th>
                            <th>Stanowisko</th>
                            <th>Waga</th>
                            <th>Udział</th>
                            <th>Kwota</th>
                          </tr>
                        </thead>
                        <tbody>
                          {settlement.installers.map((item, idx) => (
                            <tr key={idx}>
                              <td>{item.installer_name}</td>
                              <td>{item.position}</td>
                              <td className="numeric-cell">{form.settlement_mode === 'manual' || usesPzlFlatPricing ? '—' : item.position_weight}</td>
                              <td className="numeric-cell">{item.share_percentage.toFixed(2)}%</td>
                              <td className="numeric-cell settlement-amount">
                                {form.settlement_mode === 'manual' && !settlement.is_pzl ? (
                                  <>
                                    <input
                                      className="item-input numeric-input"
                                      inputMode="decimal"
                                      value={(form.manual_settlement_amounts || {})[String(item.installer_id)] ?? settlementBaseAmount(item)}
                                      onChange={(e) => updateManualSettlementAmount(item.installer_id, e.target.value)}
                                      disabled={!canEditSettlement}
                                    />
                                    {Number(item.additional_amount || 0) > 0 && (
                                      <small className="settlement-manual-final-amount">
                                        Razem: {Number(item.amount || 0).toFixed(2)} PLN
                                      </small>
                                    )}
                                  </>
                                ) : <SettlementAmountValue item={item} />}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                  </>
                  )}
                </div>
              )}
              {loadingSettlement && (
                <div className="settlement-loading">Ładowanie rozliczenia...</div>
              )}
            </div>
          )}

          </>
          )}
        </div>

        <div className={`modal-footer ${showAssignmentQuestion ? '' : 'installation-details-actions'}`}>
          {showAssignmentQuestion ? (
            <>
              <button className="btn-secondary" onClick={() => setShowAssignmentQuestion(false)}>
                ← Wróć do edycji
              </button>
              <button className="btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Zapisywanie…' : 'Zatwierdź'}
              </button>
            </>
          ) : (
            <>
              <button className="btn-secondary" onClick={onClose}>Anuluj</button>
              {!isNew && installation?.planned_start_date && (
                <button className="btn-warning" onClick={handleUnschedule} disabled={saving}>
                  ↓ Do oczekujących
                </button>
              )}
              {!isNew && (
                <button
                  className="btn-secondary"
                  title="Eksportuj do PDF"
                  onClick={handlePdfClick}
                >PDF 📄</button>
              )}
              <button className="btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Zapisywanie…' : 'Zapisz'}
              </button>
            </>
          )}
        </div>
      </aside>

      {/* PDF Day Picker */}
      {showPdfDayPicker && (
        <div className="modal-overlay" onClick={() => setShowPdfDayPicker(false)} style={{ zIndex: 10001 }}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <h3>Wybierz dzień do wydruku</h3>
              <button className="modal-close" onClick={() => setShowPdfDayPicker(false)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '16px', fontSize: '14px', color: '#475569' }}>
                Zlecenie rozplanowane na kilka dni. Wybierz dzień, dla którego chcesz wygenerować PDF:
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {installationDays.map((day) => (
                  <button
                    key={day}
                    className="btn-primary"
                    onClick={() => handlePdfDaySelected(day)}
                    style={{ justifyContent: 'flex-start' }}
                  >
                    📅 {formatDayLabel(day)}
                  </button>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowPdfDayPicker(false)}>Anuluj</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


function SplitInstallationModal({ installation, warningMessage, totalPerimeter, splitReason = 'perimeter', queueLength = 0, teamId = null, onClose, onConfirm }) {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedDates, setSelectedDates] = useState([]);
  const [premiumUslValue, setPremiumUslValue] = useState('');
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [saving, setSaving] = useState(false);
  const [showAssignmentQuestion, setShowAssignmentQuestion] = useState(false);
  const [assignmentAction, setAssignmentAction] = useState('keep'); // 'keep', 'copy'
  const [error, setError] = useState('');

  if (!installation) return null;

  const startDate = installation.planned_start_date ? new Date(installation.planned_start_date) : null;
  const endDate = installation.planned_end_date ? new Date(installation.planned_end_date) : null;
  const hasExistingAssignments = installation.assignments && installation.assignments.length > 0;
  const isPremiumSplit = splitReason === 'premium_plus';

  function handleDontSplit() {
    onClose();
  }

  function handleStartSplit() {
    setShowDatePicker(true);
    if (startDate && endDate) {
      setSelectedDates(installationDates(installation));
    }
  }

  function toggleDate(dateStr) {
    if (isPremiumSplit) {
      setSelectedDates([dateStr]);
      return;
    }
    if (selectedDates.includes(dateStr)) {
      setSelectedDates(selectedDates.filter(d => d !== dateStr));
    } else {
      setSelectedDates([...selectedDates, dateStr]);
    }
  }

  async function handleConfirmSplit() {
    if (selectedDates.length === 0) return;
    if (isPremiumSplit && (premiumUslValue === '' || Number(premiumUslValue) < 0)) return;
    
    const sorted = selectedDates.slice().sort();
    const newStartDate = sorted[0];
    const newEndDate = sorted[sorted.length - 1];
    
    if (!isPremiumSplit && !showAssignmentQuestion) {
      setShowAssignmentQuestion(true);
      return;
    }
    
    setSaving(true);
    setError('');
    try {
      await onConfirm(newStartDate, newEndDate, assignmentAction, {
        splitReason,
        plannedDates: sorted,
        premiumUslValue: premiumUslValue === '' ? null : Number(premiumUslValue),
      });
      onClose();
    } catch (e) {
      // Zapis jest atomowy – przy błędzie nic się nie zmieniło, okno zostaje otwarte.
      console.error('Error saving split dates', e);
      setError(apiErrorMessage(e, 'Nie udało się zapisać rozplanowania.'));
    } finally {
      setSaving(false);
    }
  }

  function prevMonth() {
    if (calMonth === 0) { setCalMonth(11); setCalYear(calYear - 1); }
    else { setCalMonth(calMonth - 1); }
  }

  function nextMonth() {
    if (calMonth === 11) { setCalMonth(0); setCalYear(calYear + 1); }
    else { setCalMonth(calMonth + 1); }
  }

  // Render mini calendar
  function renderCalendar() {
    const firstDay = new Date(calYear, calMonth, 1);
    const lastDay = new Date(calYear, calMonth + 1, 0);
    const startGrid = startOfWeek(firstDay);
    const days = [];
    let d = new Date(startGrid);
    while (d <= lastDay || days.length % 7 !== 0) {
      days.push(new Date(d));
      d = addDays(d, 1);
      if (days.length > 42) break;
    }

    return (
      <div className="split-calendar">
        <div className="split-calendar-header">
          <button className="btn-nav" onClick={prevMonth}>‹</button>
          <span className="split-calendar-month">{POLISH_MONTHS[calMonth]} {calYear}</span>
          <button className="btn-nav" onClick={nextMonth}>›</button>
        </div>
        <div className="vac-cal-grid">
          {POLISH_DAYS_SHORT.map((n) => <div key={n} className="vac-cal-head">{n}</div>)}
          {days.map((day, idx) => {
            const dateStr = toISODate(day);
            const isSelected = selectedDates.includes(dateStr);
            const isCurrentMonth = day.getMonth() === calMonth;
            const cls = `vac-cal-day ${isCurrentMonth ? '' : 'other-month'} ${isSelected ? 'selected-date' : ''}`;
            return (
              <div
                key={idx}
                className={cls}
                onClick={() => isCurrentMonth && toggleDate(dateStr)}
                style={{ cursor: isCurrentMonth ? 'pointer' : 'default' }}
              >
                {day.getDate()}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={handleDontSplit}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
        <div className="modal-header">
          <h3>⚠️ Montaż wymaga rozplanowania</h3>
          <button className="modal-close" onClick={handleDontSplit}>✕</button>
        </div>

        <div className="modal-body">
          {queueLength > 0 && (
            <div style={{ marginBottom: '16px', padding: '10px', background: '#eff6ff', border: '1px solid #3b82f6', borderRadius: '8px', fontSize: '13px', color: '#1e40af' }}>
              <strong>Uwaga:</strong> To jest {queueLength === 1 ? 'przedostatnie' : `jedno z ${queueLength + 1}`} zleceń wymagających rozplanowania. Po zatwierdzeniu zostaniesz poproszony o rozplanowanie kolejnego zlecenia.
            </div>
          )}
          <div style={{ marginBottom: '16px', padding: '12px', background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '8px' }}>
            <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.5' }}>{String(warningMessage || '')}</p>
            {totalPerimeter > 0 && (
              <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#92400e' }}>
                <strong>Całkowita suma obwodów:</strong> {parseFloat(totalPerimeter).toFixed(1)} m
              </p>
            )}
          </div>

          {!showDatePicker ? (
            <div style={{ marginBottom: '16px' }}>
              <p style={{ marginBottom: '12px', fontSize: '14px', color: '#475569' }}>
                {isPremiumSplit
                  ? 'Czy chcesz dodać osobny dzień montażu dla prac premium?'
                  : 'Czy chcesz rozplanować ten montaż na więcej dni?'}
              </p>
              {startDate && endDate && (
                <p style={{ fontSize: '13px', color: '#64748b' }}>
                  Obecnie zaplanowano: <strong>{toISODate(startDate)}</strong> do <strong>{toISODate(endDate)}</strong>
                </p>
              )}
            </div>
          ) : showAssignmentQuestion ? (
            <div>
              {hasExistingAssignments ? (
                <>
                  <p style={{ marginBottom: '16px', fontSize: '14px', fontWeight: 'bold' }}>
                    Zlecenie ma już przypisane ekipy. Co zrobić z przypisaniami?
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <label style={{ display: 'flex', alignItems: 'start', gap: '8px', padding: '10px', border: '1.5px solid #cbd5e1', borderRadius: '8px', cursor: 'pointer', background: assignmentAction === 'keep' ? '#eff6ff' : '#fff' }}>
                      <input 
                        type="radio" 
                        name="assignmentAction" 
                        value="keep"
                        checked={assignmentAction === 'keep'}
                        onChange={(e) => setAssignmentAction(e.target.value)}
                        style={{ marginTop: '2px' }}
                      />
                      <div>
                        <div style={{ fontWeight: 'bold', fontSize: '13px', color: '#1e293b' }}>Zostaw istniejące przypisania</div>
                        <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>Ekipy pozostaną przypisane do swoich dni. Status zlecenia pozostanie bez zmian. Nowe dni będą bez przypisań.</div>
                      </div>
                    </label>
                    
                    <label style={{ display: 'flex', alignItems: 'start', gap: '8px', padding: '10px', border: '1.5px solid #cbd5e1', borderRadius: '8px', cursor: 'pointer', background: assignmentAction === 'copy' ? '#eff6ff' : '#fff' }}>
                      <input 
                        type="radio" 
                        name="assignmentAction" 
                        value="copy"
                        checked={assignmentAction === 'copy'}
                        onChange={(e) => setAssignmentAction(e.target.value)}
                        style={{ marginTop: '2px' }}
                      />
                      <div>
                        <div style={{ fontWeight: 'bold', fontSize: '13px', color: '#1e293b' }}>Skopiuj na wszystkie dni</div>
                        <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>Przypisania zostaną skopiowane na wszystkie wybrane dni.</div>
                      </div>
                    </label>
                  </div>
                </>
              ) : (
                <div>
                  <p style={{ marginBottom: '16px', fontSize: '14px', fontWeight: 'bold' }}>
                    ℹ️ Informacja o przypisaniu ekipy
                  </p>
                  {teamId !== null && teamId !== undefined ? (
                    <div style={{ padding: '12px', background: '#eff6ff', border: '1px solid #3b82f6', borderRadius: '8px', fontSize: '13px', lineHeight: '1.6' }}>
                      <p style={{ margin: 0 }}>
                        Ekipa zostanie <strong>automatycznie przypisana do wszystkich dni montażu</strong>, ponieważ zlecenie zostało przeciągnięte na konkretną ekipę w widoku kalendarza.
                      </p>
                    </div>
                  ) : (
                    <div style={{ padding: '12px', background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '8px', fontSize: '13px', lineHeight: '1.6' }}>
                      <p style={{ margin: 0 }}>
                        To zlecenie <strong>nie ma jeszcze przypisanych ekip</strong>. Status zostanie ustawiony na <em>"wstępnie zaplanowany"</em>.
                      </p>
                      <p style={{ margin: '8px 0 0 0', color: '#92400e' }}>
                        💡 Możesz przypisać ekipy później, otwierając szczegóły zlecenia i wybierając ekipę dla każdego dnia montażu.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div>
              <p style={{ marginBottom: '12px', fontSize: '14px', fontWeight: 'bold' }}>
                {isPremiumSplit
                  ? 'Wybierz dodatkowy dzień montażu (kliknij na kalendarzu):'
                  : 'Wybierz dni montażu (kliknij na kalendarzu):'}
              </p>
              {renderCalendar()}
              {isPremiumSplit && (
                <div style={{ marginTop: '12px' }}>
                  <label style={{ display: 'block' }}>Suma USL *
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={premiumUslValue}
                      onChange={(e) => setPremiumUslValue(e.target.value)}
                      placeholder="0.00"
                    />
                  </label>
                  <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#64748b' }}>
                    Kwota dotyczy wyłącznie zlecenia {installation.order_number}-PR.
                  </p>
                </div>
              )}
              <p style={{ marginTop: '12px', fontSize: '13px', color: '#64748b' }}>
                Wybrano: <strong>{selectedDates.length}</strong> {selectedDates.length === 1 ? 'dzień' : 'dni'}
              </p>
            </div>
          )}
        </div>

        {error && <div className="modal-error" role="alert">{error}</div>}

        <div className="modal-footer">
          {!showDatePicker ? (
            <>
              <button className="btn-secondary" onClick={handleDontSplit}>
                Nie rozplanuj
              </button>
              <button className="btn-primary" onClick={handleStartSplit}>
                {isPremiumSplit ? 'Dodaj dzień premium →' : 'Rozplanuj →'}
              </button>
            </>
          ) : showAssignmentQuestion ? (
            <>
              <button className="btn-secondary" onClick={() => setShowAssignmentQuestion(false)}>
                ← Wróć do wyboru dni
              </button>
              <button 
                className="btn-primary" 
                onClick={handleConfirmSplit} 
                disabled={saving}
              >
                {saving ? 'Zapisywanie…' : 'Zatwierdź'}
              </button>
            </>
          ) : (
            <>
              <button className="btn-secondary" onClick={() => setShowDatePicker(false)}>
                ← Wróć
              </button>
              <button 
                className="btn-primary" 
                onClick={handleConfirmSplit} 
                disabled={saving || selectedDates.length === 0 || (isPremiumSplit && (premiumUslValue === '' || Number(premiumUslValue) < 0))}
              >
                {saving ? 'Zapisywanie…' : 'Zatwierdź'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
//  Quick-add modal (fast calendar entry: order number + date)
// ──────────────────────────────────────────────────────────────
function QuickAddInstallationModal({ teams = [], canViewSettlement = false, onClose, onSaved }) {
  const savingRef = useRef(false);
  const [activeTab, setActiveTab] = useState('installation');
  const [orderNumber, setOrderNumber] = useState('');
  const [noteTitle, setNoteTitle] = useState('');
  const [noteCountry, setNoteCountry] = useState('PL');
  const [noteUslAmount, setNoteUslAmount] = useState('');
  const [startDate, setStartDate] = useState('');
  const [notes, setNotes] = useState('');
  const [teamId, setTeamId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSave() {
    if (savingRef.current) return;
    if (activeTab === 'installation' && !orderNumber.trim()) {
      setError('Numer zlecenia jest wymagany');
      return;
    }
    if (activeTab === 'note' && !noteTitle.trim()) {
      setError('Tytuł notatki jest wymagany');
      return;
    }
    if (activeTab === 'note' && !notes.trim()) {
      setError('Treść notatki jest wymagana');
      return;
    }
    if (!startDate) {
      setError(activeTab === 'note' ? 'Data wpisu jest wymagana' : 'Data montażu jest wymagana');
      return;
    }
    const parsedUslAmount = noteUslAmount === ''
      ? 0
      : Number(String(noteUslAmount).replace(',', '.'));
    if (activeTab === 'note' && canViewSettlement && (!Number.isFinite(parsedUslAmount) || parsedUslAmount < 0)) {
      setError('Wartość USL musi być liczbą równą lub większą od 0');
      return;
    }
    savingRef.current = true;
    setSaving(true);
    setError('');
    try {
      const created = activeTab === 'note'
        ? await installationService.createCalendarNote({
          calendar_note_title: noteTitle.trim(),
          calendar_note_country: noteCountry,
          notes: notes.trim(),
          ...(canViewSettlement ? { usl_amount: parsedUslAmount } : {}),
          planned_start_date: startDate,
          team_id: teamId ? Number(teamId) : null,
        })
        : await installationService.quickAddInstallation({
          order_number: orderNumber.trim(),
          notes: notes || null,
          planned_start_date: startDate,
          team_id: teamId ? Number(teamId) : null,
        });
      onSaved(created);
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (Array.isArray(detail)) setError(detail.map((d) => d.msg || JSON.stringify(d)).join('; '));
      else setError(String(detail || 'Błąd zapisu'));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }

  return (
    <div
      id="quick-add-calendar-panel"
      className="modal-box quick-add-popover"
      role="dialog"
      aria-label="Dodaj wpis do kalendarza"
    >
        <div className="modal-header">
          <h3>Dodaj wpis do kalendarza</h3>
          <button className="modal-close" onClick={onClose} disabled={saving}>✕</button>
        </div>
        <div className="quick-add-tabs" role="tablist" aria-label="Rodzaj wpisu do kalendarza">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'installation'}
            className={activeTab === 'installation' ? 'active' : ''}
            onClick={() => { setActiveTab('installation'); setError(''); }}
            disabled={saving}
          >
            Szybki montaż
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'note'}
            className={activeTab === 'note' ? 'active' : ''}
            onClick={() => { setActiveTab('note'); setError(''); }}
            disabled={saving}
          >
            Notatka w kalendarzu
          </button>
        </div>
        {error && <div className="modal-error">{error}</div>}
        <div className="modal-body">
          <div className="form-grid" style={{ gridTemplateColumns: '1fr' }}>
            {activeTab === 'installation' && (
              <label>Numer zlecenia (*wymagany)
                <input
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value)}
                  placeholder="np. TK/26/06/001"
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                />
              </label>
            )}
            {activeTab === 'note' && (
              <>
                <label>Tytuł (*wymagany)
                  <input
                    value={noteTitle}
                    onChange={(e) => setNoteTitle(e.target.value)}
                    placeholder="np. Odbiór materiału"
                    maxLength={200}
                    autoFocus
                  />
                </label>
                <label>Kraj (*wymagany)
                  <select
                    value={noteCountry}
                    onChange={(e) => setNoteCountry(e.target.value)}
                    aria-label="Kraj notatki"
                    disabled={saving}
                  >
                    <option value="PL">PL</option>
                    <option value="DE">DE</option>
                  </select>
                </label>
                {canViewSettlement && <label>Wartość USL (opcjonalna)
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={noteUslAmount}
                    onChange={(e) => setNoteUslAmount(e.target.value)}
                    placeholder="0,00"
                    aria-label="Wartość USL notatki"
                  />
                </label>}
              </>
            )}
            <label>{activeTab === 'note' ? 'Data wpisu' : 'Data montażu'} (*wymagana)
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </label>
            <label>Ekipa (opcjonalna)
              <select
                value={teamId}
                onChange={(e) => setTeamId(e.target.value)}
                disabled={saving}
                aria-label="Ekipa dla wpisu"
              >
                <option value="">Nieprzypisane</option>
                {teams.map((team) => (
                  <option key={team.id} value={team.id}>{team.name}</option>
                ))}
              </select>
            </label>
            <label>{activeTab === 'note' ? 'Treść notatki (*wymagana)' : 'Notatka (opcjonalna)'}
              <textarea
                rows={activeTab === 'note' ? 5 : 3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={activeTab === 'note' ? 'Wpisz treść widoczną na kalendarzu…' : 'Notatka…'}
                maxLength={4000}
              />
            </label>
          </div>
          <p style={{ marginTop: '12px', fontSize: '12px', color: '#64748b' }}>
            {activeTab === 'note'
              ? 'Notatka pojawi się bez numeru zlecenia w wybranym miejscu kalendarza. Można ją później edytować, przenieść lub usunąć.'
              : 'Dodatkowe informacje można uzupełnić później, otwierając zlecenie na kalendarzu. Szybki montaż jest oznaczony kolorem pomarańczowym.'}
          </p>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose} disabled={saving}>Anuluj</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Zapisywanie…' : 'Dodaj'}
          </button>
        </div>
    </div>
  );
}

function calendarNoteDetailValues(installation) {
  return {
    contractor: installation.contractor || '',
    state: installation.state || '',
    address: installation.address || '',
    wh: installation.wh || '',
    realization: installation.realization || '',
    rusztowanie: !!installation.rusztowanie,
    drabina: !!installation.drabina,
    pajaczek: !!installation.pajaczek,
    manipulator: !!installation.manipulator,
    agregat: !!installation.agregat,
    dodatkowy_bus: !!installation.dodatkowy_bus,
    tir: !!installation.tir,
    overhead_percentage: installation.overhead_percentage ?? 5,
    distribution_percentage: installation.distribution_percentage ?? 25,
    settlement_mode: installation.settlement_mode || 'weights',
    manual_settlement_amounts: installation.manual_settlement_amounts || {},
  };
}

function CalendarNoteModal({ installation, teams = [], installers = [], canViewSettlement = false, canEditSettlement = canViewSettlement, onClose, onSaved, onDeleted }) {
  const [currentInstallation, setCurrentInstallation] = useState(installation);
  const noteUslItem = (installation.items || []).find(
    (item) => (item.product_type || '').trim().toUpperCase() === 'USL'
  );
  const [title, setTitle] = useState(
    installation.calendar_note_title || installation.notes || ''
  );
  const [country, setCountry] = useState(installation.calendar_note_country || 'PL');
  const [uslAmount, setUslAmount] = useState(
    noteUslItem?.net_price_pln == null ? '' : String(noteUslItem.net_price_pln)
  );
  const [notes, setNotes] = useState(installation.notes || '');
  const initialDates = installationDates(installation);
  const initialCalendarDate = new Date(`${initialDates[0] || toISODate(new Date())}T12:00:00`);
  const [selectedDates, setSelectedDates] = useState(initialDates);
  const [calMonth, setCalMonth] = useState(initialCalendarDate.getMonth());
  const [calYear, setCalYear] = useState(initialCalendarDate.getFullYear());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deletePromptOpen, setDeletePromptOpen] = useState(false);
  const [details, setDetails] = useState(() => calendarNoteDetailValues(installation));
  const [assignmentDrafts, setAssignmentDrafts] = useState({});
  const [addMemberDay, setAddMemberDay] = useState(null);
  const [assignDay, setAssignDay] = useState('');
  const [assignTeamId, setAssignTeamId] = useState('');
  const [assignInstallerId, setAssignInstallerId] = useState('');
  const [settlement, setSettlement] = useState(null);
  const [settlementLoading, setSettlementLoading] = useState(false);
  const [generalInfoExpanded, setGeneralInfoExpanded] = useState(true);
  const [assignmentsExpanded, setAssignmentsExpanded] = useState(false);
  const [settlementExpanded, setSettlementExpanded] = useState(false);

  useEffect(() => {
    if (!canViewSettlement) return;
    let active = true;
    setSettlementLoading(true);
    installationService.getInstallationSettlement(
      currentInstallation.id,
      details.overhead_percentage,
      details.distribution_percentage
    ).then((result) => {
      if (active) setSettlement(result);
    }).catch(() => {
      if (active) setSettlement(null);
    }).finally(() => {
      if (active) setSettlementLoading(false);
    });
    return () => { active = false; };
  }, [canViewSettlement, currentInstallation.id, currentInstallation.assignments,
    details.overhead_percentage, details.distribution_percentage]);

  async function refreshNoteAssignments() {
    const updated = await installationService.getInstallation(installation.id);
    const note = {
      ...updated,
      calendar_note_group_id: installation.calendar_note_group_id,
      calendar_note_group_count: installation.calendar_note_group_count,
    };
    setCurrentInstallation(note);
    onSaved(note);
  }

  async function addNoteAssignment(day, selectedAssignment = null) {
    const draft = selectedAssignment || assignmentDrafts[day] || {};
    if (!draft.teamId && !draft.installerId) return;
    setSaving(true);
    setError('');
    try {
      await installationService.createAssignment(installation.id, {
        team_id: draft.teamId ? Number(draft.teamId) : null,
        installer_id: draft.installerId ? Number(draft.installerId) : null,
        assigned_date: day,
      });
      await refreshNoteAssignments();
      setAssignmentDrafts((current) => ({ ...current, [day]: {} }));
      setAddMemberDay(null);
      if (selectedAssignment) {
        setAssignDay('');
        setAssignTeamId('');
        setAssignInstallerId('');
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Nie udało się przypisać ekipy lub pracownika.');
    } finally {
      setSaving(false);
    }
  }

  async function removeNoteAssignment(assignmentId) {
    setSaving(true);
    setError('');
    try {
      await installationService.deleteAssignment(assignmentId);
      await refreshNoteAssignments();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Nie udało się usunąć przypisania.');
    } finally {
      setSaving(false);
    }
  }

  async function removeNoteTeamAssignments(assignments) {
    setSaving(true);
    setError('');
    try {
      for (const assignment of assignments) {
        await installationService.deleteAssignment(assignment.id);
      }
      await refreshNoteAssignments();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Nie udało się usunąć przypisań ekipy.');
      await refreshNoteAssignments();
    } finally {
      setSaving(false);
    }
  }

  function renderNoteSettlementTable(workers) {
    return (
      <table className="settlement-table">
        <thead>
          <tr>
            <th>Pracownik</th>
            <th>Stanowisko</th>
            <th>Waga</th>
            <th>Udział</th>
            <th>Kwota</th>
          </tr>
        </thead>
        <tbody>
          {workers.map((worker, index) => (
            <tr key={`${worker.installer_id}-${index}`}>
              <td>{worker.installer_name}</td>
              <td>{worker.position || '—'}</td>
              <td className="numeric-cell">{details.settlement_mode === 'manual' ? '—' : worker.position_weight ?? '—'}</td>
              <td className="numeric-cell">{Number(worker.share_percentage || 0).toFixed(2)}%</td>
              <td className="numeric-cell settlement-amount">
                {details.settlement_mode === 'manual' ? (
                  <>
                    <input className="item-input numeric-input" inputMode="decimal"
                      aria-label={`Kwota dla ${worker.installer_name}`}
                      value={details.manual_settlement_amounts[String(worker.installer_id)] ?? settlementBaseAmount(worker)}
                      onChange={(event) => setDetails((current) => ({
                        ...current,
                        manual_settlement_amounts: {
                          ...current.manual_settlement_amounts,
                          [String(worker.installer_id)]: event.target.value,
                        },
                      }))} disabled={!canEditSettlement} />
                    {Number(worker.additional_amount || 0) > 0 && (
                      <small className="settlement-manual-final-amount">
                        Razem: {Number(worker.amount || 0).toFixed(2)} PLN
                      </small>
                    )}
                  </>
                ) : <SettlementAmountValue item={worker} />}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  function changeMonth(offset) {
    const next = new Date(calYear, calMonth + offset, 1);
    setCalMonth(next.getMonth());
    setCalYear(next.getFullYear());
  }

  function toggleDate(dateStr) {
    setSelectedDates((current) => (
      current.includes(dateStr)
        ? current.filter((day) => day !== dateStr)
        : [...current, dateStr].sort()
    ));
  }

  function renderDateCalendar() {
    const firstDay = new Date(calYear, calMonth, 1);
    const lastDay = new Date(calYear, calMonth + 1, 0);
    const days = [];
    let current = startOfWeek(firstDay);
    while (current <= lastDay || days.length % 7 !== 0) {
      days.push(new Date(current));
      current = addDays(current, 1);
      if (days.length >= 42) break;
    }

    return (
      <div className="split-calendar calendar-note-date-picker">
        <div className="split-calendar-header">
          <button type="button" className="btn-nav" onClick={() => changeMonth(-1)}>‹</button>
          <span className="split-calendar-month">{POLISH_MONTHS[calMonth]} {calYear}</span>
          <button type="button" className="btn-nav" onClick={() => changeMonth(1)}>›</button>
        </div>
        <div className="vac-cal-grid">
          {POLISH_DAYS_SHORT.map((dayName) => <div key={dayName} className="vac-cal-head">{dayName}</div>)}
          {days.map((day) => {
            const dateStr = toISODate(day);
            const isCurrentMonth = day.getMonth() === calMonth;
            const isSelected = selectedDates.includes(dateStr);
            return (
              <div
                key={dateStr}
                className={`vac-cal-day ${isCurrentMonth ? '' : 'other-month'} ${isSelected ? 'selected-date' : ''}`}
                data-date={dateStr}
                role="button"
                tabIndex={isCurrentMonth ? 0 : -1}
                aria-pressed={isSelected}
                onClick={() => isCurrentMonth && toggleDate(dateStr)}
                onKeyDown={(event) => {
                  if (isCurrentMonth && (event.key === 'Enter' || event.key === ' ')) {
                    event.preventDefault();
                    toggleDate(dateStr);
                  }
                }}
                style={{ cursor: isCurrentMonth ? 'pointer' : 'default' }}
              >
                {day.getDate()}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  async function handleSave(event) {
    event.preventDefault();
    if (!title.trim()) { setError('Tytuł notatki jest wymagany'); return; }
    if (!notes.trim()) { setError('Treść notatki jest wymagana'); return; }
    if (selectedDates.length === 0) { setError('Wybierz co najmniej jeden dzień'); return; }
    const parsedUslAmount = uslAmount === ''
      ? 0
      : Number(String(uslAmount).replace(',', '.'));
    if (canEditSettlement && (!Number.isFinite(parsedUslAmount) || parsedUslAmount < 0)) {
      setError('Wartość USL musi być liczbą równą lub większą od 0');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const originalDetails = calendarNoteDetailValues(installation);
      const changedDetails = Object.fromEntries(
        Object.entries(details).filter(([key, value]) =>
          JSON.stringify(value) !== JSON.stringify(originalDetails[key])
        )
      );
      const {
        overhead_percentage, distribution_percentage,
        settlement_mode, manual_settlement_amounts, ...generalDetails
      } = changedDetails;
      const cleanedManualAmounts = manual_settlement_amounts === undefined ? undefined
        : Object.fromEntries(Object.entries(manual_settlement_amounts).map(([installerId, value]) => [
          installerId, value === '' ? 0 : Number(String(value).replace(',', '.')),
        ]));
      if (cleanedManualAmounts && Object.values(cleanedManualAmounts).some((amount) => !Number.isFinite(amount) || amount < 0)) {
        setError('Kwoty ręczne muszą być liczbami równymi lub większymi od 0');
        setSaving(false);
        return;
      }
      const result = await installationService.updateInstallationCalendar(installation.id, {
        calendar_note_title: title.trim(),
        calendar_note_country: country,
        notes: notes.trim(),
        ...generalDetails,
        ...(canEditSettlement ? {
          usl_amount: parsedUslAmount,
          ...(overhead_percentage !== undefined ? { overhead_percentage } : {}),
          ...(distribution_percentage !== undefined ? { distribution_percentage } : {}),
          ...(settlement_mode !== undefined ? { settlement_mode } : {}),
          ...(cleanedManualAmounts !== undefined ? { manual_settlement_amounts: cleanedManualAmounts } : {}),
        } : {}),
        planned_dates: [...selectedDates].sort(),
      });
      onSaved({
        ...result.installation,
        calendar_note_group_id: installation.calendar_note_group_id,
        calendar_note_group_count: installation.calendar_note_group_count,
      });
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Błąd zapisu notatki');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(scope = null) {
    const duplicateCount = installation.calendar_note_group_count || 1;
    if (installation.calendar_note_group_id && duplicateCount > 1 && scope === null) {
      setDeletePromptOpen(true);
      return;
    }
    if (scope === null && !window.confirm('Usunąć tę notatkę z kalendarza?')) return;
    setSaving(true);
    setError('');
    try {
      const deletionScope = scope || 'single';
      await installationService.deleteInstallation(
        installation.id,
        deletionScope,
        installation._calendarNoteOccurrence || null
      );
      onDeleted({
        installationId: installation.id,
        groupId: installation.calendar_note_group_id,
        scope: deletionScope,
      });
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Błąd usuwania notatki');
      setSaving(false);
    }
  }

  return (
      <form className="modal-box installation-details-drawer calendar-note-details-modal"
        aria-label={`Szczegóły notatki ${title}`} noValidate onSubmit={handleSave}>
        <div className="modal-header">
          <h3 title={title}>Notatka: {title}</h3>
          <button type="button" className="modal-close" onClick={onClose} disabled={saving}>✕</button>
        </div>
        {error && <div className="modal-error" role="alert">{error}</div>}
        <div className="modal-body">
          <section className="installation-general-section">
            <button type="button" className="installation-general-toggle"
              aria-expanded={generalInfoExpanded}
              onClick={() => setGeneralInfoExpanded((expanded) => !expanded)}>
              <span>Informacje ogólne</span>
              {!generalInfoExpanded && <span className="installation-general-summary">{title}</span>}
              <span className="installation-general-chevron" aria-hidden="true">{generalInfoExpanded ? '−' : '+'}</span>
            </button>
            {generalInfoExpanded && <div className="installation-general-content">
            <div className="form-grid installation-general-grid calendar-note-details-grid">
            <label className="general-field general-field--contractor">Tytuł (*wymagany)
              <input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={200} autoFocus required />
            </label>
            <label className="general-field general-field--order">Kraj (*wymagany)
              <select value={country} onChange={(event) => setCountry(event.target.value)} aria-label="Kraj notatki" required>
                <option value="PL">PL</option>
                <option value="DE">DE</option>
              </select>
            </label>
            {[
              ['contractor', 'Kontrahent'],
              ['state', 'STAN'],
              ['address', 'Adres'],
              ['wh', 'WH'],
              ['realization', 'Realizacja zlecenia'],
            ].map(([key, label]) => (
              <label key={key} className={`general-field ${key === 'address' ? 'general-field--address' : key === 'contractor' ? 'general-field--contractor' : 'general-field--date'}`}>{label}
                <input value={details[key]} onChange={(event) => setDetails({ ...details, [key]: event.target.value })} />
              </label>
            ))}
            <label className="calendar-note-details-wide">Treść notatki (*wymagana)
              <textarea rows={6} value={notes} onChange={(event) => setNotes(event.target.value)} maxLength={4000} required />
            </label>
            <fieldset className="installation-days-picker installation-days-picker--expanded calendar-note-days-picker">
              <legend>Dni wyświetlania notatki (*wymagany co najmniej jeden)</legend>
              {renderDateCalendar()}
              <p style={{ margin: '10px 0 0', fontSize: '13px', color: '#64748b' }}>
                Wybrano: <strong>{selectedDates.length}</strong> {selectedDates.length === 1 ? 'dzień' : 'dni'}
              </p>
            </fieldset>
          </div>
          </div>}
          </section>

            <div className="equipment-flags">
              <span className="equipment-flags-label">Wyposażenie / logistyka:</span>
              {[
                ['rusztowanie', 'Rusztowanie'],
                ['drabina', 'Drabina'],
                ['pajaczek', 'Pajączek'],
                ['manipulator', 'Manipulator'],
                ['agregat', 'Agregat'],
                ['dodatkowy_bus', 'Dodatkowy bus'],
                ['tir', 'Tir'],
              ].map(([key, label]) => (
                <label key={key} className="equipment-flag-item">
                  <input type="checkbox" checked={details[key]}
                    onChange={(event) => setDetails({ ...details, [key]: event.target.checked })} />
                  {label}
                </label>
              ))}
            </div>

          <section className="assignments-section assignments-section--compact">
            <button type="button" className="section-collapse-toggle" aria-expanded={assignmentsExpanded}
              onClick={() => setAssignmentsExpanded((expanded) => !expanded)}>
              <span>Przypisania per dzień</span>
              <span aria-hidden="true">{assignmentsExpanded ? '−' : '+'}</span>
            </button>
            {assignmentsExpanded && <div>
            {installationDates(currentInstallation).map((day) => {
              const dayAssignments = (currentInstallation.assignments || []).filter((assignment) => assignment.assigned_date === day);
              const draft = assignmentDrafts[day] || {};
              const teamGroups = dayAssignments.filter((assignment) => assignment.team_id).reduce((groups, assignment) => {
                (groups[assignment.team_id] ||= []).push(assignment);
                return groups;
              }, {});
              const dayTeams = Object.keys(teamGroups).map((teamId) => teams.find((team) => team.id === Number(teamId))).filter(Boolean);
              const individualAssignments = dayAssignments.filter((assignment) => !assignment.team_id);
              const dateObj = new Date(`${day}T12:00:00`);
              const dayLabel = `${dateObj.getDate()} ${POLISH_MONTHS[dateObj.getMonth()]} ${dateObj.getFullYear()}`;
              const dayName = POLISH_DAYS_SHORT[dateObj.getDay() === 0 ? 6 : dateObj.getDay() - 1];
              return (
                <div className="day-assignment-block" key={day}>
                  <div className="day-assignment-header">
                    <span className="day-assignment-label">{dayName}, {dayLabel}</span>
                    {dayAssignments.length === 0 && <span className="day-assignment-empty">Brak przypisań</span>}
                  </div>
                  {dayAssignments.length > 0 && <ul className="assignment-list">
                    {Object.entries(teamGroups).map(([teamId, assignments]) => {
                      const team = teams.find((item) => item.id === Number(teamId));
                      const hasFullTeam = assignments.some((assignment) => !assignment.installer_id);
                      const teamMembers = hasFullTeam ? (team?.members || []).map((member) => ({
                        id: member.id,
                        name: `${member.first_name} ${member.last_name}`,
                        katB: member.kat_b,
                        assignmentId: null,
                      })) : [];
                      const assignedMembers = assignments.filter((assignment) => assignment.installer_id).map((assignment) => {
                        const installer = installers.find((item) => item.id === assignment.installer_id);
                        return {
                          id: assignment.installer_id,
                          name: assignment.installer_name || (installer ? `${installer.first_name} ${installer.last_name}` : ''),
                          katB: installer?.kat_b,
                          assignmentId: assignment.id,
                        };
                      });
                      const members = [...teamMembers, ...assignedMembers.filter((member) => !teamMembers.some((item) => item.id === member.id))];
                      return (
                        <li className="team-assignment-expanded" key={teamId}>
                          <div className="team-assignment-row">
                            <strong className="team-assignment-name">
                              {team?.name || assignments[0].team_name || 'Ekipa'}
                              <span className="team-assignment-count">{members.length} {members.length === 1 ? 'osoba' : members.length >= 2 && members.length <= 4 ? 'osoby' : 'osób'}</span>
                            </strong>
                            <button type="button" className="btn-icon-remove" title="Usuń całą ekipę z tego dnia"
                              aria-label={`Usuń ekipę ${team?.name || assignments[0].team_name || ''} z ${day}`}
                              disabled={saving} onClick={() => removeNoteTeamAssignments(assignments)}>✕</button>
                          </div>
                          {members.length > 0 && <ul className="team-members-list">
                            {members.map((member) => <li className="team-member-item" key={member.id}>
                              <span className="member-name" title={member.name}>{member.name}</span>
                              {member.katB && <span className="member-katb-badge">Kat.B</span>}
                              {member.assignmentId && <button type="button" className="btn-icon-remove"
                                title={`Usuń ${member.name} z tego dnia`} disabled={saving}
                                onClick={() => removeNoteAssignment(member.assignmentId)}>✕</button>}
                            </li>)}
                          </ul>}
                        </li>
                      );
                    })}
                    {individualAssignments.map((assignment) => {
                      const installer = installers.find((item) => item.id === assignment.installer_id);
                      const name = assignment.installer_name || (installer ? `${installer.first_name} ${installer.last_name}` : 'Pracownik');
                      return <li className="individual-assignment-row" key={assignment.id}>
                        <span>{name}</span>
                        <span className="individual-assignment-actions">
                          <button type="button" className="btn-icon-remove" title={`Usuń ${name} z tego dnia`}
                            disabled={saving} onClick={() => removeNoteAssignment(assignment.id)}>✕</button>
                        </span>
                      </li>;
                    })}
                  </ul>}
                  {addMemberDay === day ? (
                    <div className="day-add-member-inline">
                      <select className="assign-select" aria-label={`Pracownik na ${day}`} value={draft.installerId || ''}
                        onChange={(event) => setAssignmentDrafts({ ...assignmentDrafts, [day]: { ...draft, installerId: event.target.value } })}>
                        <option value="">-- Wybierz osobę --</option>
                        {installers.map((installer) => <option key={installer.id} value={installer.id}>
                          {installer.first_name} {installer.last_name}
                        </option>)}
                      </select>
                      {dayTeams.length > 0 && <select className="assign-select" aria-label={`Ekipa na ${day}`} value={draft.teamId || ''}
                        onChange={(event) => setAssignmentDrafts({ ...assignmentDrafts, [day]: { ...draft, teamId: event.target.value } })}>
                        <option value="">-- Wybierz ekipę --</option>
                        {dayTeams.map((team) => <option key={team.id} value={team.id}>{team.name}</option>)}
                      </select>}
                      <button type="button" className="btn-primary btn-sm" disabled={saving || !draft.installerId || (dayTeams.length > 0 && !draft.teamId)}
                        onClick={() => addNoteAssignment(day)}>Dodaj</button>
                      <button type="button" className="btn-secondary btn-sm" onClick={() => setAddMemberDay(null)}>Anuluj</button>
                    </div>
                  ) : <button type="button" className="btn-add-member" onClick={() => {
                    setAddMemberDay(day);
                    setAssignmentDrafts((current) => ({ ...current, [day]: { installerId: '', teamId: dayTeams.length === 1 ? String(dayTeams[0].id) : '' } }));
                  }}>+ {dayTeams.length > 0 ? 'Dodaj osobę do ekipy' : 'Dodaj osobę'}</button>}
                </div>
              );
            })}
            <div className="assign-row">
              <select className="assign-select" aria-label="Dzień przypisania notatki" value={assignDay}
                onChange={(event) => setAssignDay(event.target.value)}>
                <option value="">-- Wybierz dzień --</option>
                {installationDates(currentInstallation).map((day) => <option key={day} value={day}>{formatDayLabel(day)}</option>)}
              </select>
              <select className="assign-select" aria-label="Ekipa do notatki" value={assignTeamId}
                onChange={(event) => { setAssignTeamId(event.target.value); setAssignInstallerId(''); }}>
                <option value="">-- Wybierz ekipę --</option>
                {teams.filter((team) => !team.is_special_truck).map((team) => (
                  <option key={team.id} value={team.id}>{team.name}</option>
                ))}
              </select>
              <span className="calendar-note-assignment-or">lub</span>
              <select className="assign-select" aria-label="Pracownik do notatki" value={assignInstallerId}
                onChange={(event) => { setAssignInstallerId(event.target.value); setAssignTeamId(''); }}>
                <option value="">-- Wybierz pracownika --</option>
                {installers.map((installer) => <option key={installer.id} value={installer.id}>
                  {installer.first_name} {installer.last_name}
                </option>)}
              </select>
              <button type="button" className="btn-primary" disabled={saving || !assignDay || (!assignTeamId && !assignInstallerId)}
                onClick={() => addNoteAssignment(assignDay, { teamId: assignTeamId, installerId: assignInstallerId })}>
                {saving ? 'Przypisywanie...' : 'Przypisz'}
              </button>
            </div>
            </div>}
          </section>

          {canViewSettlement && (
            <>
              <section className="settlement-parameters">
                <h4>Parametry rozliczenia</h4>
                <div className="settlement-parameters-grid">
                  <label><span>Wartość USL</span>
                    <input type="number" min="0" step="0.01"
                      value={canEditSettlement ? uslAmount : settlement?.total_usl ?? ''}
                      onChange={(event) => setUslAmount(event.target.value)} aria-label="Wartość USL notatki"
                      disabled={!canEditSettlement} />
                  </label>
                  <label><span>Narzut %</span>
                    <input type="number" min="0" max="100" step="1" value={details.overhead_percentage}
                      onChange={(event) => setDetails({ ...details, overhead_percentage: Number(event.target.value) })}
                      disabled={!canEditSettlement} />
                  </label>
                  <label><span>Procent do podziału %</span>
                    <input type="number" min="0" max="100" step="1" value={details.distribution_percentage}
                      onChange={(event) => setDetails({ ...details, distribution_percentage: Number(event.target.value) })}
                      disabled={!canEditSettlement} />
                  </label>
                </div>
                <p className="settlement-parameters-formula">
                  Formuła: Y = SUMA_USL × narzut%, X = SUMA_USL - Y, Suma do podziału = X × procent%
                </p>
              </section>
              <section className={`settlement-section ${settlementExpanded ? '' : 'settlement-section--collapsed'}`}>
                <button type="button" className="section-collapse-toggle section-collapse-toggle--settlement"
                  aria-expanded={settlementExpanded}
                  onClick={() => setSettlementExpanded((expanded) => !expanded)}>
                  <span>💰 Rozliczenie zlecenia</span>
                  <span aria-hidden="true">{settlementExpanded ? '−' : '+'}</span>
                </button>
                {settlementExpanded && <>
                  {settlementLoading && <div className="settlement-loading">Ładowanie rozliczenia...</div>}
                  {settlement && <>
                    <div className="settlement-summary">
                      <div className="settlement-row"><span>Suma USL:</span><strong>{Number(settlement.total_usl || 0).toFixed(2)} PLN</strong></div>
                      <div className="settlement-row"><span>Narzut ({settlement.overhead_percentage ?? details.overhead_percentage}%):</span>
                        <span>− {Number(settlement.overhead_amount || 0).toFixed(2)} PLN</span></div>
                      <div className="settlement-row"><span>Podstawa:</span><span>{Number(settlement.base_amount || 0).toFixed(2)} PLN</span></div>
                      <div className="settlement-row"><span>Do podziału ({settlement.distribution_percentage ?? details.distribution_percentage}%):</span>
                        <strong className="settlement-highlight">{Number(settlement.total_to_distribute || 0).toFixed(2)} PLN</strong></div>
                    </div>
                    <SavedFinalAdditionsPanel
                      additions={settlement.saved_additional_amounts}
                    />
                    <div className="calendar-note-settlement-mode">
                      <span>Sposób rozliczenia:</span>
                      <label><input type="radio" name="noteSettlementMode" checked={details.settlement_mode === 'weights'}
                        onChange={() => setDetails({ ...details, settlement_mode: 'weights' })}
                        disabled={!canEditSettlement} /> według wag</label>
                      <label><input type="radio" name="noteSettlementMode" checked={details.settlement_mode === 'manual'}
                        onChange={() => setDetails({ ...details, settlement_mode: 'manual' })}
                        disabled={!canEditSettlement} /> manualne kwoty</label>
                    </div>
                    <div className="settlement-workers">
                      <h5>Podział między pracowników:</h5>
                      {settlement.days?.length > 1 ? settlement.days.map((day, index) => (
                        <div className="calendar-note-settlement-day" key={`${day.date}-${day.team_id ?? index}`}>
                          <div className="calendar-note-settlement-day-heading">
                            <strong>📅 {formatDayLabel(day.date)}{day.team_name ? ` · ${day.team_name}` : ''}</strong>
                            <span>Do podziału w tym dniu: <strong>{Number(day.total_to_distribute || 0).toFixed(2)} PLN</strong></span>
                          </div>
                          {renderNoteSettlementTable(day.installers || [])}
                        </div>
                      )) : renderNoteSettlementTable(settlement.installers || [])}
                    </div>
                  </>}
                </>}
              </section>
            </>
          )}
        </div>
        {deletePromptOpen && (
          <div className="team-comment-conversion-prompt team-comment-deletion-prompt" role="dialog" aria-label="Zakres usuwania notatki">
            <div className="team-comment-conversion-prompt__copy">
              <span className="team-comment-conversion-prompt__icon" aria-hidden="true">!</span>
              <div>
                <strong>Notatka występuje {installation.calendar_note_group_count} razy</strong>
                <p>Wybierz, czy usunąć tylko wybraną notatkę, czy wszystkie jej duplikaty.</p>
              </div>
            </div>
            <div className="team-comment-conversion-prompt__actions">
              <button
                type="button"
                className="btn-secondary team-comment-conversion-prompt__cancel"
                onClick={() => setDeletePromptOpen(false)}
                disabled={saving}
              >
                Anuluj
              </button>
              <button
                type="button"
                className="btn-secondary team-comment-conversion-prompt__single"
                onClick={() => handleDelete('single')}
                disabled={saving}
              >
                Tylko tę notatkę
              </button>
              <button
                type="button"
                className="btn-danger team-comment-deletion-prompt__all"
                onClick={() => handleDelete('group')}
                disabled={saving}
              >
                Usuń wszystkie ({installation.calendar_note_group_count})
              </button>
            </div>
          </div>
        )}
        <div className="modal-footer installation-details-actions calendar-note-details-actions">
          <button type="button" className="btn-danger" onClick={() => handleDelete()} disabled={saving}>Usuń</button>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>Anuluj</button>
          <button type="submit" className="btn-primary" disabled={saving || selectedDates.length === 0}>
            {saving ? 'Zapisywanie…' : 'Zapisz'}
          </button>
        </div>
      </form>
  );
}

// ──────────────────────────────────────────────────────────────
//  Tab 1 – Calendar (Planowanie)
// ──────────────────────────────────────────────────────────────
export function PlanningTab({ canManage, canViewSettlement, isViewOnly, onBreadcrumbChange, onBreadcrumbReset }) {
  const canViewOrderSettlement = canManage || canViewSettlement;
  // Filter available views based on permissions
  const availableViews = isViewOnly ? [WEEKLY_VIEW] : CALENDAR_VIEWS;
  const [view, setView] = useState(WEEKLY_VIEW);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [printDay, setPrintDay] = useState(toISODate(new Date()));
  const [printDateInputKey, setPrintDateInputKey] = useState(0);
  const printDateOpenedByPointer = useRef(false);
  const [installations, setInstallations] = useState([]);
  const [unscheduledOrders, setUnscheduledOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calendarReady, setCalendarReady] = useState(false);
  const [planningError, setPlanningError] = useState('');
  const [planningNotice, setPlanningNotice] = useState('');
  const pendingSchedules = useRef(new Set());
  const teamRefresh = useRef(null);
  const mutationVersions = useRef(new Map());
  const mutationVersion = useRef(0);
  const calendarReadId = useRef(0);
  const unscheduledReadId = useRef(0);
  const [hideSundays, setHideSundays] = useState(true);
  const [showAddresses, setShowAddresses] = useState(true);
  const [showWh, setShowWh] = useState(true);
  const [editInstallation, setEditInstallation] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [calendarNoteInstallation, setCalendarNoteInstallation] = useState(null);
  const [additionalWorkInstallation, setAdditionalWorkInstallation] = useState(null);
  const [showQuickAddModal, setShowQuickAddModal] = useState(false);
  const [showPrintMenu, setShowPrintMenu] = useState(false);
  const [printBusy, setPrintBusy] = useState(false);
  const [printError, setPrintError] = useState('');
  const [printDayData, setPrintDayData] = useState({ day: null, installations: null });
  const printDayReadId = useRef(0);
  const [orderSearch, setOrderSearch] = useState('');
  const [orderSearchResults, setOrderSearchResults] = useState([]);
  const [orderSearchStatus, setOrderSearchStatus] = useState(null);
  const [orderSearchBusy, setOrderSearchBusy] = useState(false);
  const [orderSearchFocused, setOrderSearchFocused] = useState(false);
  const [highlightedInstallationId, setHighlightedInstallationId] = useState(null);
  const [installers, setInstallers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [vacations, setVacations] = useState([]);
  const [scheduleRequest, setScheduleRequest] = useState(null);
  const [scheduleRequestBusy, setScheduleRequestBusy] = useState(false);
  const [scheduleRequestError, setScheduleRequestError] = useState('');
  const dragRef = useRef(null);
  const dragPointerYRef = useRef(null);
  const dragScrollFrameRef = useRef(null);
  const dayDropResolverRef = useRef(null);
  const [dayDropQuestion, setDayDropQuestion] = useState(null);
  const pendingScrollRestoreRef = useRef(null);
  const tirTeamName = findTirTeam(teams)?.name || 'TIR';

  
  // Split installation modal state
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [splitInstallation, setSplitInstallation] = useState(null);
  const [splitWarningMessage, setSplitWarningMessage] = useState('');
  const [splitTotalPerimeter, setSplitTotalPerimeter] = useState(0);
  const [splitReason, setSplitReason] = useState('perimeter');
  const [splitQueue, setSplitQueue] = useState([]);
  const splitTargetDayRef = useRef(null);
  const splitTeamIdRef = useRef(null);
  const pajaczekDayConflicts = getPajaczekDayConflicts(installations);
  const printableDayInstallations = printDayData.day === printDay ? printDayData.installations : null;

  useEffect(() => {
    if (!showPrintMenu || !printDay) return undefined;
    const readId = ++printDayReadId.current;
    setPrintDayData({ day: printDay, installations: null });
    setPrintError('');
    installationService.getCalendar(printDay, printDay).then((dayInstallations) => {
      if (readId === printDayReadId.current) {
        setPrintDayData({ day: printDay, installations: getInstallationsForISODate(dayInstallations, printDay) });
      }
    }).catch(() => {
      if (readId === printDayReadId.current) setPrintError('Nie udało się pobrać zleceń dla wybranego dnia.');
    });
    return () => { printDayReadId.current += 1; };
  }, [showPrintMenu, printDay]);

  const getRange = useCallback(() => {
    if (view === WEEKLY_VIEW) {
      const mon = startOfWeek(currentDate);
      return { start: mon, end: addDays(mon, 6) };
    }
    // Month
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    return {
      start: new Date(year, month, 1),
      end: new Date(year, month + 1, 0),
    };
  }, [view, currentDate]);

  const fetchCalendar = useCallback(async () => {
    const readId = ++calendarReadId.current;
    const version = mutationVersion.current;
    setLoading(true);
    try {
      const { start, end } = getRange();
      // Expand range by a week on each side to catch multi-day events
      const data = await installationService.getCalendar(
        toISODate(addDays(start, -7)),
        toISODate(addDays(end, 7))
      );
      if (readId !== calendarReadId.current) return;
      const protectedIds = new Set([...mutationVersions.current]
        .filter(([id, changedAt]) => changedAt > version || pendingSchedules.current.has(id))
        .map(([id]) => id));
      setInstallations(current => mergeCalendarRead(data, current, protectedIds));
      setCalendarReady(true);
    } catch (e) {
      console.error('Calendar load error', e);
      if (readId === calendarReadId.current) setPlanningError('Nie udało się odświeżyć kalendarza.');
    } finally {
      if (readId === calendarReadId.current) setLoading(false);
    }
  }, [getRange]);

  const fetchUnscheduled = useCallback(async () => {
    const readId = ++unscheduledReadId.current;
    const version = mutationVersion.current;
    try {
      const data = await installationService.listUnscheduled();
      if (readId !== unscheduledReadId.current) return;
      const protectedIds = new Set([...mutationVersions.current]
        .filter(([id, changedAt]) => changedAt > version || pendingSchedules.current.has(id))
        .map(([id]) => id));
      setUnscheduledOrders(current => mergeCalendarRead(data, current, protectedIds));
    } catch (e) {
      console.error('Unscheduled fetch error', e);
    }
  }, []);

  function rememberScrollPosition() {
    pendingScrollRestoreRef.current = window.scrollY;
  }

  function restoreScrollPosition() {
    const scrollY = pendingScrollRestoreRef.current;
    if (scrollY === null || scrollY === undefined) return;
    pendingScrollRestoreRef.current = null;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo({ top: scrollY, behavior: 'auto' });
      });
    });
  }

  async function refreshPlanningData({ preserveScroll = false } = {}) {
    if (preserveScroll) rememberScrollPosition();
    await Promise.all([fetchCalendar(), fetchUnscheduled()]);
    if (preserveScroll) restoreScrollPosition();
  }

  useEffect(() => {
    fetchCalendar();
  }, [fetchCalendar]);

  useEffect(() => {
    fetchUnscheduled();
    installationService.listInstallers().then(setInstallers).catch(() => {});
    installationService.listTeams().then(setTeams).catch(() => {});
    installationService.getUpcomingVacations().then(setVacations).catch(() => {});
  }, [fetchUnscheduled]);

  useEffect(() => {
    const query = orderSearch.trim();
    if (!query) {
      setOrderSearchResults([]);
      setOrderSearchBusy(false);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setOrderSearchBusy(true);
      try {
        const response = await installationService.listInstallations(undefined, query);
        if (cancelled) return;
        const normalizedQuery = query.toLocaleLowerCase('pl-PL');
        setOrderSearchResults(
          response.filter((installation) =>
            installationMatchesSearch(installation, normalizedQuery)
          )
        );
      } catch (error) {
        if (!cancelled) {
          console.error('Order suggestions error', error);
          setOrderSearchResults([]);
        }
      } finally {
        if (!cancelled) setOrderSearchBusy(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [orderSearch]);

  useEffect(() => {
    const stopAutoScroll = () => {
      dragRef.current = null;
      dragPointerYRef.current = null;
      if (dragScrollFrameRef.current !== null) {
        cancelAnimationFrame(dragScrollFrameRef.current);
        dragScrollFrameRef.current = null;
      }
    };

    const scrollWhileDragging = () => {
      dragScrollFrameRef.current = null;
      if (!dragRef.current || dragPointerYRef.current === null) return;

      const pointerY = dragPointerYRef.current;
      const edgeSize = Math.min(160, Math.max(90, window.innerHeight * 0.2));
      const maxScrollTop = Math.max(
        0,
        document.documentElement.scrollHeight - window.innerHeight
      );
      let scrollDelta = 0;

      if (pointerY < edgeSize && window.scrollY > 0) {
        const intensity = (edgeSize - Math.max(pointerY, 0)) / edgeSize;
        scrollDelta = -Math.ceil(4 + 24 * intensity);
      } else if (pointerY > window.innerHeight - edgeSize && window.scrollY < maxScrollTop) {
        const intensity = (
          Math.min(pointerY, window.innerHeight) - (window.innerHeight - edgeSize)
        ) / edgeSize;
        scrollDelta = Math.ceil(4 + 24 * intensity);
      }

      if (scrollDelta !== 0) {
        window.scrollBy(0, scrollDelta);
        dragScrollFrameRef.current = requestAnimationFrame(scrollWhileDragging);
      }
    };

    const handleWindowDragOver = (event) => {
      if (!dragRef.current) return;
      dragPointerYRef.current = event.clientY;
      if (dragScrollFrameRef.current === null) {
        dragScrollFrameRef.current = requestAnimationFrame(scrollWhileDragging);
      }
    };

    window.addEventListener('dragover', handleWindowDragOver);
    window.addEventListener('dragend', stopAutoScroll);
    window.addEventListener('drop', stopAutoScroll);
    return () => {
      window.removeEventListener('dragover', handleWindowDragOver);
      window.removeEventListener('dragend', stopAutoScroll);
      window.removeEventListener('drop', stopAutoScroll);
      stopAutoScroll();
    };
  }, []);

  useEffect(() => {
    if (!highlightedInstallationId) return undefined;
    const timer = setTimeout(() => {
      const highlightedCard = document.querySelector(
        `[data-installation-id="${highlightedInstallationId}"]`
      );
      if (highlightedCard) {
        highlightedCard.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [highlightedInstallationId, installations, unscheduledOrders, currentDate]);

  function showFoundInstallation(installation) {
    setOrderSearch(isCalendarNote(installation)
      ? installation.calendar_note_title || installation.notes || ''
      : installation.order_number || '');
    setOrderSearchResults([]);
    setOrderSearchFocused(false);
    setHighlightedInstallationId(installation.id);

    if (installation.planned_start_date) {
      setCurrentDate(new Date(`${installation.planned_start_date}T12:00:00`));
      setOrderSearchStatus({
        type: 'success',
        text: `${isCalendarNote(installation) ? 'Notatka zaplanowana' : 'Zlecenie zaplanowane'}: ${installation.planned_start_date}`,
      });
    } else {
      setOrderSearchStatus({
        type: 'success',
        text: 'Zlecenie znajduje się w sekcji „Do zaplanowania”.',
      });
    }
  }

  async function searchOrder() {
    const query = orderSearch.trim();
    if (!query) return;

    setOrderSearchBusy(true);
    setOrderSearchStatus(null);
    setOrderSearchResults([]);
    setHighlightedInstallationId(null);
    try {
      const response = await installationService.listInstallations(undefined, query);
      const normalizedQuery = query.toLocaleLowerCase('pl-PL');
      const results = response.filter((installation) =>
        installationMatchesSearch(installation, normalizedQuery)
      );
      if (results.length === 1) {
        showFoundInstallation(results[0]);
      } else if (results.length > 1) {
        setOrderSearchResults(results);
        setOrderSearchFocused(true);
        setOrderSearchStatus({ type: 'info', text: 'Wybierz zlecenie lub notatkę z listy.' });
      } else {
        setOrderSearchStatus({ type: 'error', text: 'Nie znaleziono pasującego zlecenia.' });
      }
    } catch (error) {
      console.error('Order search error', error);
      setOrderSearchStatus({ type: 'error', text: 'Nie udało się wyszukać zlecenia.' });
    } finally {
      setOrderSearchBusy(false);
    }
  }

  function navigate(direction) {
    const d = new Date(currentDate);
    if (view === WEEKLY_VIEW) d.setDate(d.getDate() + 7 * direction);
    else d.setMonth(d.getMonth() + direction);
    setCurrentDate(d);
  }

  function getCurrentLabel() {
    if (view === WEEKLY_VIEW) {
      const mon = startOfWeek(currentDate);
      const sun = addDays(mon, 6);
      return `${mon.getDate()} ${POLISH_MONTHS[mon.getMonth()]} – ${sun.getDate()} ${POLISH_MONTHS[sun.getMonth()]} ${sun.getFullYear()}`;
    }
    return `${POLISH_MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
  }

  function openEdit(installation, occurrence = null) {
    if (pendingSchedules.current.has(installation.id)) return;
    if (isCalendarNote(installation)) {
      setCalendarNoteInstallation({
        ...installation,
        _calendarNoteOccurrence: occurrence,
      });
      return;
    }
    setEditInstallation(installation);
    setShowModal(true);
    if (onBreadcrumbChange) onBreadcrumbChange(installation);
  }

  function openAdditionalWork(installation) {
    setAdditionalWorkInstallation(installation);
  }

  async function handlePrintDay(variant) {
    if (!printDay || printBusy) return;
    if (variant === 'amounts' && !canViewSettlement) return;
    setPrintBusy(true);
    setPrintError('');
    try {
      const dayInstallations = printableDayInstallations
        ?? getInstallationsForISODate(await installationService.getCalendar(printDay, printDay), printDay);
      if (variant !== 'amounts') {
        printDailyInstallationSummary(dayInstallations, teams, installers, printDay, variant);
        setShowPrintMenu(false);
        return;
      }
      const uniqueInstallations = [...new Map(dayInstallations.map((installation) => [installation.id, installation])).values()];
      const settlements = await Promise.all(uniqueInstallations.map(async (installation) => {
        const result = await installationService.getInstallationSettlement(installation.id);
        if (!result) throw new Error('Brak rozliczenia zlecenia');
        return [installation.id, result];
      }));
      printDailyInstallationAmounts(dayInstallations, teams, installers, printDay, Object.fromEntries(settlements));
      setShowPrintMenu(false);
    } catch {
      setPrintError('Nie udało się przygotować wydruku dla wybranego dnia. Spróbuj ponownie.');
    } finally {
      setPrintBusy(false);
    }
  }

  function handleDragStart(e, installations, source = null) {
    if (dayDropQuestion) {
      e.preventDefault();
      return;
    }
    const dragged = Array.isArray(installations) ? installations : [installations];
    if (dragged.some(item => pendingSchedules.current.has(item.id))) {
      e.preventDefault();
      return;
    }
    // installations może być array (z UnscheduledOrdersPanel) lub pojedynczy obiekt (z kalendarza)
    dragRef.current = {
      installations: dragged,
      source,
    };
    e.dataTransfer.effectAllowed = 'move';
  }

  function openScheduleRequest(installation) {
    const isDad = isDadOrder(installation);
    const requiredTeam = isDeliveryOnlyInstallation(installation) ? findTirTeam(teams) : null;
    const defaultTeam = requiredTeam || (isDad ? findDadTeam(teams) : null);
    setScheduleRequestError('');
    setScheduleRequest({
      installation,
      date: toISODate(new Date()),
      teamId: defaultTeam ? String(defaultTeam.id) : '',
      teamLocked: !!requiredTeam,
    });
  }

  async function submitScheduleRequest() {
    if (!scheduleRequest?.date || !scheduleRequest?.teamId || scheduleRequestBusy) return;
    setScheduleRequestBusy(true);
    setScheduleRequestError('');
    try {
      const targetDay = new Date(`${scheduleRequest.date}T12:00:00`);
      await scheduleInstallations(
        [scheduleRequest.installation],
        targetDay,
        Number(scheduleRequest.teamId),
        { throwOnError: true }
      );
      setScheduleRequest(null);
    } catch (error) {
      console.error('Context-menu scheduling failed', error);
      setScheduleRequestError(
        error.response?.data?.detail || error.message || 'Nie udało się zaplanować zlecenia.'
      );
    } finally {
      setScheduleRequestBusy(false);
    }
  }

  function askDayDropAction(installation, sourceDay, targetDay, sourceTeamId, targetTeamId) {
    setDayDropQuestion({
      entryName: isCalendarNote(installation)
        ? installation.notes || 'Notatka'
        : installation.order_number,
      isNote: isCalendarNote(installation),
      sourceDay,
      targetDay,
      sourceTeam: teams.find(team => team.id === sourceTeamId)?.name || 'Bez wskazanej ekipy',
      targetTeam: teams.find(team => team.id === targetTeamId)?.name || 'Bez wskazanej ekipy',
    });
    return new Promise((resolve) => {
      dayDropResolverRef.current = resolve;
    });
  }

  function resolveDayDropQuestion(action) {
    dayDropResolverRef.current?.(action);
    dayDropResolverRef.current = null;
    setDayDropQuestion(null);
  }

  function storeScheduledInstallation(installation) {
    mutationVersions.current.set(installation.id, ++mutationVersion.current);
    setInstallations(current => replaceInstallation(current, installation, item => !!item.planned_start_date));
    setUnscheduledOrders(current => replaceInstallation(current, installation, item => !item.planned_start_date));
  }

  async function handleQuickAddSaved(installation) {
    storeScheduledInstallation(installation);
    // DAD orders can create a team during the transaction.
    if (installation.assignments?.some(assignment => assignment.team_id
      && !teams.some(team => team.id === assignment.team_id))) {
      try {
        if (!teamRefresh.current) {
          teamRefresh.current = installationService.listTeams().then(setTeams)
            .finally(() => { teamRefresh.current = null; });
        }
        await teamRefresh.current;
      } catch (error) {
        setPlanningNotice('Zapisano zlecenie, ale nie udało się pobrać nowej ekipy. Odśwież widok, aby ją zobaczyć.');
      }
    }
  }

  async function persistSchedule(installation, payload) {
    if (pendingSchedules.current.has(installation.id)) {
      throw new Error('To zlecenie jest właśnie zapisywane.');
    }
    pendingSchedules.current.add(installation.id);
    storeScheduledInstallation(optimisticSchedule(installation, payload, teams, {
      tir: hasDeliveryToClient(installation) && (
        isDeliveryOnlyInstallation(installation) || installation.delivery_team_excluded_dates != null
        || !installation.delivery_team_auto_disabled
      ),
      mandatoryTir: isDeliveryOnlyInstallation(installation),
      dad: isDadOrder(installation),
    }));
    try {
      const result = await installationService.scheduleInstallation(installation.id, payload);
      storeScheduledInstallation(result.installation);
      // TIR/DAD can be created by this save. Fetch only the missing reference
      // data, sharing the request when several orders are scheduled together.
      if (result.installation.assignments?.some(assignment => assignment.team_id
        && !teams.some(team => team.id === assignment.team_id))) {
        try {
          if (!teamRefresh.current) {
            teamRefresh.current = installationService.listTeams().then(setTeams)
              .finally(() => { teamRefresh.current = null; });
          }
          await teamRefresh.current;
        } catch (error) {
          setPlanningNotice(current => [current,
            'Zapisano zlecenie, ale nie udało się pobrać nowej ekipy. Odśwież widok, aby ją zobaczyć.',
          ].filter(Boolean).join('\n'));
        }
      }
      if (result.vacation_warnings?.length) {
        setPlanningNotice(current => [current, ...result.vacation_warnings].filter(Boolean).join('\n'));
      }
      return result;
    } catch (error) {
      storeScheduledInstallation(installation);
      // After a lost response the transaction may have committed. Reconcile
      // this one order, never overwrite it with a second compensating write.
      if (!error.response || error.response.status >= 500) {
        try {
          const saved = await installationService.getInstallation(installation.id);
          storeScheduledInstallation(saved);
        } catch (readError) {
          console.error('Could not verify schedule after connection failure', readError);
        }
      }
      throw error;
    } finally {
      pendingSchedules.current.delete(installation.id);
    }
  }

  async function scheduleInstallations(installationsToSchedule, targetDay, teamId = null, options = {}) {
    const installationsToSplit = [];
    const errors = [];
    setPlanningError('');
    setPlanningNotice('');

    const scheduleOne = async (inst) => {
      const tirTeam = findTirTeam(teams);
      const deliveryOnly = isDeliveryOnlyInstallation(inst);
      const effectiveTeamId = deliveryOnly && tirTeam ? tirTeam.id : teamId;
      if (!confirmDadTeamChange(inst, effectiveTeamId, teams)) return;
      const startDate = toISODate(targetDay);
      let endDate = startDate;
      if (inst.planned_start_date && inst.planned_end_date) {
        const diff = Math.round(
          (new Date(inst.planned_end_date) - new Date(inst.planned_start_date)) / 86400000
        );
        endDate = toISODate(addDays(targetDay, diff));
      }

      try {
        const result = await persistSchedule(inst, {
          planned_start_date: startDate,
          planned_end_date: endDate,
          planned_dates: inst.planned_dates?.length ? shiftInstallationDates(inst, startDate) : null,
          team_id: effectiveTeamId,
          action: 'schedule',
          // A drag originating on the calendar relocates the whole card. Its
          // ordinary assignments from older dates must not be kept invisibly.
          replace_regular_assignments: !!options.source,
        });

        const resultSplitReason = result?.split_reason || 'perimeter';
        const alreadyHasPremiumOrder = resultSplitReason === 'premium_plus'
          && installations.some((existing) => existing.order_number === `${inst.order_number}-PR`);
        if (result && typeof result === 'object' && result.should_split && result.warning_message && !alreadyHasPremiumOrder) {
          installationsToSplit.push({
            installation: result.installation,
            warningMessage: String(result.warning_message || ''),
            totalPerimeter: parseFloat(result.total_perimeter) || 0,
            splitReason: resultSplitReason,
            targetDay,
            teamId: effectiveTeamId,
          });
        }
      } catch (error) {
        console.error('Scheduling failed for installation', inst.id, error);
        errors.push({ installation: inst, error });
      }
    };
    // Bound concurrent writes when the user drags a large selection.
    for (let offset = 0; offset < installationsToSchedule.length; offset += 4) {
      await Promise.all(installationsToSchedule.slice(offset, offset + 4).map(scheduleOne));
    }

    if (installationsToSplit.length > 0) {
      const first = installationsToSplit[0];
      setSplitQueue(installationsToSplit.slice(1));
      splitTargetDayRef.current = first.targetDay;
      splitTeamIdRef.current = first.teamId;
      setSplitInstallation(first.installation);
      setSplitWarningMessage(first.warningMessage);
      setSplitTotalPerimeter(first.totalPerimeter);
      setSplitReason(first.splitReason || 'perimeter');
      setShowSplitModal(true);
    }

    if (errors.length) {
      setPlanningError(errors.map(({ installation, error }) => (
        `${isCalendarNote(installation) ? installation.notes || 'Notatka' : installation.order_number}: ${error.response?.data?.detail || error.message || 'Nie udało się zaplanować wpisu.'}`
      )).join('\n'));
      if (options.throwOnError) throw errors[0].error;
    }
  }

  async function handleDrop(e, targetDay, teamId = null) {
    e.preventDefault();
    const dragData = dragRef.current;
    dragRef.current = null;
    const installations = dragData?.installations;
    const source = dragData?.source;
    if (!installations || installations.length === 0 || !canManage) return;

    const targetDayISO = toISODate(targetDay);
    if (installations.length === 1 && source?.day) {
      const installation = installations[0];
      if (source.day === targetDayISO && source.teamId === teamId) return;
      const action = await askDayDropAction(installation, source.day, targetDayISO, source.teamId, teamId);
      if (!action) return;
      if (!confirmDadTeamChange(installation, teamId, teams)) return;
      setPlanningError('');
      setPlanningNotice('');
      try {
        await persistSchedule(installation, {
          planned_start_date: installation.planned_start_date,
          planned_end_date: installation.planned_end_date || installation.planned_start_date,
          source_date: source.day,
          source_team_id: source.teamId,
          source_all_teams: !!source.allTeams,
          assigned_date: targetDayISO,
          team_id: teamId,
          action: action === 'add' ? 'card_add' : 'card_move',
        });
      } catch (error) {
        setPlanningError(error.response?.data?.detail || error.message || 'Nie udało się zmienić przypisania.');
      }
      return;
    }

    await scheduleInstallations(installations, targetDay, teamId, { source });
  }

  async function handleSave(form, assignmentAction = 'keep') {
    const equipmentFlags = {
      rusztowanie: !!form.rusztowanie,
      drabina: !!form.drabina,
      pajaczek: !!form.pajaczek,
      manipulator: !!form.manipulator,
      agregat: !!form.agregat,
      dodatkowy_bus: !!form.dodatkowy_bus,
      tir: !!form.tir,
      delivery_team_excluded_dates: form.delivery_team_excluded_dates || [],
    };
    let savedInstallation;
    if (editInstallation) {
      savedInstallation = await installationService.updateInstallation(editInstallation.id, {
        calendar_status: form.calendar_status,
        planned_start_date: form.planned_start_date || null,
        planned_end_date: form.planned_end_date || null,
        planned_dates: form.planned_start_date ? form.planned_dates || null : null,
        assignment_action: assignmentAction,
        contractor: form.contractor,
        state: form.state,
        address: form.address,
        notes: form.notes,
        ...(canViewSettlement ? {
          overhead_percentage: form.overhead_percentage,
          distribution_percentage: form.distribution_percentage,
          settlement_mode: form.settlement_mode || 'weights',
          manual_settlement_amounts: form.manual_settlement_amounts || {},
        } : {}),
        ...equipmentFlags,
      });
    } else {
      savedInstallation = await installationService.createInstallation({
        order_number: form.order_number,
        contractor: form.contractor,
        state: form.state,
        address: form.address,
        notes: form.notes,
        calendar_status: form.calendar_status,
        planned_start_date: form.planned_start_date || null,
        planned_end_date: form.planned_end_date || null,
        ...(canViewSettlement ? {
          overhead_percentage: form.overhead_percentage,
          distribution_percentage: form.distribution_percentage,
          settlement_mode: form.settlement_mode || 'weights',
          manual_settlement_amounts: form.manual_settlement_amounts || {},
        } : {}),
        ...equipmentFlags,
      });
    }
    if (savedInstallation?.vacation_warnings?.length) {
      setPlanningNotice(current => [
        current,
        ...savedInstallation.vacation_warnings,
      ].filter(Boolean).join('\n'));
    }
    fetchCalendar();
    return savedInstallation;
  }

  async function handleConfirmSplit(newStartDate, newEndDate, assignmentAction = 'keep', options = {}) {
    if (!splitInstallation) return;

    try {
      const teamId = splitTeamIdRef.current;
      const hasTeam = teamId !== null && teamId !== undefined;
      if (options.splitReason === 'premium_plus') {
        // Jedno żądanie: zlecenie -PR + pozycja USL + data + ekipa (transakcja na serwerze).
        await installationService.createRelatedInstallation(splitInstallation.id, {
          kind: 'premium',
          planned_start_date: newStartDate,
          team_id: hasTeam ? teamId : null,
          usl_value: options.premiumUslValue || 0,
        });
      } else {
        // Jedno żądanie: daty, kopiowanie/zachowanie przypisań i ekipa z przeciągnięcia
        // zapisują się razem albo wcale (np. ekipa na urlopie -> 409 i bez zmian).
        await installationService.updateInstallation(splitInstallation.id, {
          planned_start_date: newStartDate,
          planned_end_date: newEndDate,
          planned_dates: options.plannedDates || null,
          assignment_action: assignmentAction === 'copy' ? 'copy' : 'keep',
          ...(assignmentAction !== 'copy' && hasTeam ? { assign_team_id: teamId } : {}),
        });
      }

      setShowSplitModal(false);
      setSplitInstallation(null);
      setSplitWarningMessage('');
      setSplitTotalPerimeter(0);
      setSplitReason('perimeter');
      
      // Przetwórz następną instalację z kolejki
      if (splitQueue.length > 0) {
        const next = splitQueue[0];
        setSplitQueue(splitQueue.slice(1));
        splitTargetDayRef.current = next.targetDay;
        splitTeamIdRef.current = next.teamId;
        setSplitInstallation(next.installation);
        setSplitWarningMessage(next.warningMessage);
        setSplitTotalPerimeter(next.totalPerimeter);
        setSplitReason(next.splitReason || 'perimeter');
        setShowSplitModal(true);
      } else {
        // Koniec kolejki - odśwież kalendarz
        splitTargetDayRef.current = null;
        splitTeamIdRef.current = null;
        fetchCalendar();
        fetchUnscheduled();
      }
    } catch (e) {
      console.error('Error confirming split', e);
      // Zapis jest atomowy, więc nic nie zostało zmienione. Okno i kolejka zostają,
      // a błąd jest widoczny w oknie rozplanowania (SplitInstallationModal).
      fetchCalendar();
      fetchUnscheduled();
      throw e;
    }
  }

  return (
    <div className={`planning-tab ${showModal || calendarNoteInstallation ? 'planning-tab--details-open' : ''}`}>
      {scheduleRequest && (
        <div
          className="modal-overlay"
          style={{ zIndex: 12000 }}
          onClick={() => !scheduleRequestBusy && setScheduleRequest(null)}
        >
          <form
            className="modal-box schedule-order-modal"
            onClick={(event) => event.stopPropagation()}
            onSubmit={(event) => {
              event.preventDefault();
              submitScheduleRequest();
            }}
          >
            <div className="modal-header">
              <h3>Zaplanuj zlecenie {scheduleRequest.installation.order_number}</h3>
              <button
                type="button"
                className="modal-close"
                onClick={() => setScheduleRequest(null)}
                disabled={scheduleRequestBusy}
              >
                ✕
              </button>
            </div>
            <div className="modal-body schedule-order-fields">
              <label>
                Dzień montażu
                <input
                  type="date"
                  value={scheduleRequest.date}
                  onChange={(event) => setScheduleRequest(current => ({
                    ...current,
                    date: event.target.value,
                  }))}
                  required
                  autoFocus
                />
              </label>
              <label>
                Ekipa
                <select
                  value={scheduleRequest.teamId}
                  onChange={(event) => setScheduleRequest(current => ({
                    ...current,
                    teamId: event.target.value,
                  }))}
                  required
                  disabled={scheduleRequest.teamLocked}
                >
                  <option value="">-- Wybierz ekipę --</option>
                  {teams
                    .filter((team) => (
                      isDeliveryOnlyInstallation(scheduleRequest.installation)
                        ? team.is_special_truck
                        : !team.is_special_truck
                    ))
                    .map((team) => (
                    <option key={team.id} value={team.id}>{team.name}</option>
                  ))}
                </select>
              </label>
              {scheduleRequest.teamLocked && (
                <div className="schedule-order-hint">
                  Dla tego typu zlecenia ekipa jest przypisywana automatycznie.
                </div>
              )}
              {scheduleRequestError && (
                <div className="modal-error" role="alert">{scheduleRequestError}</div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setScheduleRequest(null)}
                disabled={scheduleRequestBusy}
              >
                Anuluj
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={scheduleRequestBusy || !scheduleRequest.date || !scheduleRequest.teamId}
              >
                {scheduleRequestBusy ? 'Zapisywanie…' : 'Zaplanuj'}
              </button>
            </div>
          </form>
        </div>
      )}
      {dayDropQuestion && (
        <div className="modal-overlay" style={{ zIndex: 12000 }} onClick={() => resolveDayDropQuestion(null)}>
          <div className="modal-box" style={{ maxWidth: '480px' }} onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h3>Zmiana przypisania {dayDropQuestion.isNote ? 'notatki' : 'montażu'}</h3>
              <button className="modal-close" onClick={() => resolveDayDropQuestion(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p>
                {dayDropQuestion.isNote ? 'Notatka' : 'Zlecenie'} <strong>{dayDropQuestion.entryName}</strong> jest już zaplanowane na{' '}
                <strong>{dayDropQuestion.sourceDay} — {dayDropQuestion.sourceTeam}</strong>. Upuszczono je na <strong>{dayDropQuestion.targetDay} — {dayDropQuestion.targetTeam}</strong>.
              </p>
              <p>
                <strong>Dodaj przypisanie</strong> pozostawi {dayDropQuestion.sourceDay} u ekipy {dayDropQuestion.sourceTeam}.
                {' '}<strong>Przenieś przypisanie</strong> usunie tę kartę ze źródłowego miejsca.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => resolveDayDropQuestion(null)}>Anuluj</button>
              <button className="btn-secondary" onClick={() => resolveDayDropQuestion('add')}>Dodaj przypisanie</button>
              <button className="btn-primary" onClick={() => resolveDayDropQuestion('move')}>Przenieś przypisanie</button>
            </div>
          </div>
        </div>
      )}
      {/* Toolbar */}
      <div className="calendar-toolbar">
        <div className="view-switcher">
          {availableViews.map((v) => (
            <button
              key={v}
              className={`btn-view ${view === v ? 'active' : ''}`}
              onClick={() => setView(v)}
            >
              {v}
            </button>
          ))}
        </div>
        <div className="nav-controls">
          <button className="btn-nav" onClick={() => navigate(-1)}>‹</button>
          <span className="current-label">{getCurrentLabel()}</span>
          <button className="btn-nav" onClick={() => navigate(1)}>›</button>
          <button className="btn-secondary btn-sm" onClick={() => setCurrentDate(new Date())}>Dziś</button>
        </div>
        <div className="calendar-order-search">
          <div className="calendar-order-search-controls">
            <input
              type="search"
              value={orderSearch}
              onChange={(event) => {
                setOrderSearch(event.target.value);
                setOrderSearchResults([]);
                setOrderSearchStatus(null);
                setHighlightedInstallationId(null);
                setOrderSearchFocused(true);
              }}
              onFocus={() => setOrderSearchFocused(true)}
              onBlur={() => setOrderSearchFocused(false)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') searchOrder();
              }}
              placeholder="Numer, kontrahent lub notatka"
              aria-label="Wyszukaj zlecenie po numerze, kontrahencie lub notatce"
            />
            <button type="button" className="btn-primary btn-sm" onClick={searchOrder} disabled={!orderSearch.trim() || orderSearchBusy}>
              {orderSearchBusy ? 'Szukam…' : 'Szukaj'}
            </button>
          </div>
          {orderSearchStatus && (
            <div className={`calendar-order-search-status ${orderSearchStatus.type}`} role="status">
              {orderSearchStatus.text}
            </div>
          )}
          <OrderNumberSuggestions
            query={orderSearch}
            installations={orderSearchResults}
            visible={orderSearchFocused}
            onSelect={showFoundInstallation}
          />
        </div>
        <div className="calendar-toolbar-actions">
          <div className="daily-print-menu">
            <button
              className="btn-secondary btn-sm calendar-toolbar-action-button"
              onClick={() => setShowPrintMenu((visible) => !visible)}
              title="Wybierz dzień i rodzaj wydruku"
              type="button"
            >
              Wydruk zleceń
            </button>
            {showPrintMenu && (
              <div className="daily-print-dropdown">
                <label>
                  Dzień
                  <input
                    key={printDateInputKey}
                    type="date"
                    value={printDay}
                    onPointerDown={() => { printDateOpenedByPointer.current = true; }}
                    onKeyDown={() => { printDateOpenedByPointer.current = false; }}
                    onChange={(e) => {
                      setPrintDay(e.target.value);
                      setPrintError('');
                      if (printDateOpenedByPointer.current) {
                        // Replacing the native input closes its browser date picker.
                        printDateOpenedByPointer.current = false;
                        setPrintDateInputKey((key) => key + 1);
                      }
                    }}
                    aria-label="Dzień wydruku planu montaży"
                  />
                </label>
                <div className="daily-print-count">
                  Zlecenia w dniu: <strong>{printableDayInstallations === null ? 'Pobieranie…' : printableDayInstallations.length}</strong>
                </div>
                <div className="daily-print-options" aria-label="Rodzaj wydruku">
                  <button
                    className="btn-primary btn-sm"
                    onClick={() => handlePrintDay('general')}
                    disabled={!printDay || printBusy || printableDayInstallations === null}
                    type="button"
                  >
                    <strong>Ogólny</strong>
                    <small>z montażystami</small>
                  </button>
                  <button
                    className="btn-secondary btn-sm"
                    onClick={() => handlePrintDay('warehouse')}
                    disabled={!printDay || printBusy || printableDayInstallations === null}
                    type="button"
                  >
                    <strong>Magazyn</strong>
                    <small>brygadzista i WH</small>
                  </button>
                  {canViewSettlement && <button
                    className="btn-secondary btn-sm daily-print-amounts"
                    onClick={() => handlePrintDay('amounts')}
                    disabled={!printDay || printBusy || printableDayInstallations === null}
                    type="button"
                  >
                    <strong>{printBusy ? 'Pobieranie kwot…' : 'Wydruk z kwotami'}</strong>
                    <small>ekipy, zlecenia i kwoty pracowników</small>
                  </button>}
                </div>
                {printError && <div className="daily-print-error" role="alert">{printError}</div>}
              </div>
            )}
          </div>
          <label className="calendar-toolbar-checkbox">
            <input
              type="checkbox"
              checked={hideSundays}
              onChange={(e) => setHideSundays(e.target.checked)}
            />
            Ukryj niedziele
          </label>
          {view === WEEKLY_VIEW && (
            <label className="calendar-toolbar-checkbox">
              <input
                type="checkbox"
                checked={showAddresses}
                onChange={(e) => setShowAddresses(e.target.checked)}
              />
              Pokaż adresy
            </label>
          )}
          {view === WEEKLY_VIEW && (
            <label className="calendar-toolbar-checkbox">
              <input
                type="checkbox"
                checked={showWh}
                onChange={(e) => setShowWh(e.target.checked)}
              />
              Pokaż WH
            </label>
          )}
          {canManage && (
            <div className="quick-add-menu">
              <button
                className="btn-primary btn-sm calendar-toolbar-action-button"
                onClick={() => {
                  setShowPrintMenu(false);
                  setShowQuickAddModal(true);
                }}
                type="button"
                aria-expanded={showQuickAddModal}
                aria-controls="quick-add-calendar-panel"
              >
                Dodaj szybki montaż
              </button>
              {showQuickAddModal && (
                <QuickAddInstallationModal
                  teams={teams}
                  canViewSettlement={canViewSettlement}
                  onClose={() => setShowQuickAddModal(false)}
                  onSaved={handleQuickAddSaved}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="calendar-legend">
        {STATUS_LEGEND_ORDER.map((status) => (
          <span key={status} className="legend-item">
            <span
              className="legend-dot"
              style={{
                background: STATUS_COLORS[status].bg,
                border: `2px solid ${STATUS_COLORS[status].border}`,
              }}
            />
            {STATUS_COLORS[status].label}
          </span>
        ))}
        <span className="legend-item">
          <span className="legend-dot" style={{ background: '#dbeafe', border: '2px solid #2563eb' }} />
          {tirTeamName}
        </span>
      </div>

      {pajaczekDayConflicts.length > 0 && (
        <div className="modal-warning-banner" style={{ marginBottom: '12px' }}>
          <span className="warning-icon">⚠️</span>
          <div className="warning-content">
            <strong>Konflikt wyposażenia: Pajączek</strong>
            <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
              {pajaczekDayConflicts.map((conflict) => (
                <li key={conflict.day}>
                  {conflict.day}: {conflict.orders.join(', ')}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {planningError && <div role="alert" className="modal-warning-banner" style={{ whiteSpace: 'pre-line' }}>{planningError}</div>}
      {planningNotice && <div role="status" className="modal-warning-banner" style={{ whiteSpace: 'pre-line' }}>{planningNotice}</div>}
      {loading && calendarReady && <small role="status">Odświeżanie kalendarza…</small>}
      {loading && !calendarReady ? (
        <div className="loading-text">Ładowanie…</div>
      ) : (
        <>
          {view === 'Widok miesięczny' && (
            <MonthView
              currentDate={currentDate}
              installations={installations}
              onEdit={openEdit}
              onAddAdditionalWork={openAdditionalWork}
              onDragStart={handleDragStart}
              onDrop={handleDrop}
              canManage={canManage}
              hideSundays={hideSundays}
              specialTruckName={tirTeamName}
              highlightedInstallationId={highlightedInstallationId}
            />
          )}
          {view === WEEKLY_VIEW && (
            <TeamRowsView
              key={toISODate(startOfWeek(currentDate))}
              currentDate={currentDate}
              installations={installations}
              teams={teams}
              onEdit={openEdit}
              onAddAdditionalWork={openAdditionalWork}
              onDragStart={handleDragStart}
              onDrop={handleDrop}
              onAssignmentsChanged={() => refreshPlanningData({ preserveScroll: true })}
              canManage={canManage}
              hideSundays={hideSundays}
              showAddresses={showAddresses}
              showWh={showWh}
              highlightedInstallationId={highlightedInstallationId}
            />
          )}
        </>
      )}

      {/* Unscheduled orders */}
      <UnscheduledOrdersPanel
        orders={unscheduledOrders}
        onEdit={openEdit}
        onDragStart={handleDragStart}
        onRefresh={fetchUnscheduled}
        onSchedule={openScheduleRequest}
        onAddAdditionalWork={openAdditionalWork}
        canManage={canManage}
        specialTruckName={tirTeamName}
        highlightedInstallationId={highlightedInstallationId}
      />

      {calendarNoteInstallation && (
        <CalendarNoteModal
          key={calendarNoteInstallation.id}
          installation={calendarNoteInstallation}
          teams={teams}
          installers={installers}
          canViewSettlement={canViewOrderSettlement}
          canEditSettlement={canViewSettlement}
          onClose={() => setCalendarNoteInstallation(null)}
          onSaved={(updated) => {
            storeScheduledInstallation(updated);
          }}
          onDeleted={() => {
            refreshPlanningData({ preserveScroll: true });
          }}
        />
      )}

      {showModal && (
        <InstallationModal
          key={editInstallation?.id || 'new-installation'}
          installation={editInstallation}
          installations={installations}
          onClose={() => {
            rememberScrollPosition();
            setShowModal(false);
            refreshPlanningData({ preserveScroll: true });
            if (onBreadcrumbReset) onBreadcrumbReset();
          }}
          onSave={handleSave}
          installers={installers}
          teams={teams}
          vacations={vacations}
          canViewSettlement={canViewOrderSettlement}
          canEditSettlement={canViewSettlement}
        />
      )}

      {additionalWorkInstallation && (
        <AdditionalWorkModal
          installation={additionalWorkInstallation}
          teams={teams}
          onClose={() => setAdditionalWorkInstallation(null)}
          onSaved={() => refreshPlanningData({ preserveScroll: true })}
        />
      )}

      {showSplitModal && splitInstallation && (
        <SplitInstallationModal
          key={splitInstallation.id}
          installation={splitInstallation}
          warningMessage={splitWarningMessage}
          totalPerimeter={splitTotalPerimeter}
          splitReason={splitReason}
          queueLength={splitQueue.length}
          teamId={splitTeamIdRef.current}
          onClose={() => {
            // Zamknij modal bez zapisywania ("Nie rozplanuj")
            setShowSplitModal(false);
            setSplitInstallation(null);
            setSplitWarningMessage('');
            setSplitTotalPerimeter(0);
            setSplitReason('perimeter');
            
            // Przetwórz następną instalację z kolejki
            if (splitQueue.length > 0) {
              const next = splitQueue[0];
              setSplitQueue(splitQueue.slice(1));
              splitTargetDayRef.current = next.targetDay;
              splitTeamIdRef.current = next.teamId;
              setSplitInstallation(next.installation);
              setSplitWarningMessage(next.warningMessage);
              setSplitTotalPerimeter(next.totalPerimeter);
              setSplitReason(next.splitReason || 'perimeter');
              setShowSplitModal(true);
            } else {
              // Koniec kolejki - odśwież kalendarz
              splitTargetDayRef.current = null;
              splitTeamIdRef.current = null;
              refreshPlanningData({ preserveScroll: true });
            }
          }}
          onConfirm={handleConfirmSplit}
        />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
//  Tab 2 – Lista montaży
// ──────────────────────────────────────────────────────────────

function AddInstallationModal({ onClose, onSaved }) {
  const EMPTY = {
    order_number: '', contractor: '', state: '', address: '',
    departure_date: '', realization: '', planned_production_date: '', wh: '',
  };
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function set(field) {
    return (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  }

  async function handleSave() {
    if (!form.order_number.trim()) { setError('Numer zlecenia jest wymagany'); return; }
    setSaving(true);
    setError('');
    try {
      await installationService.createInstallation({
        order_number: form.order_number.trim(),
        contractor: form.contractor || null,
        state: form.state || null,
        address: form.address || null,
        departure_date: form.departure_date || null,
        realization: form.realization || null,
        planned_production_date: form.planned_production_date || null,
        wh: form.wh || null,
      });
      onSaved();
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'Błąd zapisu');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Nowy montaż</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        {error && <div className="modal-error">{error}</div>}
        <div className="modal-body">
          <div className="form-grid">
            <label>Numer zlecenia *
              <input value={form.order_number} onChange={set('order_number')} placeholder="np. ZL/2026/001" />
            </label>
            <label>Kontrahent
              <input value={form.contractor} onChange={set('contractor')} />
            </label>
            <label>STAN
              <input value={form.state} onChange={set('state')} />
            </label>
            <label>Adres
              <input value={form.address} onChange={set('address')} />
            </label>
            <label>Data wyjazdu
              <input value={form.departure_date} onChange={set('departure_date')} placeholder="np. 2026-06-01" />
            </label>
            <label>Realizacja
              <input value={form.realization} onChange={set('realization')} />
            </label>
            <label>Planowana data produkcji
              <input value={form.planned_production_date} onChange={set('planned_production_date')} placeholder="np. 2026-05-20" />
            </label>
            <label>WH
              <input value={form.wh} onChange={set('wh')} />
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Anuluj</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Zapisywanie…' : 'Zapisz'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PlannerImportModal({ onClose, onImported }) {
  const [plannerFile, setPlannerFile] = useState(null);
  const [positionsFile, setPositionsFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');

  function isExcelFile(file) {
    return !!file && /\.xlsx$/i.test(file.name || '');
  }

  function handleFile(setter) {
    return (event) => {
      const file = event.target.files?.[0] || null;
      setter(file);
      setError('');
    };
  }

  async function handleImport() {
    if (!isExcelFile(plannerFile) || !isExcelFile(positionsFile)) {
      setError('Dodaj oba pliki XLSX: PLANER oraz POZYCJE ZLECEŃ.');
      return;
    }

    setImporting(true);
    setError('');
    try {
      const result = await installationService.importPlannerFiles(plannerFile, positionsFile);
      onImported(result);
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(Array.isArray(detail) ? detail.map((d) => d.msg || JSON.stringify(d)).join('; ') : (detail || 'Błąd importu'));
    } finally {
      setImporting(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box planner-import-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Import XLSX</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        {error && <div className="modal-error">{error}</div>}
        <div className="modal-body">
          <div className="planner-import-grid">
            <label className={`planner-file-drop ${plannerFile ? 'has-file' : ''}`}>
              <input type="file" accept=".xlsx" onChange={handleFile(setPlannerFile)} />
              <span className="planner-file-title">PLANER</span>
              <span className="planner-file-name">{plannerFile?.name || 'Wybierz plik planer_test.xlsx'}</span>
            </label>
            <label className={`planner-file-drop ${positionsFile ? 'has-file' : ''}`}>
              <input type="file" accept=".xlsx" onChange={handleFile(setPositionsFile)} />
              <span className="planner-file-title">POZYCJE ZLECEŃ</span>
              <span className="planner-file-name">{positionsFile?.name || 'Wybierz plik POZ_ZLEC_ZBIORCZO.xlsx'}</span>
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose} disabled={importing}>Anuluj</button>
          <button
            className="btn-primary"
            onClick={handleImport}
            disabled={importing || !plannerFile || !positionsFile}
          >
            {importing ? 'Importowanie…' : 'Importuj'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReceivedPositionsImportModal({ onClose, onImported }) {
  const [positionsFile, setPositionsFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');

  async function handleImport() {
    if (!positionsFile || !/\.xlsx$/i.test(positionsFile.name || '')) {
      setError('Dodaj plik XLSX: POZYCJE ZLECEŃ.');
      return;
    }

    setImporting(true);
    setError('');
    try {
      const result = await installationService.importReceivedPositions(positionsFile);
      onImported(result);
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(Array.isArray(detail) ? detail.map((d) => d.msg || JSON.stringify(d)).join('; ') : (detail || 'Błąd importu'));
    } finally {
      setImporting(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box planner-import-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Przysłano</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        {error && <div className="modal-error">{error}</div>}
        <div className="modal-body">
          <p>
            Dla zleceń bez pozycji zostaną dodane pozycje z pliku. W pozostałych
            zleceniach zaktualizowane zostaną wyłącznie pozycje USL. Jeśli istniejące
            zlecenie nie ma daty montażu, zostanie ona ustawiona z kolumny
            „PLANOWANA DATA MONTAŻU/DOSTAWY”.
          </p>
          <div className="planner-import-grid">
            <label className={`planner-file-drop ${positionsFile ? 'has-file' : ''}`}>
              <input
                type="file"
                accept=".xlsx"
                onChange={(event) => {
                  setPositionsFile(event.target.files?.[0] || null);
                  setError('');
                }}
              />
              <span className="planner-file-title">POZYCJE ZLECEŃ</span>
              <span className="planner-file-name">{positionsFile?.name || 'Wybierz plik POZ_ZLEC_ZBIORCZO.xlsx'}</span>
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose} disabled={importing}>Anuluj</button>
          <button className="btn-primary" onClick={handleImport} disabled={importing || !positionsFile}>
            {importing ? 'Importowanie…' : 'Importuj'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ListCalendarScheduleMenu({ installation, teams, anchorRect, onClose, onScheduled }) {
  const today = toISODate(new Date());
  const [date, setDate] = useState(installation.planned_start_date || today);
  const [teamId, setTeamId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const menuRef = useRef(null);
  const deliveryOnly = isDeliveryOnlyInstallation(installation);
  const menuWidth = 360;
  const menuGap = 8;
  const menuPosition = (() => {
    if (!anchorRect) return {};
    const viewportPadding = 12;
    const fitsRight = window.innerWidth - anchorRect.right >= menuWidth + menuGap;
    const fitsLeft = anchorRect.left >= menuWidth + menuGap;
    const left = fitsRight
      ? anchorRect.right + menuGap
      : fitsLeft
        ? anchorRect.left - menuWidth - menuGap
        : Math.max(viewportPadding, Math.min(anchorRect.left, window.innerWidth - menuWidth - viewportPadding));
    const top = fitsRight || fitsLeft
      ? Math.max(viewportPadding, Math.min(anchorRect.top, window.innerHeight - 330))
      : anchorRect.bottom + menuGap;
    return { left, top };
  })();

  useEffect(() => {
    function handlePointerDown(event) {
      if (!menuRef.current?.contains(event.target)) onClose();
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape') onClose();
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  async function handleSubmit(event) {
    event.preventDefault();
    if (!date) {
      setError('Wybierz dzień montażu.');
      return;
    }
    if (teamId && !confirmDadTeamChange(installation, Number(teamId), teams)) return;

    setSaving(true);
    setError('');
    try {
      const result = await installationService.scheduleInstallation(installation.id, {
        planned_start_date: date,
        planned_end_date: date,
        planned_dates: null,
        team_id: teamId ? Number(teamId) : null,
        action: 'schedule',
        only_if_unscheduled: true,
      });

      await onScheduled?.(result.installation);
      onClose();
      const warnings = [result?.warning_message, ...(result?.vacation_warnings || [])].filter(Boolean);
      if (warnings.length) {
        window.alert(warnings.join('\n'));
      }
    } catch (requestError) {
      const detail = requestError.response?.data?.detail;
      setError(
        typeof detail === 'string'
          ? detail
          : (detail ? JSON.stringify(detail) : 'Nie udało się przypisać zlecenia do kalendarza.')
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <form
      ref={menuRef}
      className="list-calendar-schedule-menu"
      style={menuPosition}
      onSubmit={handleSubmit}
      onContextMenu={(event) => event.preventDefault()}
      role="dialog"
      aria-label={`Przypisz zlecenie ${installation.order_number} do kalendarza`}
    >
      <div className="list-calendar-schedule-header">
        <div>
          <strong>Przypisz do kalendarza</strong>
          <span>{installation.order_number}</span>
        </div>
        <button type="button" onClick={onClose} disabled={saving} aria-label="Zamknij">✕</button>
      </div>
      <label>
        Dzień montażu
        <input
          type="date"
          value={date}
          onChange={(event) => setDate(event.target.value)}
          required
          autoFocus
        />
      </label>
      <label>
        Ekipa (opcjonalnie)
        <select value={teamId} onChange={(event) => setTeamId(event.target.value)}>
          <option value="">Przypisz później</option>
          {teams
            .filter((team) => deliveryOnly ? team.is_special_truck : !team.is_special_truck)
            .map((team) => (
              <option key={team.id} value={team.id}>{team.name}</option>
            ))}
        </select>
      </label>
      {error && <div className="modal-error" role="alert">{error}</div>}
      <div className="list-calendar-schedule-actions">
        <button type="button" className="btn-secondary btn-sm" onClick={onClose} disabled={saving}>
          Anuluj
        </button>
        <button type="submit" className="btn-primary btn-sm" disabled={saving || !date}>
          {saving ? 'Przypisywanie…' : 'Przypisz'}
        </button>
      </div>
    </form>
  );
}

export const SEARCH_DEBOUNCE_MS = 300;

// Returns `value` only after it stayed unchanged for `delay` ms.
export function useDebouncedValue(value, delay = SEARCH_DEBOUNCE_MS) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    if (!(delay > 0)) {
      setDebounced(value);
      return undefined;
    }
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export const LIST_PAGE_SIZES = [25, 50, 100, 200];
export const DEFAULT_LIST_PAGE_SIZE = 50;

export function ListPagination({
  page, pageSize, total, itemCount, loading = false, onPageChange, onPageSizeChange,
}) {
  const hasTotal = Number.isFinite(total);
  const first = itemCount === 0 ? 0 : page * pageSize + 1;
  const last = page * pageSize + itemCount;
  const lastPage = hasTotal ? Math.max(0, Math.ceil(total / pageSize) - 1) : null;
  const canPrev = page > 0 && !loading;
  // Without a readable total we can only guess there is more when the page is full.
  const canNext = !loading && (hasTotal ? page < lastPage : itemCount === pageSize);
  let range = `Strona ${page + 1}`;
  if (hasTotal) range = total === 0 ? 'Brak wyników' : `${first}–${last} z ${total}`;
  return (
    <div className="list-pagination" role="navigation" aria-label="Stronicowanie listy montaży">
      <span className="list-pagination-range">{range}</span>
      <button
        type="button"
        className="btn-secondary"
        style={{ flex: 'none', width: 'auto', whiteSpace: 'nowrap' }}
        disabled={!canPrev}
        onClick={() => onPageChange(page - 1)}
      >‹ Poprzednia</button>
      <button
        type="button"
        className="btn-secondary"
        style={{ flex: 'none', width: 'auto', whiteSpace: 'nowrap' }}
        disabled={!canNext}
        onClick={() => onPageChange(page + 1)}
      >Następna ›</button>
      <select
        value={pageSize}
        aria-label="Liczba pozycji na stronie"
        onChange={(e) => onPageSizeChange(Number(e.target.value))}
      >
        {LIST_PAGE_SIZES.map((size) => (
          <option key={size} value={size}>{size} / stronę</option>
        ))}
      </select>
    </div>
  );
}

export function InstallationListTab({ canManage = false, canViewSettlement = false, onBreadcrumbChange, onBreadcrumbReset }) {
  const [installations, setInstallations] = useState([]);
  const [teams, setTeams] = useState([]);
  const [states, setStates] = useState([]);
  const [filterState, setFilterState] = useState('');
  const [filterPzl, setFilterPzl] = useState('all');
  const [search, setSearch] = useState('');
  // Typing updates `search` immediately (suggestions); the server is queried
  // only after the user pauses.
  const debouncedSearch = useDebouncedValue(search);
  const listRequestId = useRef(0);
  const [searchFocused, setSearchFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(null);
  const [pageSize, setPageSize] = useState(DEFAULT_LIST_PAGE_SIZE);
  const [pageState, setPageState] = useState({ key: '', page: 0 });
  const [importResult, setImportResult] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [showAddModal, setShowAddModal] = useState(false);
  const [addingItemForId, setAddingItemForId] = useState(null);
  const [newItemForm, setNewItemForm] = useState({ name: '', product_type: '', scope: '', quantity: '', perimeter: '', perimeter_sum: '', net_price_pln: '' });
  const [editItemId, setEditItemId] = useState(null);
  const [editItemForm, setEditItemForm] = useState({});
  const [itemSortBy, setItemSortBy] = useState('product_type'); // 'product_type' lub 'name'
  const [itemSortOrder, setItemSortOrder] = useState('asc'); // 'asc' lub 'desc'
  const [filterCalendarStatuses, setFilterCalendarStatuses] = useState(new Set(Object.keys(STATUS_COLORS))); // domyślnie wszystkie pokazane
  const [showImportModal, setShowImportModal] = useState(false);
  const [showReceivedImportModal, setShowReceivedImportModal] = useState(false);
  const [calendarScheduleInstallation, setCalendarScheduleInstallation] = useState(null);
  const [calendarScheduleAnchor, setCalendarScheduleAnchor] = useState(null);

  // The page belongs to the filter set it was chosen for: changing any filter
  // (or the page size) silently falls back to page 1 without an extra request.
  const allCalendarStatusCount = Object.keys(STATUS_COLORS).length;
  const listFiltersKey = JSON.stringify([
    filterState,
    debouncedSearch,
    filterPzl,
    [...filterCalendarStatuses].sort(),
    pageSize,
  ]);
  const page = pageState.key === listFiltersKey ? pageState.page : 0;
  const setPage = useCallback(
    (nextPage) => setPageState({ key: listFiltersKey, page: nextPage }),
    [listFiltersKey]
  );

  const fetchInstallations = useCallback(async () => {
    // Ignore responses of superseded requests (slow older query must not
    // overwrite the result of a newer one).
    const requestId = ++listRequestId.current;
    if (filterCalendarStatuses.size === 0) {
      // No status selected: nothing can match, skip the request.
      setInstallations([]);
      setTotal(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const result = await installationService.listInstallationsPage({
        state: filterState || undefined,
        search: debouncedSearch || undefined,
        pzl: filterPzl === 'all' ? undefined : filterPzl,
        calendarStatuses: filterCalendarStatuses.size >= allCalendarStatusCount
          ? undefined
          : [...filterCalendarStatuses],
        limit: pageSize,
        offset: page * pageSize,
      });
      if (requestId !== listRequestId.current) return;
      setInstallations(result.items);
      setTotal(result.total);
      if (result.items.length === 0 && result.total > 0 && page > 0) {
        // The last row of the last page was removed: go back to the new last page.
        setPage(Math.ceil(result.total / pageSize) - 1);
      }
    } catch (e) {
      console.error(e);
    } finally {
      if (requestId === listRequestId.current) setLoading(false);
    }
  }, [
    filterState, debouncedSearch, filterPzl, filterCalendarStatuses,
    allCalendarStatusCount, pageSize, page, setPage,
  ]);

  // STAN values and teams do not depend on the search/filter: load them once
  // (and after mutations) instead of with every keystroke.
  const fetchReferenceData = useCallback(async () => {
    try {
      const [stateData, teamData] = await Promise.all([
        installationService.getDistinctStates(),
        installationService.listTeams(),
      ]);
      setStates(stateData);
      setTeams(teamData);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const fetchData = useCallback(
    () => Promise.all([fetchInstallations(), fetchReferenceData()]),
    [fetchInstallations, fetchReferenceData]
  );

  useEffect(() => { fetchReferenceData(); }, [fetchReferenceData]);
  useEffect(() => { fetchInstallations(); }, [fetchInstallations]);

  const visibleInstallations = useMemo(() => installations.filter((installation) => {
    if (!filterCalendarStatuses.has(installation.calendar_status)) return false;
    const hasPzl = (installation.order_number || '').toUpperCase().includes('PZL');
    if (filterPzl === 'only') return hasPzl;
    if (filterPzl === 'without') return !hasPzl;
    return true;
  }), [installations, filterCalendarStatuses, filterPzl]);

  async function handlePlannerImport(result) {
    setImportResult(result);
    await fetchData();
  }

  function startEdit(inst) {
    setEditId(inst.id);
    setEditForm({
      departure_date: inst.departure_date || '',
      planned_production_date: inst.planned_production_date || '',
      wh: inst.wh || getWh(inst).replace('—', ''),
      realization: inst.realization || '',
      state: inst.state || '',
      address: inst.address || '',
    });
  }

  async function saveEdit(id) {
    try {
      await installationService.updateInstallation(id, editForm);
      setEditId(null);
      fetchData();
    } catch (e) {
      console.error(e);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Usunąć ten montaż?')) return;
    try {
      await installationService.deleteInstallation(id);
      fetchData();
    } catch (e) {
      console.error(e);
    }
  }

  function startAddItem(installationId) {
    setAddingItemForId(installationId);
    setNewItemForm({ name: '', product_type: '', scope: '', quantity: '', perimeter: '', perimeter_sum: '', net_price_pln: '' });
    setExpandedIds(prev => { const next = new Set(prev); next.add(installationId); return next; });
  }

  async function saveNewItem(installationId) {
    const payload = {
      name: newItemForm.name || null,
      product_type: newItemForm.product_type || null,
      scope: newItemForm.scope || null,
      quantity: newItemForm.quantity !== '' ? parseFloat(newItemForm.quantity) : null,
      perimeter: newItemForm.perimeter !== '' ? parseFloat(newItemForm.perimeter) : null,
      perimeter_sum: newItemForm.perimeter_sum !== '' ? parseFloat(newItemForm.perimeter_sum) : null,
      ...(canViewSettlement ? {
        net_price_pln: newItemForm.net_price_pln !== '' ? parseFloat(newItemForm.net_price_pln) : null,
      } : {}),
    };
    try {
      await installationService.createInstallationItem(installationId, payload);
      setAddingItemForId(null);
      fetchData();
    } catch (e) {
      console.error(e);
    }
  }

  async function handleDeleteItem(itemId) {
    if (!window.confirm('Usunąć tę pozycję?')) return;
    try {
      await installationService.deleteInstallationItem(itemId);
      fetchData();
    } catch (e) {
      console.error(e);
    }
  }

  function startEditItem(item) {
    setEditItemId(item.id);
    setEditItemForm({
      name: item.name || '',
      product_type: item.product_type || '',
      scope: item.scope || '',
      quantity: item.quantity ?? '',
      perimeter: item.perimeter ?? '',
      perimeter_sum: item.perimeter_sum ?? '',
      net_price_pln: item.net_price_pln ?? '',
    });
  }

  async function saveEditItem(itemId, installationId) {
    const payload = {
      name: editItemForm.name || null,
      product_type: editItemForm.product_type || null,
      scope: editItemForm.scope || null,
      quantity: editItemForm.quantity !== '' ? parseFloat(editItemForm.quantity) : null,
      perimeter: editItemForm.perimeter !== '' ? parseFloat(editItemForm.perimeter) : null,
      perimeter_sum: editItemForm.perimeter_sum !== '' ? parseFloat(editItemForm.perimeter_sum) : null,
      ...(canViewSettlement ? {
        net_price_pln: editItemForm.net_price_pln !== '' ? parseFloat(editItemForm.net_price_pln) : null,
      } : {}),
    };
    try {
      await installationService.updateInstallationItem(itemId, payload);
      setEditItemId(null);
      fetchData();
    } catch (e) {
      console.error(e);
    }
  }

  function toggleExpand(id) {
    const wasExpanded = expandedIds.has(id);

    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (wasExpanded) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

    if (wasExpanded) {
      onBreadcrumbReset?.();
    } else {
      const installation = installations.find(item => item.id === id);
      if (installation) onBreadcrumbChange?.(installation);
    }
  }

  function openCalendarScheduleMenu(event, installation) {
    if (!canManage) return;
    event.preventDefault();
    event.stopPropagation();
    if (installation.planned_start_date) return;
    setCalendarScheduleInstallation(installation);
    setCalendarScheduleAnchor({
      left: event.clientX,
      right: event.clientX,
      top: event.clientY,
      bottom: event.clientY,
    });
  }

  function getTotalUsl(inst) {
    return inst.total_usl_value?.toFixed(2) || '0.00';
  }

  function getWh(inst) {
    const vals = [...new Set((inst.items || []).map(i => i.wh).filter(Boolean))];
    return vals.join(', ') || '—';
  }

  function toggleCalendarStatusFilter(status) {
    setFilterCalendarStatuses(prev => {
      const next = new Set(prev);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
  }

  function handleItemSort(column) {
    if (itemSortBy === column) {
      // Zmień kierunek sortowania
      setItemSortOrder(itemSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Nowa kolumna - sortuj rosnąco
      setItemSortBy(column);
      setItemSortOrder('asc');
    }
  }

  function sortItems(items) {
    if (!itemSortBy) return items;
    
    const sorted = [...items].sort((a, b) => {
      let valA = a[itemSortBy] || '';
      let valB = b[itemSortBy] || '';
      
      // Konwertuj na stringi i porównaj (case-insensitive)
      valA = String(valA).toLowerCase();
      valB = String(valB).toLowerCase();
      
      if (valA < valB) return itemSortOrder === 'asc' ? -1 : 1;
      if (valA > valB) return itemSortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  }

  const showUslTotal = canManage || canViewSettlement;
  const COL_COUNT = 11 + Number(showUslTotal) + Number(canManage);
  const pagination = (
    <ListPagination
      page={page}
      pageSize={pageSize}
      total={total}
      itemCount={installations.length}
      loading={loading}
      onPageChange={setPage}
      onPageSizeChange={setPageSize}
    />
  );

  return (
    <div className="list-tab">
      <div className="list-toolbar">
        <div className="list-filters">
          <div className="installation-list-search">
            <input
              className="search-input"
              placeholder="Szukaj zlecenia, kontrahenta lub notatki…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setSearchFocused(true);
                setCalendarScheduleInstallation(null);
                setCalendarScheduleAnchor(null);
              }}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
            />
            <OrderNumberSuggestions
              query={search}
              installations={installations}
              visible={searchFocused}
              onSelect={(installation) => {
                setSearch(isCalendarNote(installation)
                  ? installation.calendar_note_title || installation.notes || ''
                  : installation.order_number);
                setSearchFocused(false);
                setCalendarScheduleInstallation(null);
                setCalendarScheduleAnchor(null);
              }}
              onAddToCalendar={canManage ? (installation, anchorRect) => {
                if (installation.planned_start_date) return;
                setSearch(installation.order_number);
                setSearchFocused(false);
                setCalendarScheduleInstallation(installation);
                setCalendarScheduleAnchor(anchorRect);
              } : undefined}
            />
            {calendarScheduleInstallation && (
              <ListCalendarScheduleMenu
                key={calendarScheduleInstallation.id}
                installation={calendarScheduleInstallation}
                teams={teams}
                anchorRect={calendarScheduleAnchor}
                onClose={() => {
                  setCalendarScheduleInstallation(null);
                  setCalendarScheduleAnchor(null);
                }}
                onScheduled={(scheduledInstallation) => {
                  setInstallations((current) => replaceInstallation(
                    current,
                    scheduledInstallation,
                    () => true
                  ));
                }}
              />
            )}
          </div>
          <select value={filterState} onChange={(e) => setFilterState(e.target.value)}>
            <option value="">Wszystkie STAN</option>
            {states.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={filterPzl} onChange={(e) => setFilterPzl(e.target.value)}>
            <option value="all">Wszystkie zlecenia</option>
            <option value="without">Bez PZL</option>
            <option value="only">Tylko PZL</option>
          </select>
        </div>
      </div>

      {canManage && (
        <div className="list-actions">
          <button
            className="btn-secondary"
            style={{ flex: 'none', width: 'auto', whiteSpace: 'nowrap' }}
            onClick={() => setShowImportModal(true)}
          >
            Import XLSX
          </button>
          <button
            className="btn-secondary"
            style={{ flex: 'none', width: 'auto', whiteSpace: 'nowrap' }}
            onClick={() => setShowReceivedImportModal(true)}
          >
            Przysłano
          </button>
          <div className="status-filter-container">
            <label className="status-filter-label">Filtruj STATUS:</label>
            <div className="status-filter-checkboxes">
              {Object.entries(STATUS_COLORS).map(([key, value]) => (
                <label key={key} className="status-filter-checkbox">
                  <input
                    type="checkbox"
                    checked={filterCalendarStatuses.has(key)}
                    onChange={() => toggleCalendarStatusFilter(key)}
                  />
                  <span>{value.label}</span>
                </label>
              ))}
            </div>
          </div>
          <button
            className="btn-primary"
            style={{ flex: 'none', width: 'auto', whiteSpace: 'nowrap' }}
            onClick={() => setShowAddModal(true)}
          >+ Dodaj montaż</button>
        </div>
      )}

      {importResult && (
        <div className={`import-result ${importResult.errors?.length ? 'error' : 'success'}`}>
          {!importResult.errors?.length ? (
            <>
              <div className="import-summary-title">✓ Import zakończony</div>
              <div className="import-summary-grid">
                <span>Nowe zlecenia: <strong>{importResult.created}</strong></span>
                <span>Zaktualizowane zlecenia: <strong>{importResult.updated}</strong></span>
                <span>Wgrane pozycje: <strong>{importResult.items_created}</strong></span>
                <span>
                  USL dodano w: <strong>{importResult.orders_by_item_type?.USL || 0}</strong> zleceniach
                  {' '}(<strong>{importResult.item_type_counts?.USL || 0}</strong> pozycji)
                </span>
              </div>
              {Object.keys(importResult.item_type_counts || {}).length > 0 && (
                <div className="import-type-summary">
                  Rodzaje pozycji:{' '}
                  {Object.entries(importResult.item_type_counts)
                    .sort(([typeA], [typeB]) => {
                      if (typeA === 'USL') return -1;
                      if (typeB === 'USL') return 1;
                      return typeA.localeCompare(typeB, 'pl');
                    })
                    .map(([type, count]) => (
                      <span key={type} className="import-type-pill">
                        {type}: {count} poz. / {importResult.orders_by_item_type?.[type] || 0} zlec.
                      </span>
                    ))}
                </div>
              )}
              {importResult.warnings?.length > 0 && (
                <div className="import-warnings">Uwagi: {importResult.warnings.join(', ')}</div>
              )}
            </>
          ) : (
            <>✗ Błędy: {importResult.errors.join(', ')}</>
          )}
        </div>
      )}

      {showImportModal && (
        <PlannerImportModal
          onClose={() => setShowImportModal(false)}
          onImported={handlePlannerImport}
        />
      )}

      {showReceivedImportModal && (
        <ReceivedPositionsImportModal
          onClose={() => setShowReceivedImportModal(false)}
          onImported={handlePlannerImport}
        />
      )}

      {pagination}

      {loading ? (
        <div className="loading-text">Ładowanie…</div>
      ) : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{width: '28px'}}></th>
                <th>Zlecenie</th>
                <th>Kontrahent</th>
                <th>STAN</th>
                <th>Adres</th>
                <th className="departure-date-cell">Data wyjazdu</th>
                <th>Realizacja</th>
                <th>Planowana data produkcji</th>
                <th>WH</th>
                <th>Status</th>
                {showUslTotal && <th>Suma USL</th>}
                <th className="positions-count-cell">Pozycje</th>
                {canManage && <th>Akcje</th>}
              </tr>
            </thead>
            <tbody>
              {visibleInstallations.length === 0 && (
                <tr>
                  <td colSpan={COL_COUNT} className="no-data-cell">
                    Brak montaży spełniających wybrane filtry
                  </td>
                </tr>
              )}
              {visibleInstallations.map((inst) => {
                const expanded = expandedIds.has(inst.id);
                const items = inst.items || [];
                return (
                  <React.Fragment key={inst.id}>
                    <tr
                      className={`inst-row ${editId === inst.id ? 'editing' : ''} ${expanded ? 'inst-row--expanded' : ''} ${calendarScheduleInstallation?.id === inst.id ? 'inst-row--context-target' : ''}`}
                      onClick={() => editId !== inst.id && toggleExpand(inst.id)}
                      onContextMenu={canManage ? (event) => openCalendarScheduleMenu(event, inst) : undefined}
                      title={canManage ? (inst.planned_start_date
                        ? 'Zlecenie już zaplanowane — zmień przypisanie w kalendarzu'
                        : 'Kliknij prawym przyciskiem, aby przypisać do kalendarza') : undefined}
                      style={{ cursor: editId === inst.id ? 'default' : 'pointer' }}
                    >
                      <td className="expand-cell" onClick={(e) => { e.stopPropagation(); toggleExpand(inst.id); }}>
                        <span className="expand-icon">{expanded ? '▾' : '▸'}</span>
                      </td>
                      <td className="order-cell">
                        {installationSearchLabel(inst)}
                        {isCalendarNote(inst) && <div className="order-number-suggestion-note">{inst.notes}</div>}
                      </td>
                      <td>{inst.contractor || '—'}</td>
                      <td>
                        {editId === inst.id
                          ? <input value={editForm.state} onChange={(e) => setEditForm({ ...editForm, state: e.target.value })} onClick={(e) => e.stopPropagation()} style={{width: '80px'}} />
                          : <span className="state-badge">{inst.state || '—'}</span>}
                      </td>
                      <td className="address-cell">
                        {editId === inst.id
                          ? <input value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} onClick={(e) => e.stopPropagation()} style={{width: '180px'}} />
                          : inst.address || '—'}
                      </td>
                      <td className="departure-date-cell">
                        {editId === inst.id
                          ? <input value={editForm.departure_date} onChange={(e) => setEditForm({ ...editForm, departure_date: e.target.value })} onClick={(e) => e.stopPropagation()} style={{width: '110px'}} />
                          : inst.departure_date || '—'}
                      </td>
                      <td>
                        {editId === inst.id
                          ? <input value={editForm.realization} onChange={(e) => setEditForm({ ...editForm, realization: e.target.value })} onClick={(e) => e.stopPropagation()} style={{width: '120px'}} />
                          : inst.realization || '—'}
                      </td>
                      <td>
                        {editId === inst.id
                          ? <input value={editForm.planned_production_date} onChange={(e) => setEditForm({ ...editForm, planned_production_date: e.target.value })} onClick={(e) => e.stopPropagation()} style={{width: '110px'}} />
                          : inst.planned_production_date || '—'}
                      </td>
                      <td>
                        {editId === inst.id
                          ? <input value={editForm.wh} onChange={(e) => setEditForm({ ...editForm, wh: e.target.value })} onClick={(e) => e.stopPropagation()} style={{width: '80px'}} />
                          : inst.wh || getWh(inst)}
                      </td>
                      <td>
                        <span
                          className="status-pill"
                          style={{
                            background: STATUS_COLORS[inst.calendar_status]?.bg,
                            borderColor: STATUS_COLORS[inst.calendar_status]?.border,
                          }}
                        >
                          {STATUS_COLORS[inst.calendar_status]?.label || inst.calendar_status}
                        </span>
                      </td>
                      {showUslTotal && <td className="numeric-cell">{getTotalUsl(inst)} PLN</td>}
                      <td className="numeric-cell positions-count-cell">{items.length}</td>
                      {canManage && (
                        <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
                          {editId === inst.id ? (
                            <div className="list-row-actions">
                              <button className="btn-success btn-sm" title="Zapisz" aria-label="Zapisz" onClick={() => saveEdit(inst.id)}>✓</button>
                              <button className="btn-secondary btn-sm" title="Anuluj" aria-label="Anuluj" onClick={() => setEditId(null)}>✕</button>
                            </div>
                          ) : (
                            <div className="list-row-actions">
                              <button className="btn-secondary btn-sm" title="Edytuj" aria-label="Edytuj" onClick={() => startEdit(inst)}>✎</button>
                              <button className="btn-danger btn-sm" title="Usuń" aria-label="Usuń" onClick={() => handleDelete(inst.id)}>🗑</button>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>

                    {expanded && (
                      <tr className="inst-items-row">
                        <td colSpan={COL_COUNT} className="inst-items-cell">
                          <table className="items-subtable">
                            <thead>
                              <tr>
                                <th className="center-cell">#</th>
                                <th className="center-cell">Zakres</th>
                                <th 
                                  className="center-cell sortable-header" 
                                  onClick={() => handleItemSort('product_type')}
                                  style={{ cursor: 'pointer', userSelect: 'none' }}
                                >
                                  Rodzaj {itemSortBy === 'product_type' && (itemSortOrder === 'asc' ? '▲' : '▼')}
                                </th>
                                <th 
                                  className="center-cell sortable-header" 
                                  onClick={() => handleItemSort('name')}
                                  style={{ cursor: 'pointer', userSelect: 'none' }}
                                >
                                  Nazwa {itemSortBy === 'name' && (itemSortOrder === 'asc' ? '▲' : '▼')}
                                </th>
                                <th className="center-cell">Ilość</th>
                                <th className="center-cell">Obwód</th>
                                <th className="center-cell">Suma obwodów</th>
                                {canViewSettlement && <th className="center-cell">USL</th>}
                                {canManage && <th></th>}
                              </tr>
                            </thead>
                            <tbody>
                              {sortItems(items).map((item, idx) => (
                                <tr key={item.id}>
                                  <td className="center-cell">{idx + 1}</td>
                                  {editItemId === item.id ? (
                                    <>
                                      <td><input className="item-input" value={editItemForm.scope} onChange={e => setEditItemForm(f => ({...f, scope: e.target.value}))} /></td>
                                      <td><input className="item-input" value={editItemForm.product_type} onChange={e => setEditItemForm(f => ({...f, product_type: e.target.value}))} /></td>
                                      <td><input className="item-input" value={editItemForm.name} onChange={e => setEditItemForm(f => ({...f, name: e.target.value}))} /></td>
                                      <td><input className="item-input numeric-input" inputMode="decimal" value={editItemForm.quantity} onChange={e => setEditItemForm(f => ({...f, quantity: e.target.value}))} /></td>
                                      <td><input className="item-input numeric-input" inputMode="decimal" value={editItemForm.perimeter} onChange={e => setEditItemForm(f => ({...f, perimeter: e.target.value}))} /></td>
                                      <td><input className="item-input numeric-input" inputMode="decimal" value={editItemForm.perimeter_sum} onChange={e => setEditItemForm(f => ({...f, perimeter_sum: e.target.value}))} /></td>
                                      {canViewSettlement && <td><input className="item-input numeric-input" inputMode="decimal" value={editItemForm.net_price_pln} onChange={e => setEditItemForm(f => ({...f, net_price_pln: e.target.value}))} /></td>}
                                      <td>
                                        <button className="item-action-btn confirm" onClick={() => saveEditItem(item.id, inst.id)}>✓</button>
                                        <button className="item-action-btn cancel" onClick={() => setEditItemId(null)}>✕</button>
                                      </td>
                                    </>
                                  ) : (
                                    <>
                                      <td className="center-cell">{item.scope || '—'}</td>
                                      <td className="center-cell">{item.product_type || '—'}</td>
                                      <td className="center-cell">{item.name || '—'}</td>
                                      <td className="center-cell">{item.quantity ?? '—'}</td>
                                      <td className="center-cell">{item.perimeter ?? '—'}</td>
                                      <td className="center-cell">{item.perimeter_sum ?? '—'}</td>
                                      {canViewSettlement && <td className="center-cell">
                                        {(item.product_type || '').trim().toUpperCase() === 'USL'
                                          ? (item.net_price_pln ?? '—')
                                          : '—'}
                                      </td>}
                                      {canManage && (
                                        <td>
                                          <button className="item-action-btn edit" onClick={() => startEditItem(item)}>✎</button>
                                          <button className="item-action-btn delete" onClick={() => handleDeleteItem(item.id)}>✕</button>
                                        </td>
                                      )}
                                    </>
                                  )}
                                </tr>
                              ))}
                              {items.length > 0 && (
                                <tr className="items-subtable-summary">
                                  <td colSpan={4} className="center-cell"><strong>Suma</strong></td>
                                  <td className="center-cell">
                                    <strong>{items.reduce((s, i) => s + (i.quantity || 0), 0).toFixed(2)}</strong>
                                  </td>
                                  <td className="center-cell">
                                    <strong>{items.reduce((s, i) => s + (i.perimeter || 0), 0).toFixed(2)}</strong>
                                  </td>
                                  <td className="center-cell">
                                    <strong>{items.reduce((s, i) => s + (i.perimeter_sum || 0), 0).toFixed(2)}</strong>
                                  </td>
                                  {canViewSettlement && <td className="center-cell">
                                    <strong>{Number(inst.total_usl_value ?? 0).toFixed(2)}</strong>
                                  </td>}
                                  {canManage && <td></td>}
                                </tr>
                              )}
                              {canManage && addingItemForId === inst.id && (
                                <tr className="items-subtable-add-row">
                                  <td className="center-cell" style={{ color: '#aaa', fontSize: '12px' }}>*</td>
                                  <td><input className="item-input" placeholder="Zakres" value={newItemForm.scope} onChange={e => setNewItemForm(f => ({...f, scope: e.target.value}))} /></td>
                                  <td><input className="item-input" placeholder="Rodzaj" value={newItemForm.product_type} onChange={e => setNewItemForm(f => ({...f, product_type: e.target.value}))} /></td>
                                  <td><input className="item-input" placeholder="Nazwa" value={newItemForm.name} onChange={e => setNewItemForm(f => ({...f, name: e.target.value}))} /></td>
                                  <td><input className="item-input numeric-input" inputMode="decimal" placeholder="0" value={newItemForm.quantity} onChange={e => setNewItemForm(f => ({...f, quantity: e.target.value}))} /></td>
                                  <td><input className="item-input numeric-input" inputMode="decimal" placeholder="0" value={newItemForm.perimeter} onChange={e => setNewItemForm(f => ({...f, perimeter: e.target.value}))} /></td>
                                  <td><input className="item-input numeric-input" inputMode="decimal" placeholder="0" value={newItemForm.perimeter_sum} onChange={e => setNewItemForm(f => ({...f, perimeter_sum: e.target.value}))} /></td>
                                  {canViewSettlement && <td><input className="item-input numeric-input" inputMode="decimal" placeholder="0" value={newItemForm.net_price_pln} onChange={e => setNewItemForm(f => ({...f, net_price_pln: e.target.value}))} /></td>}
                                  <td>
                                    <button className="item-action-btn confirm" onClick={() => saveNewItem(inst.id)}>✓</button>
                                    <button className="item-action-btn cancel" onClick={() => setAddingItemForId(null)}>✕</button>
                                  </td>
                                </tr>
                              )}
                              {canManage && addingItemForId !== inst.id && (
                                <tr>
                                  <td colSpan={7 + Number(canViewSettlement) + Number(canManage)} style={{ paddingTop: '6px' }}>
                                    <button className="btn-secondary btn-sm" style={{ fontSize: '12px' }} onClick={() => startAddItem(inst.id)}>+ Dodaj pozycję</button>
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {pagination}

      {showAddModal && (
        <AddInstallationModal
          onClose={() => setShowAddModal(false)}
          onSaved={fetchData}
        />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  Tab 3 – Pracownicy i ekipy  (completely redesigned)
// ══════════════════════════════════════════════════════════════

// ── Vacation mini-calendar ──────────────────────────────────
function VacationMiniCalendar({ year, month, vacations, onDayClick, selectStart, selectEnd }) {
  const firstDay  = new Date(year, month, 1);
  const lastDay   = new Date(year, month + 1, 0);
  const startGrid = startOfWeek(firstDay);
  const days = [];
  let d = new Date(startGrid);
  while (d <= lastDay || days.length % 7 !== 0) {
    days.push(new Date(d));
    d = addDays(d, 1);
    if (days.length > 42) break;
  }

  // Normalize selection range so selA <= selB
  const selA = selectStart && selectEnd
    ? (selectStart <= selectEnd ? selectStart : selectEnd)
    : selectStart || null;
  const selB = selectStart && selectEnd
    ? (selectStart <= selectEnd ? selectEnd : selectStart)
    : null;

  function getDayClass(day) {
    const s = toISODate(day);
    const inMonth = day.getMonth() === month;
    const classes = ['vac-cal-day'];
    if (!inMonth) classes.push('out-month');

    // Selection highlight (takes priority)
    if (inMonth && selA) {
      if (selB) {
        if (s === selA) classes.push('sel-start');
        else if (s === selB) classes.push('sel-end');
        else if (s > selA && s < selB) classes.push('sel-range');
      } else if (s === selA) {
        classes.push('sel-start');
      }
    }

    // Existing vacation coloring (only if not in selection highlight)
    if (!selA || (!selB && s !== selA) || (selB && (s < selA || s > selB))) {
      for (const v of vacations) {
        if (s >= v.start_date && s <= v.end_date) {
          classes.push(v.approved ? 'vac-approved' : 'vac-pending');
          break;
        }
      }
    }

    if (inMonth && onDayClick) classes.push('vac-cal-clickable');
    return classes.join(' ');
  }

  return (
    <div className="vac-cal-grid">
      {POLISH_DAYS_SHORT.map((n) => <div key={n} className="vac-cal-head">{n}</div>)}
      {days.map((day) => {
        const inMonth = day.getMonth() === month;
        return (
          <div
            key={toISODate(day)}
            className={getDayClass(day)}
            onClick={() => inMonth && onDayClick && onDayClick(toISODate(day))}
          >
            {day.getDate()}
          </div>
        );
      })}
    </div>
  );
}

// ── Vacation drawer (shown when clicking the calendar icon on an employee) ──
function VacationDrawer({ installer, onClose, canManage, onVacationChange }) {
  const [vacations, setVacations] = useState([]);
  const [calYear, setCalYear]   = useState(new Date().getFullYear());
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [selectStart, setSelectStart] = useState('');
  const [selectEnd, setSelectEnd]     = useState('');
  const [vacationType, setVacationType] = useState('UW');
  const [notes, setNotes]             = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await installationService.listVacations(installer.id);
      setVacations(data);
    } catch (e) { console.error(e); }
  }, [installer.id]);

  useEffect(() => { load(); }, [load]);

  function handleDayClick(dateStr) {
    if (!selectStart || (selectStart && selectEnd)) {
      // Start new selection
      setSelectStart(dateStr);
      setSelectEnd('');
    } else {
      // Complete range
      if (dateStr < selectStart) {
        setSelectEnd(selectStart);
        setSelectStart(dateStr);
      } else if (dateStr === selectStart) {
        // single day range
        setSelectEnd(dateStr);
      } else {
        setSelectEnd(dateStr);
      }
    }
    setErr('');
  }

  async function handleAdd() {
    if (!selectStart || !selectEnd) { setErr('Zaznacz zakres dni na kalendarzu'); return; }
    setSaving(true); setErr('');
    try {
      const [a, b] = selectStart <= selectEnd ? [selectStart, selectEnd] : [selectEnd, selectStart];
      await installationService.createVacation(installer.id, {
        start_date: a,
        end_date: b,
        vacation_type: vacationType,
        notes,
      });
      setSelectStart('');
      setSelectEnd('');
      setNotes('');
      await load();
      onVacationChange && onVacationChange();
    } catch (e) { setErr(e.response?.data?.detail || 'Błąd'); }
    finally { setSaving(false); }
  }

  async function handleApprove(id, approved) {
    try {
      await installationService.updateVacation(id, { approved });
      await load();
      onVacationChange && onVacationChange();
    } catch (e) { setErr(e.response?.data?.detail || 'Błąd'); }
  }

  async function handleDelete(id) {
    try {
      await installationService.deleteVacation(id);
      await load();
      onVacationChange && onVacationChange();
    } catch (e) { setErr(e.response?.data?.detail || 'Błąd'); }
  }

  function prevMonth() {
    if (calMonth === 0) { setCalMonth(11); setCalYear(calYear - 1); }
    else setCalMonth(calMonth - 1);
  }
  function nextMonth() {
    if (calMonth === 11) { setCalMonth(0); setCalYear(calYear + 1); }
    else setCalMonth(calMonth + 1);
  }

  const approved = vacations.filter((v) => v.approved);
  const pending  = vacations.filter((v) => !v.approved);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="vac-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="vac-drawer-header">
          <div>
            <span className="vac-drawer-title">Urlopy</span>
            <span className="vac-drawer-name">{installer.first_name} {installer.last_name}</span>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {err && <div className="modal-error">{err}</div>}

        {/* Mini calendar */}
        <div className="vac-cal-section">
          <div className="vac-cal-nav">
            <button className="btn-nav" onClick={prevMonth}>‹</button>
            <span className="vac-cal-label">{POLISH_MONTHS[calMonth]} {calYear}</span>
            <button className="btn-nav" onClick={nextMonth}>›</button>
          </div>
          {canManage && (
            <div className="vac-cal-hint">
              {!selectStart
                ? 'Kliknij dzień startowy urlopu'
                : !selectEnd
                ? 'Kliknij dzień końcowy urlopu'
                : <span className="vac-sel-preview">
                    Od <strong>{selectStart}</strong> do <strong>{selectEnd}</strong>
                    <button className="vac-btn-clear" onClick={() => { setSelectStart(''); setSelectEnd(''); }}>✕</button>
                  </span>
              }
            </div>
          )}
          <div className="vac-cal-legend">
            <span className="vac-legend-dot vac-approved" /> Zatwierdzone
          </div>
          <VacationMiniCalendar
            year={calYear} month={calMonth} vacations={vacations}
            onDayClick={canManage ? handleDayClick : null}
            selectStart={selectStart} selectEnd={selectEnd}
          />
        </div>

        {/* Vacation list */}
        <div className="vac-list-section">
          <h4 className="vac-section-title">Lista urlopów</h4>
          {vacations.length === 0 && <p className="no-data-text">Brak urlopów</p>}

          {approved.length > 0 && (
            <>
              <div className="vac-group-label approved">Zatwierdzone</div>
              {approved.map((v) => (
                <div key={v.id} className="vac-item vac-item--approved">
                  <div className="vac-item-dates">
                    <VacationTypeBadge type={v.vacation_type} />
                    <span>{v.start_date}</span>
                    {v.start_date !== v.end_date && <><span className="vac-dash">→</span><span>{v.end_date}</span></>}
                  </div>
                  {v.notes && <div className="vac-item-notes">{v.notes}</div>}
                  <div className="vac-item-actions">
                    {canManage && (
                      <button className="btn-icon-remove" onClick={() => handleDelete(v.id)}>✕</button>
                    )}
                  </div>
                </div>
              ))}
            </>
          )}

          {pending.length > 0 && (
            <>
              <div className="vac-group-label pending">Oczekujące</div>
              {pending.map((v) => (
                <div key={v.id} className="vac-item vac-item--pending">
                  <div className="vac-item-dates">
                    <VacationTypeBadge type={v.vacation_type} />
                    <span>{v.start_date}</span>
                    {v.start_date !== v.end_date && <><span className="vac-dash">→</span><span>{v.end_date}</span></>}
                  </div>
                  {v.notes && <div className="vac-item-notes">{v.notes}</div>}
                  <div className="vac-item-actions">
                    {canManage && (
                      <button className="vac-btn-approve" onClick={() => handleApprove(v.id, true)}
                        title="Zatwierdź">✓</button>
                    )}
                    {canManage && (
                      <button className="btn-icon-remove" onClick={() => handleDelete(v.id)}>✕</button>
                    )}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Add vacation form */}
        {canManage && (
          <div className="vac-add-section">
            <h4 className="vac-section-title">Dodaj urlop</h4>
            <div className="vac-add-form">
              <label>Typ nieobecności
                <select value={vacationType} onChange={(e) => setVacationType(e.target.value)}>
                  {Object.entries(VACATION_TYPES).map(([code, label]) => (
                    <option key={code} value={code}>{code} — {label}</option>
                  ))}
                </select>
              </label>
              <label>Notatka
                <input value={notes} placeholder="opcjonalnie"
                  onChange={(e) => setNotes(e.target.value)} />
              </label>
              <button className="btn-primary btn-sm" onClick={handleAdd} disabled={saving || !selectStart || !selectEnd}>
                {saving ? '…' : '+ Dodaj'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Employee add/edit modal ─────────────────────────────────
export function EmployeeModal({ employee, positions, onClose, onSaved }) {
  const isNew = !employee?.id;
  const [form, setForm] = useState({
    first_name: employee?.first_name ?? '',
    last_name: employee?.last_name ?? '',
    position: employee?.position ?? '',
    position_id: employee?.position_id ?? '',
    works_near_home: employee?.works_near_home ?? false,
    under_26: employee?.under_26 ?? false,
    kat_b: employee?.kat_b ?? false,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  async function handleSave() {
    if (!form.first_name || !form.last_name) { setErr('Imię i nazwisko są wymagane'); return; }
    setSaving(true); setErr('');
    try {
      const payload = {
        ...form,
        position_id: form.position_id ? Number(form.position_id) : null,
      };
      if (isNew) await installationService.createInstaller(payload);
      else await installationService.updateInstaller(employee.id, payload);
      onSaved();
      onClose();
    } catch (e) { setErr(e.response?.data?.detail || 'Błąd zapisu'); }
    finally { setSaving(false); }
  }

  // Auto-fill position text when position_id changes
  function handlePositionChange(pid) {
    const pos = positions.find((p) => p.id === Number(pid));
    setForm({ ...form, position_id: pid, position: pos ? pos.name : form.position });
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box modal-box--employee" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{isNew ? 'Nowy pracownik' : 'Edytuj pracownika'}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        {err && <div className="modal-error">{err}</div>}
        <div className="modal-body">
          <div className="form-grid">
            <label>Imię *
              <input value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
            </label>
            <label>Nazwisko *
              <input value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
            </label>
            <label>Stanowisko
              <select value={form.position_id || ''}
                onChange={(e) => handlePositionChange(e.target.value)}>
                <option value="">-- Wybierz stanowisko --</option>
                {positions.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="emp-toggle-row">
            <label className="toggle-label">
              <input type="checkbox" checked={form.works_near_home}
                onChange={(e) => setForm({ ...form, works_near_home: e.target.checked })} />
              Pracuje blisko domu
            </label>
            <label className="toggle-label">
              <input type="checkbox" checked={form.under_26}
                onChange={(e) => setForm({ ...form, under_26: e.target.checked })} />
              Poniżej 26 lat
            </label>
            <label className="toggle-label">
              <input type="checkbox" checked={form.kat_b}
                onChange={(e) => setForm({ ...form, kat_b: e.target.checked })} />
              Kat. B
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Anuluj</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Zapisywanie…' : 'Zapisz'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function VacationRatesModal({ employee, onClose, onSaved }) {
  const [rates, setRates] = useState(Object.fromEntries(
    Object.keys(VACATION_RATE_TYPES).map((type) => [
      type,
      employee?.vacation_rates?.[type] ?? '',
    ])
  ));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  async function handleSave() {
    const vacationRates = {};
    for (const [type, rawAmount] of Object.entries(rates)) {
      if (rawAmount === '') continue;
      const amount = Number(String(rawAmount).replace(',', '.'));
      if (!Number.isFinite(amount) || amount < 0) {
        setErr(`Nieprawidłowa stawka za urlop ${type}`);
        return;
      }
      vacationRates[type] = Math.round(amount * 100) / 100;
    }

    setSaving(true);
    setErr('');
    try {
      await installationService.updateInstallerVacationRates(employee.id, vacationRates);
      onSaved();
      onClose();
    } catch (error) {
      setErr(error.response?.data?.detail || 'Błąd zapisu stawek urlopowych');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box modal-box--employee" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <h3>Stawki urlopowe: {employee.first_name} {employee.last_name}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        {err && <div className="modal-error">{err}</div>}
        <div className="modal-body">
          <div className="employee-vacation-rates">
            <div className="employee-vacation-rates-heading">
              <h4>Stawki dzienne za urlop</h4>
              <p>Kwoty zostaną doliczone za każdy zatwierdzony dzień urlopu w okresie rozliczenia.</p>
            </div>
            <div className="employee-vacation-rates-grid">
              {Object.entries(VACATION_RATE_TYPES).map(([type, label]) => (
                <label key={type}>
                  <span>{type} — {label}</span>
                  <span className="employee-vacation-rate-input">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      inputMode="decimal"
                      placeholder="0,00"
                      value={rates[type]}
                      onChange={(event) => setRates({
                        ...rates,
                        [type]: event.target.value,
                      })}
                    />
                    <span>PLN / dzień</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Anuluj</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Zapisywanie…' : 'Zapisz'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Positions section ───────────────────────────────────────
function PositionsSection({ canManage, positions, onRefresh }) {
  const [newPos, setNewPos]   = useState({ name: '' });
  const [editPos, setEditPos] = useState(null);
  const [err, setErr] = useState('');

  async function handleCreate() {
    if (!newPos.name) { setErr('Nazwa jest wymagana'); return; }
    try {
      await installationService.createPosition(newPos);
      setNewPos({ name: '' });
      onRefresh();
    } catch (e) { setErr(e.response?.data?.detail || 'Błąd'); }
  }

  async function handleSave() {
    try {
      await installationService.updatePosition(editPos.id, { name: editPos.name });
      setEditPos(null);
      onRefresh();
    } catch (e) { setErr(e.response?.data?.detail || 'Błąd'); }
  }

  async function handleDelete(id) {
    if (!window.confirm('Usunąć stanowisko? Pracownicy stracą powiązanie.')) return;
    try {
      await installationService.deletePosition(id);
      onRefresh();
    } catch (e) { setErr(e.response?.data?.detail || 'Błąd'); }
  }

  // Move position up or down
  async function handleMove(position, direction) {
    const currentIndex = positions.findIndex(p => p.id === position.id);
    if (direction === 'up' && currentIndex === 0) return;
    if (direction === 'down' && currentIndex === positions.length - 1) return;

    const reordered = [...positions];
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    // Swap positions
    [reordered[currentIndex], reordered[targetIndex]] = [reordered[targetIndex], reordered[currentIndex]];

    // Assign new ranks (10, 20, 30, ...)
    const positionsWithRanks = reordered.map((p, idx) => ({
      id: p.id,
      rank: (idx + 1) * 10
    }));

    try {
      await installationService.reorderPositions(positionsWithRanks);
      onRefresh();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Błąd podczas zmiany kolejności');
    }
  }

  return (
    <div className="wt-section">
      <div className="wt-section-header">
        <h3 className="wt-section-title">Stanowiska</h3>
        <span className="wt-badge">{positions.length}</span>
      </div>
      {err && <div className="error-banner" onClick={() => setErr('')}>{err} ✕</div>}

      <table className="wt-table">
        <thead>
          <tr>
            {canManage && <th style={{width:70}}>Kolejność</th>}
            <th>Nazwa</th>
            <th style={{width:90}}>Pracownicy</th>
            {canManage && <th style={{width:140}}>Akcje</th>}
          </tr>
        </thead>
        <tbody>
          {positions.length === 0 && (
            <tr><td colSpan={canManage ? 4 : 2} className="wt-empty">Brak stanowisk – dodaj poniżej</td></tr>
          )}
          {positions.map((p, idx) => (
            <tr key={p.id}>
              {canManage && (
                <td>
                  <div className="position-arrows">
                    <button 
                      className="btn-arrow" 
                      onClick={() => handleMove(p, 'up')}
                      disabled={idx === 0}
                      title="Przesuń w górę"
                    >
                      ▲
                    </button>
                    <button 
                      className="btn-arrow" 
                      onClick={() => handleMove(p, 'down')}
                      disabled={idx === positions.length - 1}
                      title="Przesuń w dół"
                    >
                      ▼
                    </button>
                  </div>
                </td>
              )}
              {editPos?.id === p.id ? (
                <>
                  <td>
                    <input className="wt-inline-input"
                      value={editPos.name}
                      onChange={(e) => setEditPos({ ...editPos, name: e.target.value })} />
                  </td>
                  <td>{p.installer_count}</td>
                  <td>
                    <button className="btn-success btn-xs" onClick={handleSave}>Zapisz</button>
                    <button className="btn-secondary btn-xs" onClick={() => setEditPos(null)}>Anuluj</button>
                  </td>
                </>
              ) : (
                <>
                  <td><strong>{p.name}</strong></td>
                  <td><span className="count-chip">{p.installer_count}</span></td>
                  {canManage && (
                    <td>
                      <div className="position-actions">
                        <button className="btn-text-edit" onClick={() => setEditPos({ ...p })}>Edycja</button>
                        <button className="btn-text-remove" onClick={() => handleDelete(p.id)}>Usuń</button>
                      </div>
                    </td>
                  )}
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {canManage && (
        <div className="wt-add-row">
          <input className="wt-add-input" placeholder="Nazwa stanowiska" value={newPos.name}
            onChange={(e) => setNewPos({ ...newPos, name: e.target.value })}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()} />
          <button className="btn-primary btn-sm" onClick={handleCreate}>+ Dodaj</button>
        </div>
      )}
    </div>
  );
}

// ── Employees section ───────────────────────────────────────
export function EmployeesSection({ canManage, canViewSettlement = false, installers, positions, pendingVacations = [], allVacations = [], teams = [], onRefresh }) {
  const [search, setSearch]       = useState('');
  const [vacDrawer, setVacDrawer] = useState(null); // installer object
  const [empModal, setEmpModal]   = useState(null); // null=closed, 'new'|object
  const [rateModal, setRateModal] = useState(null);
  const [vacationListTab, setVacationListTab] = useState('all');
  const [vacationExportMonth, setVacationExportMonth] = useState(currentMonthValue);
  const [vacationExporting, setVacationExporting] = useState(null);
  const [vacationExportError, setVacationExportError] = useState('');
  const vacationTabScrollPosition = useRef(null);

  useLayoutEffect(() => {
    if (!vacationTabScrollPosition.current) return;
    const { left, top } = vacationTabScrollPosition.current;
    vacationTabScrollPosition.current = null;
    window.scrollTo({ left, top, behavior: 'auto' });
  }, [vacationListTab]);

  function handleVacationListTabChange(event, nextTab) {
    event.preventDefault();
    event.stopPropagation();
    if (nextTab === vacationListTab) return;
    vacationTabScrollPosition.current = {
      left: window.scrollX,
      top: window.scrollY,
    };
    setVacationListTab(nextTab);
  }

  async function handleDelete(id) {
    if (!window.confirm('Usunąć pracownika?')) return;
    try { await installationService.deleteInstaller(id); onRefresh(); }
    catch (e) { alert(e.response?.data?.detail || 'Błąd'); }
  }

  // Format date with day of week in Polish
  const formatDateWithDay = (dateStr) => {
    const days = ['niedziela', 'poniedziałek', 'wtorek', 'środa', 'czwartek', 'piątek', 'sobota'];
    const date = new Date(dateStr + 'T00:00:00');
    const dayName = days[date.getDay()];
    return `${dateStr} (${dayName})`;
  };

  const filtered = installers.filter((i) => {
    const matchSearch = (i.first_name + ' ' + i.last_name + ' ' + i.position)
      .toLowerCase().includes(search.toLowerCase());
    return matchSearch;
  });

  // Sort by position rank, then last name
  const sorted = [...filtered].sort((a, b) => {
    const ra = a.position_rank ?? 999;
    const rb = b.position_rank ?? 999;
    if (ra !== rb) return ra - rb;
    return a.last_name.localeCompare(b.last_name);
  });

  const vacationGroups = vacationListTab === 'all'
    ? groupVacationsByStartMonth(allVacations)
    : [{
        key: 'pending',
        label: null,
        vacations: sortVacationsNewestFirst(
          pendingVacations.filter((vacation) => !vacation.approved)
        ),
      }];
  const displayedVacationCount = vacationGroups.reduce(
    (count, group) => count + group.vacations.length,
    0
  );

  async function handleVacationExport(format) {
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(vacationExportMonth)) {
      setVacationExportError('Wybierz miesiąc eksportu.');
      return;
    }

    setVacationExporting(format);
    setVacationExportError('');
    try {
      await installationService.exportVacations(format, vacationExportMonth);
    } catch (error) {
      setVacationExportError(
        error.response?.data?.detail || 'Nie udało się wyeksportować urlopów.'
      );
    } finally {
      setVacationExporting(null);
    }
  }

  return (
    <div className="wt-section">
      <div className="wt-section-header">
        <h3 className="wt-section-title">Pracownicy</h3>
        <span className="wt-badge">{installers.length}</span>
        <div className="wt-toolbar">
          <input className="wt-search" placeholder="Szukaj…" value={search}
            onChange={(e) => setSearch(e.target.value)} />
          {canManage && (
            <button className="btn-primary btn-sm" onClick={() => setEmpModal('new')}>
              + Dodaj pracownika
            </button>
          )}
        </div>
      </div>

      <table className="wt-table wt-emp-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Imię i nazwisko</th>
            <th>Stanowisko</th>
            {canViewSettlement && <th>Stawki urlopowe</th>}
            <th title="Pracuje blisko domu">Czy blisko</th>
            <th title="Poniżej 26 lat">Czy &lt;26 lat</th>
            <th title="Prawo jazdy kat. B">Kat. B</th>
            <th style={{textAlign: 'right'}}>Akcje</th>
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 && (
            <tr><td colSpan={canViewSettlement ? 8 : 7} className="wt-empty">Brak pracowników</td></tr>
          )}
          {sorted.map((ins, idx) => (
            <tr key={ins.id}>
              <td className="col-num">{idx + 1}</td>
              <td>
                <div className="emp-name">{ins.first_name} {ins.last_name}</div>
                {ins.team_ids?.length > 0 && (
                  <div className="emp-teams-tiny">
                    {ins.team_ids.map(teamId => {
                      const team = teams.find(t => t.id === teamId);
                      return team?.name || '';
                    }).filter(Boolean).join(', ')}
                  </div>
                )}
              </td>
              <td>
                {ins.position_name ? (
                  <span className="pos-badge" style={{ '--pos-rank': ins.position_rank }}>
                    {ins.position_name}
                  </span>
                ) : (
                  <span className="pos-text">{ins.position}</span>
                )}
              </td>
              {canViewSettlement && (
                <td>
                  <div className="employee-vacation-rate-summary">
                    {Object.entries(ins.vacation_rates || {})
                      .filter(([type, amount]) => type !== 'UB' && Number(amount) > 0)
                      .map(([type, amount]) => (
                        <span key={type}>{type}: {Number(amount).toFixed(2)} PLN</span>
                      ))}
                    {!Object.entries(ins.vacation_rates || {})
                      .some(([type, amount]) => type !== 'UB' && Number(amount) > 0) && '—'}
                  </div>
                </td>
              )}
              <td className="col-bool">{ins.works_near_home ? 'TAK' : 'NIE'}</td>
              <td className="col-bool">{ins.under_26 ? 'TAK' : 'NIE'}</td>
              <td className="col-bool">{ins.kat_b ? 'TAK' : 'NIE'}</td>
              <td style={{textAlign: 'right'}}>
                <div className="emp-actions">
                  {canManage && (
                    <>
                      <button className="btn-text-edit" onClick={() => setEmpModal(ins)}>Edycja</button>
                      <button className="btn-text-calendar" onClick={() => setVacDrawer(ins)}>Dodaj urlop</button>
                      <button className="btn-text-remove" onClick={() => handleDelete(ins.id)}>Usuń</button>
                    </>
                  )}
                  {canViewSettlement && (
                    <button className="btn-text-edit" onClick={() => setRateModal(ins)}>
                      Stawki urlopowe
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {vacDrawer && (
        <VacationDrawer
          installer={vacDrawer}
          canManage={canManage}
          onClose={() => setVacDrawer(null)}
          onVacationChange={onRefresh}
        />
      )}

      {empModal && (
        <EmployeeModal
          employee={empModal === 'new' ? null : empModal}
          positions={positions}
          onClose={() => setEmpModal(null)}
          onSaved={onRefresh}
        />
      )}

      {rateModal && canViewSettlement && (
        <VacationRatesModal
          employee={rateModal}
          onClose={() => setRateModal(null)}
          onSaved={onRefresh}
        />
      )}

      {/* Vacation lists */}
      {canManage && (
        <div className="vacations-list" style={{ marginTop: '32px' }}>
        <div className="vacations-tabs" role="tablist" aria-label="Zakres wyświetlanych urlopów">
          <button
            type="button"
            role="tab"
            aria-selected={vacationListTab === 'all'}
            className={`vacations-tab ${vacationListTab === 'all' ? 'active' : ''}`}
            onClick={(event) => handleVacationListTabChange(event, 'all')}
          >
            Wszystkie urlopy
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={vacationListTab === 'pending'}
            className={`vacations-tab ${vacationListTab === 'pending' ? 'active' : ''}`}
            onClick={(event) => handleVacationListTabChange(event, 'pending')}
          >
            Niezatwierdzone urlopy
          </button>
        </div>
        {vacationListTab === 'all' && canManage && (
          <div className="vacation-export-panel">
            <div className="vacation-export-heading">
              <strong>Eksport zatwierdzonych urlopów</strong>
              <span>Wybierz miesiąc, który ma znaleźć się w zestawieniu.</span>
            </div>
            <div className="vacation-export-controls">
              <label>
                Miesiąc
                <input
                  type="month"
                  value={vacationExportMonth}
                  onChange={(event) => setVacationExportMonth(event.target.value)}
                  disabled={Boolean(vacationExporting)}
                />
              </label>
              <button
                type="button"
                className="btn-secondary vacation-export-button"
                onClick={() => handleVacationExport('pdf')}
                disabled={Boolean(vacationExporting)}
              >
                <Download size={15} aria-hidden="true" />
                {vacationExporting === 'pdf' ? 'Generowanie…' : 'Eksport PDF'}
              </button>
              <button
                type="button"
                className="btn-primary vacation-export-button"
                onClick={() => handleVacationExport('xlsx')}
                disabled={Boolean(vacationExporting)}
              >
                <Download size={15} aria-hidden="true" />
                {vacationExporting === 'xlsx' ? 'Generowanie…' : 'Eksport XLSX'}
              </button>
            </div>
            {vacationExportError && (
              <div className="vacation-export-error" role="alert">
                {vacationExportError}
              </div>
            )}
          </div>
        )}
        {displayedVacationCount === 0 ? (
          <p className="vacations-empty">
            {vacationListTab === 'all' ? 'Brak urlopów' : 'Brak niezatwierdzonych urlopów'}
          </p>
        ) : (
          <table className="wt-table">
            <thead>
              <tr>
                <th>Pracownik</th>
                <th>Od</th>
                <th>Do</th>
                <th>Typ</th>
                <th>Uwagi</th>
                <th>Status</th>
                {canManage && <th></th>}
              </tr>
            </thead>
            <tbody>
              {vacationGroups.map((group) => (
                <React.Fragment key={group.key}>
                  {group.label && (
                    <tr className="vacation-month-row">
                      <td colSpan={canManage ? 7 : 6}>{group.label}</td>
                    </tr>
                  )}
                  {group.vacations.map((vac) => (
                    <tr key={vac.id}>
                  <td><strong>{vac.installer_name} {vac.installer_surname}</strong></td>
                  <td>{formatDateWithDay(vac.start_date)}</td>
                  <td>{formatDateWithDay(vac.end_date)}</td>
                  <td>
                    <VacationTypeBadge type={vac.vacation_type} />{' '}
                    <span className="vac-type-label">{VACATION_TYPES[vac.vacation_type || 'UW']}</span>
                  </td>
                  <td>{vac.notes || '—'}</td>
                  <td>
                    {vac.approved ? (
                      <span style={{ background: '#bbf7d0', color: '#166534', borderRadius: '999px', padding: '2px 10px', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap' }}>Zatwierdzony</span>
                    ) : (
                      <span style={{ background: '#fef08a', color: '#854d0e', borderRadius: '999px', padding: '2px 10px', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap' }}>Niezatwierdzony</span>
                    )}
                  </td>
                  {canManage && (
                    <td>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        {!vac.approved && (
                          <button
                            className="btn-text-edit"
                            onClick={async () => {
                              try {
                                await installationService.updateVacation(vac.id, { approved: true });
                                onRefresh();
                              } catch (e) {
                                alert(e.response?.data?.detail || 'Błąd zatwierdzania');
                              }
                            }}
                          >
                            Zatwierdź
                          </button>
                        )}
                        <button
                          className="btn-text-remove"
                          onClick={async () => {
                            if (!window.confirm('Usunąć urlop?')) return;
                            try {
                              await installationService.deleteVacation(vac.id);
                              onRefresh();
                            } catch (e) {
                              alert(e.response?.data?.detail || 'Błąd usuwania');
                            }
                          }}
                        >
                          Usuń
                        </button>
                      </div>
                    </td>
                  )}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
        </div>
      )}
    </div>
  );
}

// ── Teams section ───────────────────────────────────────────
function TeamsSection({ canManage, teams, installers, onRefresh }) {
  const [newTeam, setNewTeam]   = useState({ name: '' });
  const [editTeam, setEditTeam] = useState(null);
  const [err, setErr] = useState('');

  async function handleCreate() {
    if (!newTeam.name) { setErr('Nazwa ekipy jest wymagana'); return; }
    try {
      await installationService.createTeam({ name: newTeam.name });
      setNewTeam({ name: '' });
      onRefresh();
    } catch (e) { setErr(e.response?.data?.detail || 'Błąd'); }
  }

  async function handleSaveTeam() {
    try {
      await installationService.updateTeam(editTeam.id, { name: editTeam.name });
      setEditTeam(null);
      onRefresh();
    } catch (e) { setErr(e.response?.data?.detail || 'Błąd'); }
  }

  async function handleDeleteTeam(id) {
    if (!window.confirm('Usunąć ekipę?')) return;
    try { await installationService.deleteTeam(id); onRefresh(); }
    catch (e) { setErr(e.response?.data?.detail || 'Błąd'); }
  }

  async function handleAddMember(teamId, installerId) {
    try { await installationService.addTeamMember(teamId, installerId); onRefresh(); }
    catch (e) { setErr(e.response?.data?.detail || 'Błąd'); }
  }

  async function handleRemoveMember(teamId, installerId) {
    try { await installationService.removeTeamMember(teamId, installerId); onRefresh(); }
    catch (e) { setErr(e.response?.data?.detail || 'Błąd'); }
  }

  return (
    <div className="wt-section">
      <div className="wt-section-header">
        <h3 className="wt-section-title">Ekipy</h3>
        <span className="wt-badge">{teams.length}</span>
      </div>
      {err && <div className="error-banner" onClick={() => setErr('')}>{err} ✕</div>}

      {canManage && (
        <div className="wt-add-row">
          <input className="wt-add-input" placeholder="Nazwa ekipy" value={newTeam.name}
            onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })} />
          <button className="btn-primary btn-sm" onClick={handleCreate}>+ Dodaj ekipę</button>
        </div>
      )}

      <div className="team-cards-grid">
        {teams.length === 0 && <p className="no-data-text">Brak ekip</p>}
        {teams.map((team) => {
          const allAssignedIds = new Set(teams.flatMap((t) => t.members.map((m) => m.id)));
          const available      = installers.filter((i) => !allAssignedIds.has(i.id));
          const isEditing      = editTeam?.id === team.id;
          const isSpecialTeam  = isSpecialSystemTeam(team);
          const hasNoMembers   = team.is_special_truck;

          return (
            <div
              key={team.id}
              className="team-card-v2"
              style={isSpecialTeam ? { borderColor: '#2563eb', boxShadow: 'inset 4px 0 0 #2563eb' } : undefined}
            >
              <div className="team-card-header">
                {isEditing ? (
                  <div className="edit-form-inline" style={{ flex: 1 }}>
                    <input value={editTeam.name}
                      onChange={(e) => setEditTeam({ ...editTeam, name: e.target.value })} />
                    <button className="btn-success btn-xs" onClick={handleSaveTeam}>✓</button>
                    <button className="btn-secondary btn-xs" onClick={() => setEditTeam(null)}>✕</button>
                  </div>
                ) : (
                  <>
                    <div className="team-name-block">
                      <span className="team-name-v2">{team.name}</span>
                      {isSpecialTeam && (
                        <span
                          style={{
                            marginLeft: '8px',
                            padding: '2px 8px',
                            borderRadius: '999px',
                            background: '#dbeafe',
                            color: '#1d4ed8',
                            fontSize: '11px',
                            fontWeight: 700,
                          }}
                        >
                          specjalna
                        </span>
                      )}
                      {team.kat_b && (
                        <span className="badge badge--katb" title="Ekipa ma kierowcę z prawem jazdy kat. B">
                          Kat. B
                        </span>
                      )}
                    </div>
                    {canManage && (
                      <div className="team-card-actions">
                        <button className="btn-icon-edit" onClick={() => setEditTeam({ ...team })}>Edycja</button>
                        {!isSpecialTeam && (
                          <button className="btn-icon-remove" onClick={() => handleDeleteTeam(team.id)}>Usuń</button>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="team-member-count">
                {hasNoMembers
                  ? 'Ekipa specjalna bez członków'
                  : `${team.members.length} ${team.members.length === 1 ? 'członek' : 'członków'}`}
              </div>

              {!hasNoMembers && (
                <div className="team-members-v2">
                  {team.members.length === 0 && (
                    <div className="no-data-text" style={{fontSize:12}}>Brak członków – dodaj poniżej</div>
                  )}
                  {team.members.map((m, idx) => (
                    <div key={m.id} className="member-row-v2">
                      <span className="member-rank-num">{idx + 1}</span>
                      <div className="member-info-v2">
                        <span className="member-name-v2">{m.first_name} {m.last_name}</span>
                        <span className="member-pos-v2">{m.position}</span>
                      </div>
                      <div className="member-badges">
                        {m.works_near_home && <span className="badge badge--home" title="Pracuje blisko domu">DOM</span>}
                        {m.under_26       && <span className="badge badge--age"  title="Poniżej 26 lat">26-</span>}
                        {m.kat_b          && <span className="badge badge--katb" title="Prawo jazdy kat. B">B</span>}
                      </div>
                      {canManage && (
                        <button className="btn-icon-remove btn-xs"
                          onClick={() => handleRemoveMember(team.id, m.id)}>✕</button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {canManage && !hasNoMembers && available.length > 0 && (
                <div className="team-add-member">
                  <select defaultValue=""
                    onChange={(e) => {
                      if (e.target.value) { handleAddMember(team.id, Number(e.target.value)); e.target.value = ''; }
                    }}>
                    <option value="">+ Dodaj pracownika…</option>
                    {available.map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.first_name} {i.last_name}{i.position_name ? ` (${i.position_name})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main WorkersTeamsTab ────────────────────────────────────
function WorkersTeamsTab({ canManage, canViewSettlement }) {
  const [installers, setInstallers] = useState([]);
  const [teams, setTeams]           = useState([]);
  const [positions, setPositions]   = useState([]);
  const [pendingVacations, setPendingVacations] = useState([]);
  const [allVacations, setAllVacations] = useState([]);
  const [loading, setLoading]       = useState(false);
  const [loaded, setLoaded]         = useState(false);
  const [activeSection, setSection] = useState('employees');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ins, t, pos, pendingVac, allVac, vacationRates] = await Promise.all([
        installationService.listInstallers(),
        installationService.listTeams(),
        installationService.listPositions(),
        canManage ? installationService.getPendingVacations() : Promise.resolve([]),
        canManage ? installationService.getAllVacations() : Promise.resolve([]),
        canViewSettlement
          ? installationService.listInstallerVacationRates()
          : Promise.resolve([]),
      ]);
      const ratesByInstaller = new Map(
        vacationRates.map((entry) => [entry.installer_id, entry.vacation_rates || {}])
      );
      setInstallers(ins.map((installer) => ({
        ...installer,
        ...(canViewSettlement
          ? { vacation_rates: ratesByInstaller.get(installer.id) || {} }
          : {}),
      })));
      setTeams(t);
      setPositions(pos);
      setPendingVacations(pendingVac);
      setAllVacations(allVac);
    } catch (e) { console.error(e); }
    finally {
      setLoading(false);
      setLoaded(true);
    }
  }, [canManage, canViewSettlement]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const SECTIONS = [
    { key: 'positions', label: 'Stanowiska', count: positions.length },
    { key: 'employees', label: 'Pracownicy', count: installers.length },
    { key: 'teams',     label: 'Ekipy',      count: teams.length },
  ];

  if (loading && !loaded) return <div className="loading-text">Ładowanie…</div>;

  return (
    <div className="wt-root">
      {/* Sub-section navigation */}
      <div className="wt-nav">
        {SECTIONS.map((s) => (
          <button
            key={s.key}
            className={`wt-nav-btn ${activeSection === s.key ? 'active' : ''}`}
            onClick={() => setSection(s.key)}
          >
            {s.label}
            <span className="wt-nav-count">{s.count}</span>
          </button>
        ))}
      </div>

      {/* Section content */}
      {activeSection === 'positions' && (
        <PositionsSection canManage={canManage} positions={positions} onRefresh={fetchAll} />
      )}
      {activeSection === 'employees' && (
        <EmployeesSection canManage={canManage} canViewSettlement={canViewSettlement} installers={installers} positions={positions} pendingVacations={pendingVacations} allVacations={allVacations} teams={teams} onRefresh={fetchAll} />
      )}
      {activeSection === 'teams' && (
        <TeamsSection canManage={canManage} teams={teams} installers={installers} onRefresh={fetchAll} />
      )}
    </div>
  );
}


// ──────────────────────────────────────────────────────────────
//  Billing Tab - Position Rates & Earnings
// ──────────────────────────────────────────────────────────────
function BillingTab({ onNavigateToPlanning }) {
  return (
    <div className="billing-tab-wrapper">
      <div className="billing-layout">
        <PositionRatesSection variant="official-positions" />
        <PzlPriceListSection />
        <EarningsSection onNavigateToPlanning={onNavigateToPlanning} />
      </div>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────
//  Main Page
// ──────────────────────────────────────────────────────────────
export default function InstallationPlanning() {
  const { canAccessMontazManage, canAccessMontazSettlement, hasOnlyMontazView } = useAuth();
  const canManage = canAccessMontazManage();
  const canViewSettlement = canAccessMontazSettlement();
  const isViewOnly = hasOnlyMontazView();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const { availableTabs, availableTabIndices, availableTabNames } = useMemo(() => {
    const tabs = [];
    const indices = [];
    const names = [];

    if (isViewOnly) {
      tabs.push(TABS[0]);
      indices.push(0);
      names.push(TAB_NAMES_URL[0]);
    } else {
      TABS.forEach((tab, index) => {
        if (index === 3 && !canViewSettlement) {
          return;
        }
        tabs.push(tab);
        indices.push(index);
        names.push(TAB_NAMES_URL[index]);
      });
    }

    return {
      availableTabs: tabs,
      availableTabIndices: indices,
      availableTabNames: names,
    };
  }, [canViewSettlement, isViewOnly]);
  
  // Initialize activeTab from URL or default to 0
  const tabFromUrl = searchParams.get('tab');
  let initialTab = 0;
  if (tabFromUrl) {
    const urlTabIndex = availableTabNames.indexOf(tabFromUrl);
    if (urlTabIndex >= 0) {
      initialTab = urlTabIndex;
    }
  }
  const [activeTab, setActiveTab] = useState(initialTab);
  const [breadcrumb, setBreadcrumb] = useState({ installation: null });

  const handleBreadcrumbChange = useCallback((installation) => {
    setBreadcrumb({ installation });
  }, []);

  const handleBreadcrumbReset = useCallback(() => {
    setBreadcrumb({ installation: null });
  }, []);

  const handleTabChange = useCallback((tabIndex) => {
    setActiveTab(tabIndex);
    setSearchParams({ tab: availableTabNames[tabIndex] });
  }, [setSearchParams, availableTabNames]);

  const handleNavigateToTab = useCallback((tabIndex) => {
    handleTabChange(tabIndex);
    handleBreadcrumbReset();
  }, [handleTabChange, handleBreadcrumbReset]);

  // Update URL when tab changes
  useEffect(() => {
    const currentTabName = availableTabNames[activeTab];
    const urlTabName = searchParams.get('tab');
    if (urlTabName !== currentTabName) {
      setSearchParams({ tab: currentTabName }, { replace: true });
    }
  }, [activeTab, searchParams, setSearchParams, availableTabNames]);

  const renderBreadcrumb = () => {
    return (
      <div className="breadcrumb-nav">
        <span className="breadcrumb-link" onClick={() => window.location.href = '/'}>
          <Home className="breadcrumb-icon" aria-hidden="true" /> Strona główna
        </span>
        <span className="breadcrumb-separator">›</span>
        <span 
          className={breadcrumb.installation ? "breadcrumb-link" : "breadcrumb-current"}
          onClick={() => {
            if (breadcrumb.installation) {
              handleBreadcrumbReset();
            }
          }}
          style={{ cursor: breadcrumb.installation ? 'pointer' : 'default' }}
        >
          <CalendarDays className="breadcrumb-icon" aria-hidden="true" /> Planowanie montaży
        </span>
        {breadcrumb.installation && (
          <>
            <span className="breadcrumb-separator">›</span>
            <span 
              className="breadcrumb-link"
              onClick={() => handleNavigateToTab(activeTab)}
              title={`Wróć do: ${availableTabs[activeTab]}`}
            >
              {availableTabs[activeTab]}
            </span>
            <span className="breadcrumb-separator">›</span>
            <span className="breadcrumb-current">
              Zlecenie #{breadcrumb.installation.order_number || breadcrumb.installation.id}
            </span>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="page-container installation-planning">
      {renderBreadcrumb()}

      <div className="page-header">
        <h2 className="module-page-title"><CalendarDays aria-hidden="true" /> Planowanie montaży</h2>
      </div>

      <div className="tab-bar">
        {availableTabs.map((tab, i) => (
          <button
            key={tab}
            className={`tab-btn ${activeTab === i ? 'active' : ''}`}
            onClick={() => handleTabChange(i)}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className="tab-content">
        {availableTabIndices[activeTab] === 0 && <PlanningTab canManage={canManage} canViewSettlement={canViewSettlement} isViewOnly={isViewOnly} onBreadcrumbChange={handleBreadcrumbChange} onBreadcrumbReset={handleBreadcrumbReset} />}
        {availableTabIndices[activeTab] === 1 && <InstallationListTab canManage={canManage} canViewSettlement={canViewSettlement} onBreadcrumbChange={handleBreadcrumbChange} onBreadcrumbReset={handleBreadcrumbReset} />}
        {availableTabIndices[activeTab] === 2 && <WorkersTeamsTab canManage={canManage} canViewSettlement={canViewSettlement} />}
        {availableTabIndices[activeTab] === 3 && <BillingTab onNavigateToPlanning={() => handleNavigateToTab(0)} />}
      </div>
    </div>
  );
}
